
import React, { useState, useEffect, useRef } from 'react';
import { Settings, Database, RefreshCw, X, FileText, Shield, ChevronRight, ChevronLeft, CheckCircle, Eye, Upload, Trash2, Search, Plus, Minus, RotateCcw, ChevronUp, ChevronDown, File, Play, Clock, Download, LayoutTemplate, Scan, Sparkles } from 'lucide-react';
import TemplateManagement from './components/TemplateManagement';
import TemplateSampleViewer from './components/TemplateSampleViewer'; // 🚀 Import new viewer
import OCRLabelingViewer from './components/OCRLabelingViewer'; // 🚀 OCR Visualization
import * as XLSX from 'xlsx';
import PromptManagementModal from './components/PromptManagementModal';
import PromptPlayground from './components/PromptPlayground';
import VehicleMasterManagement from './components/VehicleMasterManagement';
import VehicleMatchingPanel from './components/VehicleMatchingPanel';
import PDFViewer from './components/PDFViewer';
import CanvasPDFViewer from './components/CanvasPDFViewer';
import ReactPDFViewer from './components/ReactPDFViewer'; // 📘 @react-pdf-viewer 기반 뷰어
import NativePDFViewer from './components/NativePDFViewer';
import UpdateNotification from './components/UpdateNotification';
import FineTuningManagement from './components/FineTuningManagement';
import BatchValidationGrid from './components/BatchValidationGrid';
import BoundingBoxOverlay from './components/BoundingBoxOverlay';
import FieldHighlightOverlay from './components/FieldHighlightOverlay';
import GroundingHighlightOverlay from './components/GroundingHighlightOverlay'; // ⭐ [Grounding] LangExtract 필드 위치 하이라이트
import Login from './components/Login';
import LandingPage from './components/LandingPage';
import DBSettings from './components/DBSettings';
import PageBasedGridPanel from './components/PageBasedGridPanel';
import CompareResultPanel from './components/CompareResultPanel';
import RightPanelTabs from './components/RightPanelTabs';
import ChunkDebugModal from './components/ChunkDebugModal';
import DocumentWizard from './components/DocumentWizard';
import ExcelImageExtractor from './components/ExcelImageExtractor'; // 📊 마이그레이션 도구
import LegacyCustomerRegistration from './components/LegacyCustomerRegistration'; // 🏛️ 레거시 거래처 등록
import TalkCloudDocManager from './components/TalkCloudDocManager'; // 📎 톡클라우드 문서관리
import Sidebar from './components/Sidebar'; // 🧭 사이드바
import AIChatPanel from './components/AIChatPanel'; // 🤖 AI 채팅 패널
import { parseBoundingBoxes, scaleBoundingBoxes } from './utils/boundingBoxParser';
import { getModelNickname, getAllModelNicknames } from './utils/modelNickname';
import { mergePageResults, applyMergeRules } from './utils/mergeUtils';  // 📄 멀티 문서 머지
import * as pdfjsLib from 'pdfjs-dist'; // 📄 PDF 페이지 수 감지용

// PDF.js worker 설정
const PDFJS_VERSION = pdfjsLib.version || '3.11.174';
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.js`;

function App() {
  // API 베이스 URL 설정 (localhost일 때만 3002 포트 사용, 프로덕션은 상대 경로 사용)
  const API_BASE_URL = process.env.REACT_APP_API_URL || (
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? `http://${window.location.hostname}:3002`
      : ''
  );

  // ⚠️ 중요: 모든 hooks는 조건부 return 전에 선언되어야 합니다 (React Hooks 규칙)

  // 로그인 상태 관리
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // localStorage 또는 sessionStorage에서 로그인 상태 확인
    const savedGroupCode = localStorage.getItem('groupCode') || sessionStorage.getItem('groupCode');
    const savedUserId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
    return !!(savedGroupCode && savedUserId);
  });
  const [currentGroupCode, setCurrentGroupCode] = useState(() => {
    return localStorage.getItem('groupCode') || sessionStorage.getItem('groupCode') || '';
  });
  const [currentUserId, setCurrentUserId] = useState(() => {
    return localStorage.getItem('userId') || sessionStorage.getItem('userId') || '';
  });

  // Landing Page vs Login toggle
  const [showLogin, setShowLogin] = useState(false);

  // DB 연결 상태 관리
  const [dbStatus, setDbStatus] = useState({
    connected: false,
    server: null,
    database: null,
    loading: true
  });

  // 상태 관리 (모든 hooks를 early return 전에 선언)
  const [fileList, setFileList] = useState([]); // { id, name, file, preview, status, extractedData, checked }
  const [selectedFileIndex, setSelectedFileIndex] = useState(null);
  const [gridSelectedFile, setGridSelectedFile] = useState(null); // 그리드에서 선택된 파일 (PDF 뷰어용)
  const [selectedPageNumber, setSelectedPageNumber] = useState(1); // PDF 페이지 선택
  const [pageJumpToken, setPageJumpToken] = useState(0); // 📄 페이지 점프 강제 트리거 토큰
  const [checkedPages, setCheckedPages] = useState([]); // 체크된 페이지 번호들
  const [validatedPages, setValidatedPages] = useState({}); // 검증 완료된 페이지 { fileId: { pageNumber: true } }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processingCount, setProcessingCount] = useState(0); // 동시 처리 중인 파일 수
  const [activeTab, setActiveTab] = useState('viewer'); // 'viewer', 'grid', 'mapping', 'finetuning', 'dbsettings'

  // 템플릿 관리 상태
  const [showTemplateManagement, setShowTemplateManagement] = useState(false);
  const [templateManagementMode, setTemplateManagementMode] = useState('list'); // 'list', 'create', 'edit'
  const [templateManagementTargetId, setTemplateManagementTargetId] = useState(null); // ID of template to edit/pre-select

  // 템플릿 관리 열기 핸들러
  const handleOpenTemplateManager = (mode = 'list', templateId = null) => {
    setTemplateManagementMode(mode);
    setTemplateManagementTargetId(templateId);
    setShowTemplateManagement(true);
  };
  const [templates, setTemplates] = useState({});
  const [selectedOcrTemplate, setSelectedOcrTemplate] = useState(''); // 🔧 수동 템플릿 선택 (문서인식)
  const [ocrProcessingMode, setOcrProcessingMode] = useState('speed'); // 🚀 처리 모드: 'speed'(PDF 네이티브) | 'coordinate'(PNG 청크+Bbox)
  const [showChunkDebug, setShowChunkDebug] = useState(true); // 🔍 청크 디버그 팝업 표시 여부
  const [chunkDebugData, setChunkDebugData] = useState(null); // 청크 디버그 데이터 (chunkDetails, referenceExample)
  const [showChunkDebugModal, setShowChunkDebugModal] = useState(false); // 모달 표시 상태
  const [showDocumentWizard, setShowDocumentWizard] = useState(false); // 🧙 문서 마법사
  const [wizardFirstPage, setWizardFirstPage] = useState(null); // 마법사용 첫 페이지 이미지
  const [centerViewTab, setCenterViewTab] = useState('document'); // 🎯 가운데 패널 탭: 'template' | 'document'

  // 템플릿 로드
  // 템플릿 로드 함수 (컴포넌트 스코프로 이동)
  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/templates`);
      const data = await res.json();
      // 배열을 객체로 변환 (id 기준)
      const templateMap = {};
      data.forEach(tpl => {
        templateMap[tpl.id] = tpl;
      });
      setTemplates(templateMap);

      // 🔧 isDefault가 true인 템플릿을 자동 선택
      const defaultTemplate = data.find(tpl => tpl.isDefault === true);
      if (defaultTemplate && !selectedOcrTemplate) {
        setSelectedOcrTemplate(defaultTemplate.name);
      }
    } catch (error) {
      console.error('템플릿 로드 실패:', error);
    }
  };

  // 템플릿 로드 Effect
  useEffect(() => {
    fetchTemplates();
  }, [API_BASE_URL, showTemplateManagement]);

  // 🚀 [UX] 템플릿 관리 열릴 때 우측 패널 자동 확장 (Wide Mode) & 왼쪽 패널 축소 & 샘플 탭 선택
  // ⭐ 이전 상태 저장을 위한 ref (닫을 때 복원용)
  const prevPanelStateRef = useRef(null);

  useEffect(() => {
    if (showTemplateManagement) {
      // ⭐ 열기 전 현재 상태 저장
      prevPanelStateRef.current = {
        isRightPanelCollapsed,
        rightPanelWidth,
        isLeftPanelCollapsed,
        centerViewTab
      };
      console.log('📦 [Template] 이전 상태 저장:', prevPanelStateRef.current);

      // 화면 확장
      if (isRightPanelCollapsed) setIsRightPanelCollapsed(false);
      if (rightPanelWidth < 1200) setRightPanelWidth(1200); // 1200px로 확장 (요청사항: 더 넓게)
      setIsLeftPanelCollapsed(true); // 🚀 왼쪽 패널 자동 닫기
      setCenterViewTab('template'); // 🎯 템플릿 탭 자동 선택
    } else {
      // ⭐ 닫힐 때 이전 상태 복원
      if (prevPanelStateRef.current) {
        console.log('📦 [Template] 이전 상태 복원:', prevPanelStateRef.current);
        setIsRightPanelCollapsed(prevPanelStateRef.current.isRightPanelCollapsed);
        setRightPanelWidth(prevPanelStateRef.current.rightPanelWidth);
        setIsLeftPanelCollapsed(prevPanelStateRef.current.isLeftPanelCollapsed);
        setCenterViewTab(prevPanelStateRef.current.centerViewTab);
        prevPanelStateRef.current = null; // 복원 후 초기화
      }
    }
  }, [showTemplateManagement]);

  // 🎯 [UX] 템플릿 선택 시 템플릿 탭 자동 전환
  useEffect(() => {
    if (selectedOcrTemplate) {
      console.log('🎯 템플릿 선택됨 → 템플릿 탭으로 전환:', selectedOcrTemplate);
      setCenterViewTab('template');
      // 오른쪽 패널도 자동 열기
      if (isRightPanelCollapsed) setIsRightPanelCollapsed(false);
    }
  }, [selectedOcrTemplate]);

  // 🎯 [UX] 파일 선택/업로드 시 탭 자동 전환
  useEffect(() => {
    if (selectedFileIndex !== null && fileList[selectedFileIndex]) {
      if (showTemplateManagement) {
        // 템플릿 관리가 열려 있을 때는 템플릿 탭으로 전환 (업로드된 파일 = 템플릿 샘플)
        console.log('📄 템플릿 관리 + 파일 업로드 → 템플릿 탭으로 전환');
        setCenterViewTab('template');
      } else {
        // 일반 모드에서는 문서 탭으로 전환
        console.log('📄 파일 선택됨 → 문서 탭으로 전환:', fileList[selectedFileIndex]?.name);
        setCenterViewTab('document');
      }
    }
  }, [selectedFileIndex, fileList, showTemplateManagement]);

  const [showBatchValidationGrid, setShowBatchValidationGrid] = useState(false); // 전체검증 그리드 표시 여부
  const [showMasterSettingsModal, setShowMasterSettingsModal] = useState(false); // 기초설정 모달 표시 여부
  const [showMigrationModal, setShowMigrationModal] = useState(false); // 📊 마이그레이션 도구 모달
  const [isDragging, setIsDragging] = useState(false);
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false); // 왼쪽 패널 접기 상태
  const [autoCollapseOnExtract, setAutoCollapseOnExtract] = useState(() => {
    const saved = localStorage.getItem('autoCollapseOnExtract');
    return saved !== null ? saved === 'true' : true; // 기본값: ON (시인성 향상)
  }); // ⭐ 문서 추출 시 왼쪽 패널 자동 닫힘
  const [processingProgress, setProcessingProgress] = useState({
    currentPage: 0,      // 현재 처리 중인 페이지
    totalPages: 0,       // 전체 페이지 수
    step: '',
    fileName: '',
    percentage: 0,
    startTime: null,     // 처리 시작 시간
    estimatedSeconds: 0  // 예상 시간 (초)
  }); // ⭐ 진행률 표시
  const [lastCompletionLog, setLastCompletionLog] = useState(null); // ⭐ 완료 메시지 (하단 바에 표시)
  const [isCenterPanelCollapsed, setIsCenterPanelCollapsed] = useState(false); // 가운데 패널 접기 상태
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false); // 오른쪽 패널 접기 상태 (초기: 열림)
  const [isVehicleRegistrationPanelCollapsed, setIsVehicleRegistrationPanelCollapsed] = useState(true); // 차량등록 패널 접기 상태 (기본: 접힘)
  const [layoutMode, setLayoutMode] = useState(() => {
    const saved = localStorage.getItem('layoutMode');
    return saved || 'vertical'; // 'vertical' (기본) | 'horizontal' (상단 그리드 + 하단 PDF)
  }); // 🔧 레이아웃 모드
  const [leftPanelWidth, setLeftPanelWidth] = useState(230); // 왼쪽 패널 너비 (px)
  const [rightPanelWidth, setRightPanelWidth] = useState(600); // 오른쪽 패널 너비 (px) - 그리드 확대를 위해 조정

  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [isPromptPlaygroundOpen, setIsPromptPlaygroundOpen] = useState(true); // 기본적으로 열림 (사용자 요청)
  const [playgroundProcessing, setPlaygroundProcessing] = useState(false);
  const [isVehicleMasterModalOpen, setIsVehicleMasterModalOpen] = useState(false);
  const [highlightedBboxes, setHighlightedBboxes] = useState(null); // ⭐ 필드 클릭 시 하이라이트할 bbox 좌표

  // 🤖 AI 채팅 패널 상태 (PageBasedGridPanel 인라인 사이드바로 대체 — 툴바 버튼 토글용)
  const [showAIPanel, setShowAIPanel] = useState(false);
  // 🤖 PageBasedGridPanel에서 AI 패널 상태 변경 시 툴바 버튼 동기화
  useEffect(() => {
    const handler = (e) => setShowAIPanel(e.detail?.open ?? false);
    window.addEventListener('aiPanelStateChange', handler);
    return () => window.removeEventListener('aiPanelStateChange', handler);
  }, []);
  // ⭐ 리사이징 성능 최적화를 위한 ref
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const resizeAnimationFrameRef = useRef(null);
  const promptTextareaRef = useRef(null); // 프롬프트 textarea ref
  const scrollAnimationFrameRef = useRef(null); // 스크롤 애니메이션 프레임 ref
  const [promptEditorHeight, setPromptEditorHeight] = useState(200); // 프롬프트 편집기 높이 (px)
  const [isPromptEditorCollapsed, setIsPromptEditorCollapsed] = useState(true); // 프롬프트 편집기 접기 상태 (초기: 접힘)
  const [isPromptEditing, setIsPromptEditing] = useState(false); // 프롬프트 편집 모드
  const [editablePrompt, setEditablePrompt] = useState(''); // 편집 중인 프롬프트
  const [customPromptEnabled, setCustomPromptEnabled] = useState(false); // 커스텀 프롬프트 사용 여부
  const [defaultPromptLoaded, setDefaultPromptLoaded] = useState(false); // 기본 프롬프트 로드 여부
  const [promptType, setPromptType] = useState('ultra-light'); // 'ultra-light', 'classification', or 'unified'
  const [selectedModel, setSelectedModel] = useState('gemini-2.5-flash'); // 선택된 모델
  const [executionTime, setExecutionTime] = useState(null); // 실행 시간 (ms)
  const [tokenCount, setTokenCount] = useState(null); // 토큰 카운트

  // Resize state
  const [gridHeight, setGridHeight] = useState(50); // percentage
  const [isResizing, setIsResizing] = useState(false);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;

    // Calculate percentage based on container height
    const containerHeight = e.currentTarget.clientHeight;
    const newHeight = (e.clientY - e.currentTarget.getBoundingClientRect().top) / containerHeight * 100;

    // Limit between 20% and 80%
    if (newHeight >= 20 && newHeight <= 80) {
      setGridHeight(newHeight);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  const [showSettings, setShowSettings] = useState(false); // 설정 모달 표시
  const [showPromptManager, setShowPromptManager] = useState(false); // 프롬프트 관리 모달 표시

  const [settingsModalTab, setSettingsModalTab] = useState('ocr'); // 설정 모달 탭: 'ocr', 'basic', 'db', 'finetuning'
  const [selectedDocType, setSelectedDocType] = useState(''); // ⭐ 프롬프트 편집용 문서 타입 선택

  // localStorage에서 설정 불러오기 (hooks 선언 전에 실행)
  const loadSettings = () => {
    try {
      const savedClassificationModel = localStorage.getItem('classificationFlashModel');
      const savedTemplateExtractionModel = localStorage.getItem('templateExtractionModel'); // ⭐ 템플릿 추출 모델
      const savedValidationModels = localStorage.getItem('selectedValidationModels');
      const savedOcrEngine = localStorage.getItem('ocrEngine');
      const savedDeepseekMode = localStorage.getItem('deepseekMode');
      const savedGrounding = localStorage.getItem('grounding');
      const savedDatalabBoundingBox = localStorage.getItem('datalabBoundingBox');

      const savedThinkingBudget = localStorage.getItem('thinkingBudget');
      const savedThinkingLevel = localStorage.getItem('thinkingLevel');

      // ⭐ 마이그레이션: 기존 설정이 있으면 파싱하되, 구형 모델은 신형으로 교체
      let parsedValidationModels = savedValidationModels
        ? JSON.parse(savedValidationModels).filter(m => m !== 'gemini-2.5-pro-preview-06-05') // ⭐ 06-05 모델 자동 제거
        : ['gemini-2.5-flash']; // 기본값: Flash 단일 모드 (속도 최적화)

      return {
        classificationFlashModel: savedClassificationModel || '2.0',
        templateExtractionModel: savedTemplateExtractionModel || 'gemini-3-flash-preview', // ⭐ 기본값: Gemini 3.0 Flash (속도+정확도)
        selectedValidationModels: parsedValidationModels,
        ocrEngine: savedOcrEngine || 'gemini',
        deepseekMode: savedDeepseekMode || 'optimized',
        grounding: savedGrounding !== null ? savedGrounding === 'true' : true,
        datalabBoundingBox: savedDatalabBoundingBox !== null ? savedDatalabBoundingBox === 'true' : true,
        thinkingBudget: savedThinkingBudget !== null ? parseInt(savedThinkingBudget) : -1,
        thinkingLevel: savedThinkingLevel || 'HIGH'
      };
    } catch (error) {
      console.error('설정 불러오기 실패:', error);
      return {
        classificationFlashModel: '2.0',
        templateExtractionModel: 'gemini-3-flash-preview', // ⭐ 기본값: Gemini 3.0 Flash (속도+정확도)
        selectedValidationModels: ['gemini-2.5-flash', 'gemini-3-pro-preview'], // ⭐ 기본값 변경
        ocrEngine: 'gemini',
        deepseekMode: 'optimized',
        grounding: true,
        datalabBoundingBox: true,
        thinkingBudget: -1,
        thinkingLevel: 'HIGH'
      };
    }
  };

  const initialSettings = loadSettings();

  // 검증 모델 설정 (새로운 구조 - 최대 2개 선택 가능)
  const [selectedValidationModels, setSelectedValidationModels] = useState(initialSettings.selectedValidationModels);

  // 레거시 호환용 (기존 코드와 호환)
  const [validationModel, setValidationModel] = useState('gemini-pro');
  const [useClaudeForValidation, setUseClaudeForValidation] = useState(false);
  const [speedMode, setSpeedMode] = useState(true);

  const [classificationFlashModel, setClassificationFlashModel] = useState(initialSettings.classificationFlashModel);
  const [templateExtractionModel, setTemplateExtractionModel] = useState(initialSettings.templateExtractionModel); // ⭐ 템플릿 추출 모델
  const [ocrEngine, setOcrEngine] = useState(initialSettings.ocrEngine);
  const [deepseekMode, setDeepseekMode] = useState(initialSettings.deepseekMode || 'optimized');
  const [deepseekServerUrl, setDeepseekServerUrl] = useState(localStorage.getItem('deepseekServerUrl') || 'http://localhost:8001');
  const [grounding, setGrounding] = useState(initialSettings.grounding);
  const [datalabBoundingBox, setDatalabBoundingBox] = useState(initialSettings.datalabBoundingBox);
  // ⭐ OCR 설정 (Hybrid Mode)
  const [enableHybridOCR, setEnableHybridOCR] = useState(() => {
    const saved = localStorage.getItem('enableHybridOCR');
    return saved !== null ? saved === 'true' : true; // Default true
  });
  // ⭐ 문서 분류 활성화 설정
  const [enableClassification, setEnableClassification] = useState(() => {
    const saved = localStorage.getItem('enableClassification');
    return saved !== null ? saved === 'true' : true; // Default true (분류 사용)
  });
  // ⭐ ThinkingConfig 설정 (Gemini 2.5/3.0)
  const [thinkingBudget, setThinkingBudget] = useState(initialSettings.thinkingBudget); // Gemini 2.5: -1(동적), 0(비활성화), 1024~32768
  const [thinkingLevel, setThinkingLevel] = useState(initialSettings.thinkingLevel);     // Gemini 3.0: 'HIGH', 'LOW'
  const [showBoundingBoxes, setShowBoundingBoxes] = useState(true);
  const [showDetailMode, setShowDetailMode] = useState(false);
  const [highlightedField, setHighlightedField] = useState(null);
  const [highlightedFieldValue, setHighlightedFieldValue] = useState(null);
  const [clickedField, setClickedField] = useState(null);
  const [groundingHighlight, setGroundingHighlight] = useState(null); // ⭐ [Grounding] LangExtract 필드 위치 { fieldKey, page, coordinates, confidence }
  const [useCanvasPDF, setUseCanvasPDF] = useState(true);
  const [useGridMode, setUseGridMode] = useState(true);
  const [processingLogs, setProcessingLogs] = useState([]);

  const [showLogs, setShowLogs] = useState(false);

  // Auto-start Extraction State
  // Auto-start Extraction State
  const [isAutoExtractionEnabled, setIsAutoExtractionEnabled] = useState(() => {
    return localStorage.getItem('isAutoExtractionEnabled') === 'true';
  });
  const [shouldAutoStart, setShouldAutoStart] = useState(false);

  // 🚀 처리 취소 기능 (Google AI Studio 스타일)
  const [isCancelled, setIsCancelled] = useState(false);
  const abortControllerRef = useRef(null);

  // 🎯 OCR 파이프라인 상태 (메인 화면 bbox 시각화)
  const [ocrLabels, setOcrLabels] = useState([]); // PaddleOCR bbox + 필드 매핑
  const [correctedImageUrl, setCorrectedImageUrl] = useState(null); // 보정된 이미지 URL
  const [ocrPipelineStep, setOcrPipelineStep] = useState(''); // 현재 단계 표시
  const [ocrImageSize, setOcrImageSize] = useState({ width: 0, height: 0 }); // OCR 이미지 크기

  // 📄 멀티페이지 PDF 상태
  const [pdfPageResults, setPdfPageResults] = useState([]); // 모든 페이지 OCR 결과
  const [currentPdfPage, setCurrentPdfPage] = useState(1); // 현재 표시 중인 페이지 (1-indexed)
  const [totalPdfPages, setTotalPdfPages] = useState(1); // 총 페이지 수

  const logsEndRef = useRef(null);
  const lastClickedPageRef = useRef(null);

  // ⚠️ 모든 useEffect hooks도 조건부 return 전에 선언되어야 합니다
  // validationModel 변경 시 useClaudeForValidation 자동 업데이트
  useEffect(() => {
    setUseClaudeForValidation(validationModel.startsWith('claude'));
  }, [validationModel]);

  // 로그가 추가될 때마다 자동 스크롤
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [processingLogs]);

  // 페이지 번호 변경 추적 (디버깅용)
  useEffect(() => {
    console.log(`🔄 [State] selectedPageNumber 변경됨: ${selectedPageNumber}`);
  }, [selectedPageNumber]);

  // 파일 선택 시 자동으로 프롬프트 로드
  useEffect(() => {
    const loadDefaultPrompt = async () => {
      if (selectedFileIndex !== null && !editablePrompt) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/prompts?type=${promptType}`);
          const data = await response.json();
          if (data.success) {
            setEditablePrompt(data.prompt);
            setDefaultPromptLoaded(true);
          }
        } catch (err) {
          console.error('프롬프트 로드 실패:', err);
        }
      }
    };
    loadDefaultPrompt();
  }, [selectedFileIndex, promptType, editablePrompt, API_BASE_URL]);

  // Auto-start Extraction Effect
  useEffect(() => {
    if (shouldAutoStart && !loading && fileList.length > 0) {
      console.log('🚀 자동 추출 시작 트리거됨');
      setShouldAutoStart(false);
      handleBatchFullProcess();
    }
  }, [shouldAutoStart, loading, fileList]);

  // Save Auto-start setting
  useEffect(() => {
    localStorage.setItem('isAutoExtractionEnabled', isAutoExtractionEnabled);
  }, [isAutoExtractionEnabled]);

  // ⭐ 모든 설정 자동 저장 (Auto-persistence)
  useEffect(() => {
    localStorage.setItem('classificationFlashModel', classificationFlashModel);
  }, [classificationFlashModel]);

  useEffect(() => {
    localStorage.setItem('selectedValidationModels', JSON.stringify(selectedValidationModels));
  }, [selectedValidationModels]);

  useEffect(() => {
    localStorage.setItem('ocrEngine', ocrEngine);
  }, [ocrEngine]);

  useEffect(() => {
    localStorage.setItem('deepseekMode', deepseekMode);
  }, [deepseekMode]);

  useEffect(() => {
    localStorage.setItem('deepseekServerUrl', deepseekServerUrl);
  }, [deepseekServerUrl]);

  useEffect(() => {
    localStorage.setItem('grounding', grounding.toString());
  }, [grounding]);

  useEffect(() => {
    localStorage.setItem('datalabBoundingBox', datalabBoundingBox.toString());
  }, [datalabBoundingBox]);

  useEffect(() => {
    localStorage.setItem('thinkingBudget', thinkingBudget.toString());
  }, [thinkingBudget]);

  useEffect(() => {
    localStorage.setItem('thinkingLevel', thinkingLevel);
  }, [thinkingLevel]);

  // DB 연결 상태 확인 (로그인 후)
  useEffect(() => {
    if (!isLoggedIn) return;

    const fetchDbStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/db/status`);
        const data = await response.json();

        setDbStatus({
          connected: data.connected,
          server: data.server,
          database: data.database,
          loading: false
        });
      } catch (error) {
        console.error('DB 상태 확인 실패:', error);
        setDbStatus({
          connected: false,
          server: null,
          database: null,
          loading: false
        });
      }
    };

    // 초기 로드
    fetchDbStatus();

    // 30초마다 DB 상태 갱신 (사용자 요청으로 비활성화 - 2025.12.19)
    // const intervalId = setInterval(fetchDbStatus, 30000);
    // return () => clearInterval(intervalId);
    return () => { };
  }, [isLoggedIn, API_BASE_URL]);

  // 🚀 외부(모달)에서 파일 선택 시 뷰어 업데이트
  const handleExternalFileSelect = (file) => {
    if (!file) return;

    // 🔧 File 객체인지 wrapper 객체인지 구분 (duck typing 사용)
    const hasFileProperties = file && typeof file === 'object' &&
      (file.name || file.size) &&
      (typeof file.arrayBuffer === 'function' || typeof file.slice === 'function');

    const actualFile = hasFileProperties ? file : file?.file;
    const fileName = file.name || actualFile?.name;
    const fileSize = file.size || actualFile?.size;

    if (!actualFile && !hasFileProperties) {
      console.warn('⚠️ handleExternalFileSelect: Invalid file object', file);
      return;
    }

    // 이미 리스트에 있는지 확인 (이름과 크기로 대략적 중복 체크)
    const existingIndex = fileList.findIndex(f => f.name === fileName && (f.file?.size || f.size) === fileSize);

    if (existingIndex >= 0) {
      setSelectedFileIndex(existingIndex);
      return;
    }

    // 새 파일 추가
    const objectUrl = URL.createObjectURL(actualFile);
    const newFile = {
      id: Date.now(),
      name: fileName,
      file: actualFile,
      preview: objectUrl,
      type: actualFile.type,
      status: 'ready',
      checked: true,
      extractedData: null
    };

    setFileList(prev => [...prev, newFile]);
    setSelectedFileIndex(fileList.length); // 마지막 인덱스 (현재 길이 = 새 인덱스)
  };

  // 파일 선택이 바뀌거나 pageResults가 업데이트될 때 페이지 체크 상태 초기화
  useEffect(() => {
    if (selectedFileIndex !== null) {
      const selectedFile = fileList[selectedFileIndex];

      if (selectedFile?.pageResults && selectedFile.pageResults.length > 0) {
        // PDF 처리 완료: 모든 페이지 자동 선택
        const allPageNumbers = selectedFile.pageResults.map(p => p.pageNumber);
        console.log('📄 PDF 페이지 자동 선택 (처리 완료):', {
          selectedFileIndex,
          fileName: selectedFile.name,
          totalPages: allPageNumbers.length,
          allPageNumbers
        });
        setCheckedPages(allPageNumbers);
        setSelectedPageNumber(allPageNumbers[0] || 1);
        lastClickedPageRef.current = allPageNumbers[0] || 1;
      } else if (selectedFile?.isPDF && selectedFile?.totalPages > 0) {
        // PDF 파일인데 아직 pageResults가 없는 경우 (처리 대기 중)
        const expectedPages = Array.from({ length: selectedFile.totalPages }, (_, i) => i + 1);
        console.log('📄 PDF 처리 대기 중, 예상 페이지 선택:', {
          selectedFileIndex,
          fileName: selectedFile.name,
          totalPages: selectedFile.totalPages,
          expectedPages
        });
        setCheckedPages(expectedPages);
        setSelectedPageNumber(1);
        lastClickedPageRef.current = 1;
      } else if (selectedFile?.isPDF) {
        // PDF 파일이지만 아직 totalPages도 없는 경우 (업로드 직후)
        console.log('📄 PDF 파일 업로드 직후, 페이지 1 선택:', {
          selectedFileIndex,
          fileName: selectedFile.name
        });
        setCheckedPages([1]);
        setSelectedPageNumber(1);
        lastClickedPageRef.current = 1;
      } else if (selectedFile) {
        // 단일 이미지 파일인 경우
        console.log('🖼️ 단일 이미지 파일 선택:', selectedFile.name);
        setCheckedPages([]);
        setSelectedPageNumber(1);
        lastClickedPageRef.current = null;
      }
    } else {
      setCheckedPages([]);
    }
  }, [selectedFileIndex, fileList]);

  // 로그인 성공 핸들러
  const handleLoginSuccess = (userInfo) => {
    setCurrentGroupCode(userInfo.groupCode);
    setCurrentUserId(userInfo.userId);
    setIsLoggedIn(true);
  };

  // 로그아웃 핸들러
  const handleLogout = () => {
    localStorage.removeItem('groupCode');
    localStorage.removeItem('userId');
    localStorage.removeItem('token');
    localStorage.removeItem('password'); // 패스워드도 삭제

    sessionStorage.removeItem('groupCode');
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('token');

    setCurrentGroupCode('');
    setCurrentUserId('');
    setIsLoggedIn(false);
  };

  // 로그인되지 않은 경우 LandingPage 또는 Login 표시
  if (!isLoggedIn) {
    if (showLogin) {
      return (
        <Login
          API_BASE_URL={API_BASE_URL}
          onLoginSuccess={handleLoginSuccess}
          onBack={() => setShowLogin(false)}
        />
      );
    }
    return <LandingPage onLoginClick={() => setShowLogin(true)} />;
  }

  // 문서 타입 코드 매핑
  const DOC_TYPE_MAP = {
    '경락확인서': 'N',
    '자동차매매계약서': 'O',
    '자동차등록원부': 'L',
    '자동차등록증': 'R',
    '말소등록증': 'B',
    '사업자등록증': 'A',
    '신분증': 'H',
    '폐차증': 'D',
    '세금계산서': 'T',
    '기타': 'U'
  };

  // ⭐ 필드 호버 핸들러
  const handleFieldHover = (fieldKey, fieldValue) => {
    console.log(`🎯 필드 호버: ${fieldKey} = ${fieldValue}`);
    setHighlightedField(fieldKey);
    setHighlightedFieldValue(fieldValue);
    setClickedField(null); // 호버 시 클릭 상태 해제
  };

  // ⭐ 필드 클릭 핸들러 (자동 스크롤 + bbox 하이라이트)
  // 🎯 [Grounding] fieldInfo 객체 형식: { fieldKey, fieldValue, page, coordinates, confidence, hasGrounding }
  const handleFieldClick = (fieldInfo) => {
    // 하위 호환: 기존 (fieldKey, fieldValue) 형식도 지원
    if (typeof fieldInfo === 'string') {
      const fieldKey = fieldInfo;
      const fieldValue = arguments[1];
      console.log(`🖱️ 필드 클릭: ${fieldKey} = ${fieldValue}`);
      setHighlightedField(fieldKey);
      setHighlightedFieldValue(fieldValue);
      setClickedField(fieldKey);
      setHighlightedBboxes(fieldKey);
      setTimeout(() => {
        setClickedField(null);
        setHighlightedBboxes(null);
      }, 3000);
      return;
    }

    // 🎯 [Grounding] 새 형식: 객체로 전달됨
    const { fieldKey, fieldValue, page, coordinates, confidence, hasGrounding } = fieldInfo;
    console.log(`🖱️ 필드 클릭: ${fieldKey} = ${fieldValue}`, hasGrounding ? `📍 Page ${page}` : '(no grounding)');

    setHighlightedField(fieldKey);
    setHighlightedFieldValue(fieldValue);
    setClickedField(fieldKey);
    setHighlightedBboxes(fieldKey);

    // 🎯 [Grounding] 위치 정보가 있으면 해당 페이지로 이동 + 하이라이트 표시
    // 🔧 FIX: page가 0일 때도 처리되도록 !== null 사용 (0은 falsy이므로)
    if (hasGrounding && page !== null && page !== undefined) {
      console.log(`🎯 [Grounding] PDF 페이지 이동: ${page}, 좌표:`, coordinates);

      // 1. 해당 페이지로 이동
      setSelectedPageNumber(page);
      handlePdfPageChange(page);

      // 2. 문서 탭으로 전환 (grounding 확인을 위해)
      setCenterViewTab('document');

      // 3. Grounding 하이라이트 좌표 저장 (PDFViewer에서 사용)
      setGroundingHighlight({
        fieldKey,
        fieldValue, // 🔍 텍스트 검색 하이라이트용
        page,
        coordinates, // [x1, y1, x2, y2]
        confidence
      });
    } else {
      // 🔍 [Text Search Fallback] bbox가 없어도 텍스트 검색으로 하이라이트
      // 📄 페이지 정보가 있으면 해당 페이지로 이동
      if (page !== null && page !== undefined) {
        setSelectedPageNumber(page);
        handlePdfPageChange(page);
        setCenterViewTab('document');
      }
      setGroundingHighlight({
        fieldKey,
        fieldValue, // 텍스트 값만 저장 (좌표 없음)
        page: page || null,
        coordinates: null,
        confidence: null
      });
    }

    // 3초 후 일반 클릭 상태 해제
    setTimeout(() => {
      setClickedField(null);
      setHighlightedBboxes(null);
    }, 3000);
  };

  // 법인 여부 판별 함수 (개인사업자 vs 법인)
  const isCompanyType = (data) => {
    if (!data) return false;

    // 사업자등록증이 있는 경우
    if (data.documentType === '사업자등록증' && data.businessName) {
      // 상호에 "(주)" 또는 "주식회사"가 포함되어야 법인
      const name = data.businessName;
      return name.includes('(주)') || name.includes('주식회사') || name.includes('㈜');
    }

    // 차량 서류에서도 확인
    if (data.ownerName) {
      const name = data.ownerName;
      return name.includes('(주)') || name.includes('주식회사') || name.includes('㈜');
    }

    return false;
  };

  // 문서 타입별 필드 정의 (라벨 및 레이아웃)
  const DOCUMENT_FIELDS_MAP = {
    '말소등록증': {
      ownerName: { label: '성명', width: 'full' },
      residentNumber: { label: '주민등록번호', width: 'full' },
      plateNumber: { label: '차량등록번호', width: 'full' },
      vin: { label: '차대번호 (VIN)', width: 'full' },
      model: { label: '차명 (모델)', width: 'full' },
      modelYear: { label: '연식', width: 'half' },
      productionDate: { label: '제작연월일', width: 'half' }, // 생년월일/법인등록번호? 문맥에 따라 다름
      mileage: { label: '주행거리', width: 'half' },
      deregistrationDate: { label: '말소등록일', width: 'full' }
    },
    '자동차등록증': {
      ownerName: { label: '소유자명', width: 'full' },
      residentNumber: { label: '주민등록번호', width: 'full' },
      address: { label: '사용본거지', width: 'full' },
      plateNumber: { label: '차량번호', width: 'full' },
      vin: { label: '차대번호', width: 'full' },
      model: { label: '차명', width: 'full' },
      modelYear: { label: '연식', width: 'half' },
      firstRegDate: { label: '최초등록일', width: 'half' },
      totalWeight: { label: '총중량', width: 'half' },
      displacement: { label: '배기량', width: 'half' },
      length: { label: '길이', width: 'third' },
      width: { label: '너비', width: 'third' },
      height: { label: '높이', width: 'third' },
      inspectionDate: { label: '검사유효기간', width: 'full' }
    },
    '자동차등록원부': {
      ownerName: { label: '소유자명', width: 'full' },
      residentNumber: { label: '주민부호', width: 'full' },
      address: { label: '사용본거지', width: 'full' },
      plateNumber: { label: '차량번호', width: 'full' },
      vin: { label: '차대번호', width: 'full' },
      model: { label: '차명', width: 'full' },
      modelYear: { label: '연식', width: 'full' }
    },
    '경락확인서': {
      ownerName: { label: '소유자명', width: 'full' },
      residentNumber: { label: '주민등록번호', width: 'full' },
      address: { label: '주소', width: 'full' },
      plateNumber: { label: '차량번호', width: 'full' },
      vin: { label: '차대번호', width: 'full' },
      model: { label: '차명', width: 'full' },
      displacement: { label: '배기량', width: 'half' },
      mileage: { label: '주행거리', width: 'half' },
      totalAmount: { label: '낙찰금액', width: 'full' }
    },
    '자동차매매계약서': {
      ownerName: { label: '매수인 성명', width: 'full' },
      residentNumber: { label: '주민등록번호', width: 'full' },
      address: { label: '주소', width: 'full' },
      plateNumber: { label: '차량번호', width: 'full' },
      vin: { label: '차대번호', width: 'full' },
      model: { label: '차명', width: 'full' },
      totalAmount: { label: '매매금액', width: 'full' },
      tradeDate: { label: '매매일', width: 'full' }
    },
    '폐차증': {
      ownerName: { label: '소유자 성명', width: 'full' },
      residentNumber: { label: '주민등록번호', width: 'full' },
      address: { label: '주소', width: 'full' },
      plateNumber: { label: '차량번호', width: 'full' },
      vin: { label: '차대번호', width: 'full' },
      model: { label: '차명', width: 'full' },
      modelYear: { label: '연식', width: 'full' },
      deregistrationDate: { label: '폐차(인수)일자', width: 'full' }
    },
    '신분증': {
      name: { label: '이름', width: 'full' },
      residentNumber: { label: '주민등록번호', width: 'full' },
      licenseNumber: { label: '면허번호', width: 'full' },
      issueDate: { label: '발급일', width: 'full' },
      address: { label: '주소', width: 'full' }
    },
    '사업자등록증': {
      businessNumber: { label: '사업자등록번호', width: 'full' },
      businessName: { label: '상호', width: 'full' },
      ownerName: { label: '대표자', width: 'full' },
      address: { label: '사업장소재지', width: 'full' },
      openDate: { label: '개업년월일', width: 'full' }
    },
    '세금계산서': {
      businessNumber: { label: '사업자등록번호', width: 'full' },
      businessName: { label: '상호', width: 'full' },
      ownerName: { label: '대표자', width: 'full' },
      address: { label: '사업장소재지', width: 'full' },
      totalAmount: { label: '합계금액', width: 'full' },
      issueDate: { label: '작성일자', width: 'full' },
      plateNumber: { label: '차량번호', width: 'half' },
      vin: { label: '차대번호', width: 'half' },
      model: { label: '차명', width: 'full' }
    }
  };

  // 필드 존재 여부 확인 함수
  const isFieldApplicable = (documentType, fieldName) => {
    const fieldsMap = DOCUMENT_FIELDS_MAP[documentType];
    if (!fieldsMap) return true; // 알 수 없는 문서 타입은 모든 필드 표시
    return fieldsMap[fieldName] !== false;
  };

  // 셀 렌더링 함수
  const renderTableCell = (data, fieldName, value) => {
    const documentType = data?.documentType;
    const fieldApplicable = isFieldApplicable(documentType, fieldName);

    // 이 문서 타입에 해당 필드가 프롬프트에 정의되지 않은 경우 (회색, 작게)
    if (!fieldApplicable) {
      return <span className="text-gray-300 text-xs">-</span>;
    }

    // 🔧 객체인 경우 처리 (React는 객체를 직접 렌더링할 수 없음)
    if (typeof value === 'object' && value !== null) {
      // JSON 형태로 문자열 변환 시도
      try {
        const stringValue = JSON.stringify(value);
        if (stringValue && stringValue !== '{}' && stringValue !== '[]') {
          return <span className="text-xs text-gray-600">{stringValue}</span>;
        }
      } catch (e) {
        console.warn('Failed to stringify value:', value);
      }
      return <span className="text-gray-400 text-xs">-</span>;
    }

    // ✅ 미인식 판단 로직 개선:
    // Flash와 Pro 모두 인식 결과가 없는 경우 → 공백 처리 (미인식 아님)
    // Flash나 Pro 중 하나라도 값이 있었는데 최종 결과 없음 → 미인식
    if (!value || value === '-') {
      const comparisonDetail = data?.metadata?.matchDetails?.comparisonDetails?.[fieldName];
      const flashValue = comparisonDetail?.flash;
      const proValue = comparisonDetail?.pro || comparisonDetail?.final;
      const hadAnyValue = flashValue || proValue;

      // Flash나 Pro에서 값이 있었는데 최종 결과가 없는 경우만 미인식
      if (hadAnyValue) {
        return <span className="text-orange-400 text-xs font-semibold">미인식</span>;
      }

      // Flash와 Pro 모두 없는 경우 → 공백
      return <span className="text-gray-400 text-xs">-</span>;
    }

    // 정상적으로 추출된 경우
    return <span>{String(value)}</span>;
  };

  // 로그 추가 헬퍼 함수
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setProcessingLogs(prev => [...prev, { message, type, timestamp }]);
  };

  // 통합 API: 1차 추출 + 2차 검증 + 일치율 계산 (한 번에)
  const fullExtract = async (file) => {
    try {
      addLog(`📸 파일 처리 시작: ${file.name}`, 'info');
      const validationText = selectedValidationModels.length === 1
        ? `${getModelDisplayName(selectedValidationModels[0])} (단일)`
        : `${selectedValidationModels.map(getModelDisplayName).join(' + ')} (이중 검증)`;
      addLog(`⚙️ 검증 모델: ${validationText}`, 'info');

      const formData = new FormData();
      formData.append('image', file);
      formData.append('useClaudeForValidation', useClaudeForValidation);
      formData.append('enableHybridOCR', enableHybridOCR); // ⭐ Hybrid Mode Flag
      // ⭐ 선택된 검증 모델 전송 (동적 모델 선택)
      if (selectedValidationModels && selectedValidationModels.length > 0) {
        formData.append('selectedValidationModels', JSON.stringify(selectedValidationModels));
      }

      addLog('⏳ Step 1/3: 문서 분류 중...', 'info');

      const response = await fetch(`${API_BASE_URL}/api/ocr/extract`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        addLog(`❌ 서버 오류: ${errorData.error}`, 'error');
        throw new Error(errorData.error || '서버 오류');
      }

      addLog('⏳ Step 2/3: 병렬 추출 중...', 'info');

      const result = await response.json();

      if (!result.success) {
        addLog(`❌ 처리 실패: ${result.error}`, 'error');
        throw new Error(result.error || '처리 실패');
      }

      addLog('⏳ Step 3/3: 결과 비교 중...', 'info');
      addLog('✅ OCR 처리 완료!', 'success');

      return result.data;

    } catch (err) {
      addLog(`❌ OCR 처리 실패: ${err.message}`, 'error');
      throw new Error('OCR 처리 실패: ' + err.message);
    }
  };

  // 1차 추출만 수행
  const extractOnly = async (file) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('enableHybridOCR', enableHybridOCR); // ⭐ Hybrid Mode Flag

      const response = await fetch(`${API_BASE_URL}/api/ocr/extract-only`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '서버 오류');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || '처리 실패');
      }

      return result.data;

    } catch (err) {
      throw new Error('1차 추출 실패: ' + err.message);
    }
  };

  // 2차 검증만 수행
  const validateOnly = async (file, extractedData) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('extractedData', JSON.stringify(extractedData));
      // ⭐ 선택된 검증 모델 전송 (동적 모델 선택)
      if (selectedValidationModels && selectedValidationModels.length > 0) {
        formData.append('selectedValidationModels', JSON.stringify(selectedValidationModels));
      }

      const response = await fetch(`${API_BASE_URL}/api/ocr/validate`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '서버 오류');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || '처리 실패');
      }

      return result.data;

    } catch (err) {
      throw new Error('2차 검증 실패: ' + err.message);
    }
  };

  // ====== 관리자 모드 전용 API ======

  // 관리자: 분류만 실행
  const adminClassify = async (file) => {
    try {
      const formData = new FormData();
      // ⭐ 중요: 파일보다 필드를 먼저 append 해야 백엔드(Multer)에서 정상 인식 가능성 높음
      formData.append('flashModelVersion', classificationFlashModel);
      formData.append('image', file);

      if (ocrEngine === 'deepseek') {
        formData.append('model', 'deepseek');
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/classify`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('분류 실패');
      }

      const result = await response.json();
      return result;

    } catch (err) {
      throw new Error('분류 실패: ' + err.message);
    }
  };

  // 관리자: Flash 추출만
  const adminExtractFlash = async (file, documentType = null) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (documentType) {
        formData.append('documentType', documentType);
      }
      if (ocrEngine === 'deepseek') {
        formData.append('model', 'deepseek');
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/extract-flash`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Flash 추출 실패');
      }

      const result = await response.json();
      return result;

    } catch (err) {
      throw new Error('Flash 추출 실패: ' + err.message);
    }
  };

  // 관리자: Pro 추출만
  const adminExtractPro = async (file, documentType = null) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (documentType) {
        formData.append('documentType', documentType);
      }
      if (ocrEngine === 'deepseek') {
        formData.append('model', 'deepseek');
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/extract-pro`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Pro 추출 실패');
      }

      const result = await response.json();
      return result;

    } catch (err) {
      throw new Error('Pro 추출 실패: ' + err.message);
    }
  };

  // ====== 관리자 모드 핸들러 ======
  const handleAdminClassifyWrapper = async () => {
    if (selectedFileIndex === null) return;

    const selectedFile = fileList[selectedFileIndex];
    setLoading(true);
    setError(null);

    try {
      const result = await adminClassify(selectedFile.file);

      console.log('✅ 분류 완료:', result.data.documentType);

      setFileList(prev => prev.map((f, idx) =>
        idx === selectedFileIndex
          ? {
            ...f,
            classifyResult: result,
            status: 'classified'
          }
          : f
      ));

      alert(`✅ 분류 완료\n문서 타입: ${result.data.documentType}\n소요 시간: ${result.duration}`);

    } catch (err) {
      setError(err.message);
      console.error('❌ 분류 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminExtractFlashWrapper = async () => {
    if (selectedFileIndex === null) return;

    const selectedFile = fileList[selectedFileIndex];
    setLoading(true);
    setError(null);

    try {
      const documentType = selectedFile.classifyResult?.data?.documentType || null;
      const result = await adminExtractFlash(selectedFile.file, documentType);

      console.log('✅ Flash 추출 완료:', result.data);

      setFileList(prev => prev.map((f, idx) =>
        idx === selectedFileIndex
          ? {
            ...f,
            flashResult: result,
            status: 'flash-extracted'
          }
          : f
      ));

      alert(`✅ Flash 추출 완료\n소요 시간: ${result.duration}`);

    } catch (err) {
      setError(err.message);
      console.error('❌ Flash 추출 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminExtractProWrapper = async () => {
    if (selectedFileIndex === null) return;

    const selectedFile = fileList[selectedFileIndex];
    setLoading(true);
    setError(null);

    try {
      const documentType = selectedFile.classifyResult?.data?.documentType || null;
      const result = await adminExtractPro(selectedFile.file, documentType);

      console.log('✅ Pro 추출 완료:', result.data);

      setFileList(prev => prev.map((f, idx) =>
        idx === selectedFileIndex
          ? {
            ...f,
            proResult: result,
            status: 'pro-extracted'
          }
          : f
      ));

      alert(`✅ Pro 추출 완료\n소요 시간: ${result.duration}`);

    } catch (err) {
      setError(err.message);
      console.error('❌ Pro 추출 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminValidateWrapper = async () => {
    if (selectedFileIndex === null) return;

    const selectedFile = fileList[selectedFileIndex];

    if (!selectedFile.flashResult || !selectedFile.proResult) {
      alert('먼저 Flash와 Pro 추출을 완료해주세요');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const flashData = selectedFile.flashResult.data;
      const proData = selectedFile.proResult.data;

      // 간단한 비교 로직
      const differences = {};
      const allFields = [
        'plateNumber', 'vin', 'model', 'ownerName', 'modelYear',
        'productionDate', 'mileage', 'totalWeight', 'displacement',
        'width', 'length', 'height', 'cbm', 'deregistrationDate'
      ];

      let matchCount = 0;
      allFields.forEach(field => {
        const flashValue = flashData[field];
        const proValue = proData[field];

        if (String(flashValue || '') === String(proValue || '')) {
          matchCount++;
        } else {
          differences[field] = {
            flash: flashValue,
            pro: proValue,
            different: true
          };
        }
      });

      const matchRate = Math.round((matchCount / allFields.length) * 100);

      // 최종 데이터 구성
      const finalData = {
        ...flashData,
        alternatives: proData,
        differences: differences,
        metadata: {
          validated: true,
          matchRate: matchRate,
          needsReview: matchRate < 100,
          flashExtraction: flashData,
          proExtraction: proData
        }
      };

      setFileList(prev => prev.map((f, idx) =>
        idx === selectedFileIndex
          ? {
            ...f,
            extractedData: finalData,
            status: 'completed'
          }
          : f
      ));

      alert(
        `✅ 검증 완료\n\n` +
        `일치율: ${matchRate}%\n` +
        `일치 필드: ${matchCount}/${allFields.length}\n` +
        `차이 필드: ${Object.keys(differences).length}개\n\n` +
        `${matchRate === 100 ? '✅ 완벽히 일치합니다!' : '⚠️ 검토가 필요합니다.'}`
      );

    } catch (err) {
      setError(err.message);
      console.error('❌ 검증 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMultipleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
  };

  // 드래그 앤 드롭 핸들러
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    // 이미지와 PDF 파일만 필터링
    const validFiles = files.filter(file =>
      file.type.startsWith('image/') || file.type === 'application/pdf'
    );

    if (validFiles.length > 0) {
      addFiles(validFiles);
    } else {
      alert('이미지 또는 PDF 파일만 업로드 가능합니다.');
    }
  };

  // 파일 추가 공통 함수
  const addFiles = async (files) => {
    const newFiles = files.map((file, index) => {
      const isPDF = file.name.toLowerCase().endsWith('.pdf');
      return {
        id: Date.now() + index,
        name: file.name,
        file: file,
        preview: URL.createObjectURL(file),
        status: 'pending', // pending, processing, completed, error
        extractedData: null,
        error: null,
        checked: true, // ⭐ 업로드 시 자동 선택
        isPDF: isPDF,
        totalPages: null, // 아래에서 PDF 페이지 수 감지 후 업데이트
        pageResults: null // SSE로 받아서 업데이트
      };
    });

    setFileList(prev => [...prev, ...newFiles]);

    // 첫 번째 파일 자동 선택
    if (fileList.length === 0 && newFiles.length > 0) {
      setSelectedFileIndex(0);
      addLog(`📌 ${newFiles[0].name} 자동 선택됨`);
    }

    // 📄 PDF 파일의 페이지 수 감지 (비동기)
    newFiles.forEach(async (fileObj) => {
      if (fileObj.isPDF) {
        try {
          const arrayBuffer = await fileObj.file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          const numPages = pdf.numPages;
          console.log(`📄 [PDF 페이지 감지] ${fileObj.name}: ${numPages}페이지`);

          // 파일 리스트 업데이트
          setFileList(prev => prev.map(f =>
            f.id === fileObj.id
              ? { ...f, totalPages: numPages }
              : f
          ));
        } catch (error) {
          console.error(`❌ PDF 페이지 수 감지 실패: ${fileObj.name}`, error);
        }
      }
    });

    // Auto-start trigger
    if (isAutoExtractionEnabled) {
      setShouldAutoStart(true);
    }
  };

  // 체크박스 토글
  const toggleFileCheck = (index) => {
    setFileList(prev => prev.map((f, idx) =>
      idx === index ? { ...f, checked: !f.checked } : f
    ));
  };

  // 전체 선택/해제
  const toggleAllChecks = () => {
    const allChecked = fileList.every(f => f.checked);
    setFileList(prev => prev.map(f => ({ ...f, checked: !allChecked })));
  };

  // 선택된 파일 개수
  const checkedCount = fileList.filter(f => f.checked).length;

  // 🚀 처리 취소 핸들러 (Google AI Studio 스타일)
  const handleCancelProcessing = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsCancelled(true);
    setLoading(false);
    setProcessingCount(0);
    addLog('⛔ 사용자가 처리를 취소했습니다.', 'warning');
  };

  // 📄 멀티페이지 PDF 페이지 변경 핸들러
  const handlePdfPageChange = (newPage) => {
    console.log('📄 [handlePdfPageChange] 호출됨:', {
      newPage,
      totalPdfPages,
      pdfPageResultsLength: pdfPageResults.length,
      selectedFilePageResults: selectedFile?.pageResults?.length
    });

    // 🔧 수정: selectedFile.totalPages를 우선 사용 (pdfjs로 미리 감지된 페이지 수)
    const hasPageResults = selectedFile?.pageResults?.length > 0 || pdfPageResults.length > 0 || selectedFile?.totalPages > 0;
    const maxPages = selectedFile?.totalPages || selectedFile?.pageResults?.length || totalPdfPages || 0;

    console.log('📄 [handlePdfPageChange] maxPages 계산:', {
      selectedFileTotalPages: selectedFile?.totalPages,
      selectedFilePageResults: selectedFile?.pageResults?.length,
      totalPdfPages,
      maxPages,
      hasPageResults
    });

    if (newPage < 1 || newPage > maxPages || !hasPageResults) {
      console.log('⚠️ [handlePdfPageChange] 조건 불충족으로 return:', { newPage, maxPages, hasPageResults });
      return;
    }

    setCurrentPdfPage(newPage);
    setSelectedPageNumber(newPage); // 📄 OCR Viewer용 페이지 번호도 동기화
    setPageJumpToken(prev => prev + 1); // 📄 jumpToPage 강제 트리거
    console.log('✅ [handlePdfPageChange] 페이지 변경 완료:', newPage);

    // 해당 페이지의 데이터 적용
    const pageData = pdfPageResults[newPage - 1];
    if (pageData) {
      // 보정된 이미지 URL 업데이트
      setCorrectedImageUrl(pageData.correctedImageUrl);

      // 이미지 크기 업데이트
      if (pageData.ocrImageSize) {
        setOcrImageSize(pageData.ocrImageSize);
      }

      // 해당 페이지의 labels만 필터링하여 표시
      const pageLabels = ocrLabels.filter(label => label.pageNumber === newPage);
      // Note: ocrLabels 전체는 유지하고, 화면에는 현재 페이지만 표시하도록 처리됨

      console.log(`📄 [Page Change] Page ${newPage}/${totalPdfPages}, Labels: ${pageLabels.length}`);
    }
  };

  // 🎯 OCR 파이프라인: PaddleOCR → vLLM → Gemini (메인 화면용)
  // returnResult: true이면 setFileList 호출하지 않고 결과 객체 반환 (배치 처리용)
  const handleOcrPipeline = async (fileItem, returnResult = false) => {
    if (!fileItem?.file) {
      console.warn('[OCR Pipeline] No file provided');
      return returnResult ? null : undefined;
    }

    const file = fileItem.file;
    const startTime = Date.now();
    const isPDF = file.name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';

    // 템플릿 정보 가져오기
    const templateObj = selectedOcrTemplate
      ? Object.values(templates).find(t => t.name === selectedOcrTemplate)
      : null;
    const templateFields = templateObj?.singleFields || templateObj?.fields || [];
    const templateId = templateObj?.id || '';

    console.log('🚀 [OCR Pipeline] 시작:', {
      fileName: file.name,
      template: templateObj?.name || 'None',
      fieldCount: templateFields.length,
      processingMode: ocrProcessingMode,  // 🚀 처리 모드 로깅
      isPDF: isPDF
    });

    setLoading(true);
    setOcrLabels([]);
    setCorrectedImageUrl(null);
    addLog(`🔄 OCR 파이프라인 시작: ${file.name} (${ocrProcessingMode === 'speed' ? '⚡속도' : '📍좌표'} 모드)`);

    // 🚀 속도 기반 모드 + PDF 파일 → 안정적인 기존 엔드포인트 사용
    if (ocrProcessingMode === 'speed' && isPDF) {
      try {
        addLog('  ⚡ [속도 모드] PDF 네이티브 처리 중...');

        const formData = new FormData();
        formData.append('file', file);
        formData.append('templateId', templateId);
        // 📘 선택된 추출 모델 전달 (langextract 등)
        formData.append('selectedValidationModels', JSON.stringify(selectedValidationModels));

        // 🎯 안정적인 기존 엔드포인트 사용 (SSE는 추출 오류로 임시 비활성화)
        const sseResponse = await fetch(`${API_BASE_URL}/api/ocr/gemini-extract-pdf-native`, {
          method: 'POST',
          body: formData
        });

        if (!sseResponse.ok) {
          throw new Error(`API 호출 실패: ${sseResponse.status}`);
        }

        const pdfNativeData = await sseResponse.json();

        if (pdfNativeData.success && pdfNativeData.data) {
          const elapsed = Date.now() - startTime;
          addLog(`  ✅ [속도 모드] 완료: ${pdfNativeData.data.pageResults?.length || 0}페이지 (${(elapsed / 1000).toFixed(1)}초)`);

          const finalResult = {
            documentType: templateObj?.name || '미분류',
            ...pdfNativeData.data,
            metadata: {
              mode: 'pdf-native',
              elapsed: elapsed,
              apiDuration: pdfNativeData.metadata?.apiDuration
            }
          };

          if (!returnResult) {
            // 🔍 Debug: grounded 데이터 확인
            const receivedGrounding = pdfNativeData.data.groundedFields || {};
            console.log('📘 [속도 모드] groundedFields:', {
              totalFields: Object.keys(receivedGrounding).length,
              fieldsWithBbox: Object.values(receivedGrounding).filter(v => v?.bbox?.coordinates).length
            });

            setFileList(prev => prev.map((f, idx) =>
              idx === selectedFileIndex
                ? {
                  ...f,
                  status: 'completed',
                  extractedData: finalResult,
                  flashData: finalResult,
                  proData: finalResult,
                  pageResults: pdfNativeData.data.pageResults || [],
                  items: pdfNativeData.data.items || [],
                  totalPages: pdfNativeData.metadata?.totalPages || pdfNativeData.data.pageResults?.length || 1,
                  groundedFields: receivedGrounding, // ⭐ [Grounding] LangExtract 필드 위치 정보
                  pageDimensions: pdfNativeData.data.pageDimensions || {} // ⭐ [Grounding] PDF 페이지별 크기 정보
                }
                : f
            ));
            // 🔧 OCR 완료 후 grid 탭 자동 전환 비활성화 (사용자 요청: 1.문서인식에서 추출결과 확인)
            // setActiveTab('grid');

            // ⭐ OCR 완료 후 전체 페이지 기본 체크
            const totalPageCount = pdfNativeData.metadata?.totalPages || pdfNativeData.data.pageResults?.length || 1;
            const allPages = Array.from({ length: totalPageCount }, (_, i) => i + 1);
            setCheckedPages(allPages);
            console.log(`📄 [OCR 완료] 전체 ${totalPageCount}페이지 기본 체크`)

            // 🔍 청크 디버그 팝업 (토글 ON일 때 자동 표시)
            if (showChunkDebug && pdfNativeData.chunkDetails) {
              setChunkDebugData({
                chunkDetails: pdfNativeData.chunkDetails,
                referenceExample: pdfNativeData.referenceExample,
                totalPages: pdfNativeData.metadata?.totalPages || 0
              });
              setShowChunkDebugModal(true);
            }
          }

          setLoading(false);
          addLog(`✅ OCR 파이프라인 완료 (${(elapsed / 1000).toFixed(1)}초)`);

          return returnResult ? {
            fileId: fileItem.id,
            isPDF: true,
            finalResult,
            mode: 'pdf-native',
            pageResults: pdfNativeData.data.pageResults || [],
            paddlePageResults: pdfNativeData.data.pageResults || [],
            totalPages: pdfNativeData.metadata?.totalPages || pdfNativeData.data.pageResults?.length || 1,
            multiPage: (pdfNativeData.data.pageResults?.length || 0) > 1,
            groundedFields: pdfNativeData.data.groundedFields || {}, // ⭐ [Grounding]
            pageDimensions: pdfNativeData.data.pageDimensions || {} // ⭐ [Grounding]
          } : undefined;
        } else {
          throw new Error(pdfNativeData.error || 'PDF 네이티브 처리 실패');
        }


      } catch (pdfError) {
        // 🚨 폴백 제거: 명확한 에러 표시 (사용자가 문제를 인지할 수 있도록)
        console.error('❌ [속도 모드] 실패:', pdfError);
        addLog(`  ❌ [속도 모드] 실패: ${pdfError.message}`, 'error');

        // 🔔 사용자에게 에러 알림 (토스트)
        if (window.toast) {
          window.toast.error(`PDF 추출 실패: ${pdfError.message}`);
        } else {
          alert(`PDF 추출 실패: ${pdfError.message}\n\n좌표 모드로 다시 시도하려면 처리모드를 변경하세요.`);
        }

        setLoading(false);
        setFileList(prev => prev.map((f, idx) =>
          idx === selectedFileIndex
            ? { ...f, status: 'error', error: pdfError.message }
            : f
        ));

        return returnResult ? { fileId: fileItem.id, error: pdfError.message } : undefined;
      }
    }

    try {
      // ===== 1단계: PaddleOCR =====
      setOcrPipelineStep('1/3 PaddleOCR...');
      addLog('  📷 [1/3] PaddleOCR 텍스트 감지 중...');

      const formData = new FormData();
      formData.append('file', file);

      const paddleRes = await fetch(`${API_BASE_URL}/api/ocr/paddle-only`, {
        method: 'POST',
        body: formData
      });
      const paddleData = await paddleRes.json();

      // 🔍 DEBUG: 백엔드 응답 확인
      console.log('🔍 [DEBUG] paddleData 응답:', JSON.stringify({
        success: paddleData.success,
        multiPage: paddleData.multiPage,
        totalPages: paddleData.totalPages,
        hasPageResults: !!paddleData.pageResults,
        pageResultsLength: paddleData.pageResults?.length,
        hasLabels: !!paddleData.labels
      }));

      if (!paddleData.success) {
        throw new Error(paddleData.error || 'PaddleOCR 실패');
      }

      // ⭐ 멀티페이지 PDF 처리
      let allLabels = [];
      let correctedUrl = null;
      let pageOcrImageSize = null;

      if (paddleData.multiPage && paddleData.pageResults) {
        console.log(`📄 [OCR Pipeline] 멀티페이지 PDF: ${paddleData.totalPages}페이지`);
        addLog(`  📄 멀티페이지 PDF 감지: ${paddleData.totalPages}페이지`);

        // 모든 페이지의 labels 합치기
        paddleData.pageResults.forEach((pageResult, idx) => {
          const pageLabels = (pageResult.labels || []).map((label, labelIdx) => ({
            id: label.id || `ocr-page${pageResult.pageNumber}-${labelIdx}`,
            text: label.text || '',
            bbox: label.bbox,
            confidence: label.confidence,
            fieldKey: label.fieldKey || null,
            pageNumber: pageResult.pageNumber,
            vllmText: null,
            geminiText: null
          }));
          allLabels = [...allLabels, ...pageLabels];

          console.log(`  📄 Page ${pageResult.pageNumber}: ${pageLabels.length} labels`);
        });

        // 첫 페이지의 corrected image 사용 (현재 뷰어용)
        const firstPage = paddleData.pageResults[0];
        correctedUrl = firstPage?.correctedImageUrl
          ? `${API_BASE_URL}${firstPage.correctedImageUrl}?t=${Date.now()}`
          : null;
        pageOcrImageSize = firstPage?.ocrImageSize;

        addLog(`  ✅ 총 ${allLabels.length}개 텍스트 감지 (${paddleData.totalPages}페이지)`);
      } else {
        // 단일 페이지 처리 (기존 로직)
        correctedUrl = paddleData.correctedImageUrl
          ? `${API_BASE_URL}${paddleData.correctedImageUrl}?t=${Date.now()}`
          : null;
        pageOcrImageSize = paddleData.ocrImageSize;

        allLabels = (paddleData.labels || []).map((label, idx) => ({
          id: label.id || `ocr-${idx}`,
          text: label.text || '',
          bbox: label.bbox,
          confidence: label.confidence,
          fieldKey: label.fieldKey || null,
          vllmText: null,
          geminiText: null
        }));

        addLog(`  ✅ PaddleOCR: ${allLabels.length}개 텍스트 감지${paddleData.detectedAngle ? ` (${paddleData.detectedAngle}° 보정)` : ''}`);
      }

      // 보정된 이미지 URL 설정
      setCorrectedImageUrl(correctedUrl);

      // 📄 멀티페이지 상태 저장
      if (paddleData.multiPage && paddleData.pageResults) {
        console.log('📄 [Frontend] 멀티페이지 감지:', paddleData.totalPages, '페이지');
        console.log('📄 [Frontend] pageResults:', paddleData.pageResults.length, '개');
        setPdfPageResults(paddleData.pageResults.map(pr => ({
          ...pr,
          correctedImageUrl: pr.correctedImageUrl
            ? `${API_BASE_URL}${pr.correctedImageUrl}?t=${Date.now()}`
            : null
        })));
        setCurrentPdfPage(1);
        setSelectedPageNumber(1); // 📄 OCR Viewer용 페이지 번호도 초기화
        setTotalPdfPages(paddleData.totalPages);
        console.log('📄 [Frontend] setTotalPdfPages 호출:', paddleData.totalPages);
      } else {
        // 단일 페이지 - 멀티페이지 상태 초기화
        setPdfPageResults([]);
        setCurrentPdfPage(1);
        setSelectedPageNumber(1);
        setTotalPdfPages(1);
      }

      // 이미지 크기 저장
      if (pageOcrImageSize) {
        setOcrImageSize(pageOcrImageSize);
      }

      // 🔍 디버그: labels과 bbox 데이터 확인
      console.log('🔍 [OCR Debug] allLabels:', allLabels.length, 'items');
      console.log('🔍 [OCR Debug] First label bbox:', allLabels[0]?.bbox);
      console.log('🔍 [OCR Debug] ocrImageSize:', pageOcrImageSize);

      setOcrLabels(allLabels);

      // initialLabels를 allLabels로 대체
      const initialLabels = allLabels;



      // ===== 2단계: vLLM (병렬 시작) =====
      setOcrPipelineStep('2/3 AI 병합...');
      addLog('  🤖 [2/3] vLLM 필드 병합 중...');

      // 📄 pageNumber 포함하여 백엔드에서 페이지별 분리 처리 가능하게 함
      const ocrBoxes = initialLabels.map(l => ({ text: l.text, bbox: l.bbox, pageNumber: l.pageNumber || 1 }));

      // 📄 멀티페이지 PDF면 단일 Gemini 호출 스킵 (나중에 병렬 추출)
      const isMultiPage = paddleData.multiPage && paddleData.pageResults?.length > 1;

      // 🚀 vLLM 타임아웃: 30초 (페이지별 병렬 처리 시간 확보)
      const vllmController = new AbortController();
      const vllmTimeout = setTimeout(() => vllmController.abort(), 30000);

      // vLLM + Gemini 병렬 실행
      const [mergeResult, geminiResult] = await Promise.allSettled([
        // vLLM Merge (2초 타임아웃)
        fetch(`${API_BASE_URL}/api/ocr/merge-with-llm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ocrBoxes: ocrBoxes,
            templateFields: templateFields
          }),
          signal: vllmController.signal
        }).then(res => res.json()).finally(() => clearTimeout(vllmTimeout)),

        // Gemini Extract (멀티페이지면 스킵!)
        isMultiPage
          ? Promise.resolve({ success: true, data: {}, skipped: true })
          : (async () => {
            const geminiFormData = new FormData();
            // correctedUrl 또는 원본 파일 사용
            if (correctedUrl) {
              const imgRes = await fetch(correctedUrl);
              const imgBlob = await imgRes.blob();
              geminiFormData.append('file', imgBlob, 'image.png');
            } else {
              geminiFormData.append('file', file);
            }
            geminiFormData.append('templateId', templateId);
            geminiFormData.append('templateFields', JSON.stringify(templateFields));
            geminiFormData.append('skipPaddleOCR', 'true');  // 🚀 중복 PaddleOCR 방지
            return fetch(`${API_BASE_URL}/api/ocr/gemini-extract`, {
              method: 'POST',
              body: geminiFormData
            }).then(res => res.json());
          })()
      ]);

      // ===== vLLM 결과 처리 =====
      let updatedLabels = [...initialLabels];
      if (mergeResult.status === 'fulfilled' && mergeResult.value.success) {
        const mergedData = mergeResult.value.data;
        addLog(`  ✅ vLLM: ${Object.keys(mergedData).length}개 필드 추출`);

        // 역매핑: 추출값으로 bbox에 fieldKey 할당
        for (const [fieldKey, extractedValue] of Object.entries(mergedData)) {
          if (!extractedValue || typeof extractedValue !== 'string') continue;

          const normalizedValue = extractedValue.replace(/[\s\-.,·()]/g, '').toLowerCase();

          for (let i = 0; i < updatedLabels.length; i++) {
            const label = updatedLabels[i];
            if (label.fieldKey) continue;
            if (!label.text) continue;

            const normalizedText = label.text.replace(/[\s\-.,·()]/g, '').toLowerCase();
            const isMatch = normalizedValue.includes(normalizedText) || normalizedText.includes(normalizedValue);

            if (isMatch && normalizedText.length >= 2) {
              updatedLabels[i] = {
                ...label,
                fieldKey: fieldKey,
                vllmText: extractedValue,
                source: 'vllm'
              };
            }
          }
        }
      } else {
        addLog('  ⚠️ vLLM 병합 실패', 'warning');
      }

      // ===== 3단계: Gemini 결과 처리 =====
      setOcrPipelineStep('3/3 Gemini 추출...');

      let extractedData = {};
      if (geminiResult.status === 'fulfilled' && geminiResult.value.success) {
        extractedData = geminiResult.value.data || {};
        addLog(`  ✅ Gemini: ${Object.keys(extractedData).length}개 필드 추출`);

        // Gemini 결과를 labels에 geminiText로 추가
        for (const [fieldKey, value] of Object.entries(extractedData)) {
          // 해당 fieldKey를 가진 labels 찾기
          const matchingIndices = updatedLabels
            .map((l, i) => l.fieldKey === fieldKey ? i : -1)
            .filter(i => i >= 0);

          matchingIndices.forEach(idx => {
            updatedLabels[idx] = {
              ...updatedLabels[idx],
              geminiText: value
            };
          });
        }
      } else {
        addLog('  ⚠️ Gemini 추출 실패', 'warning');
      }

      setOcrLabels(updatedLabels);

      // ===== 결과를 fileList에 저장 =====
      const elapsed = Date.now() - startTime;
      const finalResult = {
        documentType: templateObj?.name || '미분류',
        ...extractedData,
        metadata: {
          ocrLabels: updatedLabels,
          correctedImageUrl: correctedUrl,
          ocrImageSize: paddleData.ocrImageSize,
          elapsed: elapsed
        }
      };

      // 📄 멀티페이지 PDF: 각 페이지에 대해 LLM 추출 실행
      let allPageExtractedData = [];
      if (paddleData.multiPage && paddleData.pageResults && paddleData.pageResults.length > 1) {
        addLog(`📄 멀티페이지 PDF: ${paddleData.totalPages}개 페이지 LLM 추출 중...`);

        // 🚀 30페이지 청크 기반 배치 API 사용 (문맥 유지 + 효율성)
        addLog(`  🚀 ${paddleData.totalPages}페이지 배치 Gemini 추출 시작 (30페이지/청크)...`);

        try {
          // 페이지 이미지 정보 수집
          const pageImages = paddleData.pageResults.map((pageResult, i) => {
            const pageNum = pageResult.pageNumber || (i + 1);
            const pageImageUrl = pageResult.correctedImageUrl
              ? (pageResult.correctedImageUrl.startsWith('http')
                ? pageResult.correctedImageUrl
                : `${API_BASE_URL}${pageResult.correctedImageUrl}`)
              : null;

            return {
              imageUrl: pageImageUrl,
              pageNumber: pageNum
            };
          }).filter(p => p.imageUrl);

          console.log(`📄 [Batch] 유효 페이지 이미지: ${pageImages.length}개`);

          // 배치 API 호출
          const batchRes = await fetch(`${API_BASE_URL}/api/ocr/gemini-extract-batch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pageImages: pageImages,
              templateId: templateId
            })
          });
          const batchData = await batchRes.json();

          if (batchData.success && batchData.data) {
            addLog(`  ✅ 배치 추출 완료: ${batchData.metadata?.chunksSuccessful || 1}/${batchData.metadata?.chunksProcessed || 1} 청크 성공 (${(batchData.metadata?.elapsed / 1000).toFixed(1)}초)`);

            // 배치 결과를 페이지별 데이터 형태로 변환
            // 실제 전송된 pageImages의 pageNumber를 사용 (첫 번째 페이지에 전체 데이터 할당)
            const validPageNumbers = pageImages.map(p => p.pageNumber);
            const firstValidPage = validPageNumbers[0] || 1;

            allPageExtractedData = [{
              pageNumber: firstValidPage,  // 실제 첫 번째 유효 페이지 번호 사용
              data: {
                documentType: templateObj?.name || '미분류',
                ...batchData.data
              }
            }];

            // 나머지 유효 페이지는 빈 데이터 (테이블 행은 첫 번째에 병합됨)
            for (let i = 1; i < validPageNumbers.length; i++) {
              allPageExtractedData.push({
                pageNumber: validPageNumbers[i],
                data: { documentType: templateObj?.name || '미분류' }
              });
            }

            console.log(`📄 [Batch] 페이지 매핑: ${validPageNumbers.join(', ')}`);
          } else {
            // 배치 실패 시 기존 방식으로 폴백
            console.warn('⚠️ 배치 API 실패, 기존 방식으로 폴백:', batchData.error);
            addLog(`  ⚠️ 배치 실패, 개별 추출로 전환...`, 'warning');

            // 기존 개별 페이지 처리 로직
            const extractionPromises = paddleData.pageResults.map(async (pageResult, i) => {
              const pageNum = pageResult.pageNumber || (i + 1);
              const pageImageUrl = pageResult.correctedImageUrl
                ? (pageResult.correctedImageUrl.startsWith('http')
                  ? pageResult.correctedImageUrl
                  : `${API_BASE_URL}${pageResult.correctedImageUrl}?t=${Date.now()}`)
                : null;

              if (!pageImageUrl) return { pageNumber: pageNum, data: null };

              try {
                const geminiFormData = new FormData();
                const imgRes = await fetch(pageImageUrl);
                const imgBlob = await imgRes.blob();
                geminiFormData.append('file', imgBlob, `page${pageNum}.png`);
                geminiFormData.append('templateId', templateId);
                geminiFormData.append('templateFields', JSON.stringify(templateFields));
                geminiFormData.append('skipPaddleOCR', 'true');

                const geminiRes = await fetch(`${API_BASE_URL}/api/ocr/gemini-extract`, {
                  method: 'POST',
                  body: geminiFormData
                });
                const geminiJson = await geminiRes.json();

                if (geminiJson.success && geminiJson.data) {
                  return {
                    pageNumber: pageNum,
                    data: { documentType: templateObj?.name || '미분류', ...geminiJson.data }
                  };
                }
                return { pageNumber: pageNum, data: null };
              } catch (err) {
                console.error(`❌ [페이지 ${pageNum}] Gemini 추출 오류:`, err);
                return { pageNumber: pageNum, data: null };
              }
            });

            allPageExtractedData = await Promise.all(extractionPromises);
          }
        } catch (batchError) {
          console.error('❌ 배치 API 오류:', batchError);
          addLog(`  ❌ 배치 오류: ${batchError.message}`, 'error');
        }

        allPageExtractedData.sort((a, b) => a.pageNumber - b.pageNumber);
        addLog(`✅ 멀티페이지 LLM 추출 완료: ${allPageExtractedData.filter(p => p.data).length}/${paddleData.totalPages} 성공`);
      }

      // 📄 멀티페이지 머지 그룹 생성
      let mergedGroups = null;
      if (paddleData.multiPage && allPageExtractedData.length > 0) {
        // allPageExtractedData를 pageResults 형태로 변환
        let pageResultsForMerge = allPageExtractedData.map(p => ({
          pageNumber: p.pageNumber,
          extractedData: p.data,
          documentType: p.data?.documentType
        }));

        // 📄 템플릿에서 mergeRules 수집 및 적용
        const allMergeRules = [];
        Object.values(templates || {}).forEach(tpl => {
          if (tpl.mergeRules && Array.isArray(tpl.mergeRules)) {
            allMergeRules.push(...tpl.mergeRules);
          }
        });

        if (allMergeRules.length > 0) {
          pageResultsForMerge = applyMergeRules(pageResultsForMerge, allMergeRules);
          addLog(`🔄 머지 규칙 적용: ${allMergeRules.length}개`);
        }

        mergedGroups = mergePageResults(pageResultsForMerge, templates, {
          defaultMatchKey: '성명',
          enableMerge: true
        });

        addLog(`📦 머지 완료: ${pageResultsForMerge.length}페이지 → ${mergedGroups.length}그룹`);
      }

      // 🎯 결과 객체 생성 (배치 모드/단일 모드 공통)
      const ocrResult = {
        fileId: fileItem.id,
        isPDF: fileItem.isPDF,
        pageNumber: selectedPageNumber || 1,
        finalResult,
        updatedLabels,
        ocrImageSize: paddleData.ocrImageSize,
        correctedUrl,
        // 📄 멀티페이지 PDF 데이터 추가
        multiPage: paddleData.multiPage || false,
        totalPages: paddleData.totalPages || 1,
        paddlePageResults: paddleData.pageResults || null, // 전체 페이지 OCR 결과
        allPageExtractedData: allPageExtractedData.length > 0 ? allPageExtractedData : null, // 📄 각 페이지 LLM 결과
        mergedGroups // 📄 머지된 그룹
      };

      // 🔥 배치 모드: 결과만 반환 (setFileList는 호출하지 않음)
      if (returnResult) {
        addLog(`✅ OCR 파이프라인 완료 (${(elapsed / 1000).toFixed(1)}초)`);
        return ocrResult;
      }

      // 🎯 단일 모드: 직접 setFileList 호출
      setFileList(prev => prev.map((f, idx) => {
        if (idx !== selectedFileIndex) return f;

        if (f.isPDF) {
          // 🎯 PDF 파일: pageResults 업데이트

          // ⭐ 멀티페이지 PDF 처리: paddleData.pageResults 사용
          console.log('🔍 [FileList Update] PDF 조건 확인:', {
            multiPage: paddleData.multiPage,
            hasPageResults: !!paddleData.pageResults,
            pageResultsLength: paddleData.pageResults?.length,
            totalPages: paddleData.totalPages
          });
          if (paddleData.multiPage && paddleData.pageResults && paddleData.pageResults.length > 1) {
            console.log('📄 [FileList Update] 멀티페이지 PDF:', paddleData.totalPages, '페이지');

            // 모든 페이지 결과를 pageResults로 저장
            const allPageResults = paddleData.pageResults.map((pr, prIdx) => ({
              pageNumber: pr.pageNumber || (prIdx + 1),
              ocrImageSize: pr.ocrImageSize,
              boundingBoxes: (pr.labels || []).map((label, labelIdx) => ({
                id: label.id || `ocr-page${pr.pageNumber}-${labelIdx}`,
                text: label.text || '',
                bbox: label.bbox,
                confidence: label.confidence,
                fieldKey: label.fieldKey || null,
                pageNumber: pr.pageNumber
              })),
              correctedImageUrl: pr.correctedImageUrl
                ? (pr.correctedImageUrl.startsWith('http') ? pr.correctedImageUrl : `${API_BASE_URL}${pr.correctedImageUrl}?t=${Date.now()}`)
                : null,
              pngImage: pr.correctedImageUrl
                ? (pr.correctedImageUrl.startsWith('http') ? pr.correctedImageUrl : `${API_BASE_URL}${pr.correctedImageUrl}?t=${Date.now()}`)
                : null,
              flashData: null,  // LLM 추출 결과는 아직 없음
              proData: null,
              extractedData: null
            }));

            console.log('📄 [FileList Update] allPageResults:', allPageResults.length, '페이지');

            return {
              ...f,
              status: 'completed',
              pageResults: allPageResults,
              totalPages: paddleData.totalPages // ⭐ totalPages 설정!
            };
          }

          // 단일 페이지 PDF 처리 (기존 로직)
          const pageNum = selectedPageNumber || 1;
          const existingPageResults = f.pageResults || [];

          // 해당 페이지 결과 업데이트 또는 추가
          let pageResultUpdated = false;
          const updatedPageResults = existingPageResults.map(pr => {
            if (pr.pageNumber === pageNum) {
              pageResultUpdated = true;
              return {
                ...pr,
                flashData: finalResult,  // PageBasedGridPanel이 인식
                proData: finalResult,    // 둘 다 같은 값으로 설정
                extractedData: finalResult,
                boundingBoxes: updatedLabels,
                ocrImageSize: paddleData.ocrImageSize,
                pngImage: correctedUrl || pr.pngImage  // correctedUrl은 이미 전체 URL
              };
            }
            return pr;
          });

          // 페이지가 없으면 추가
          if (!pageResultUpdated) {
            updatedPageResults.push({
              pageNumber: pageNum,
              flashData: finalResult,
              proData: finalResult,
              extractedData: finalResult,
              boundingBoxes: updatedLabels,
              ocrImageSize: paddleData.ocrImageSize,
              pngImage: correctedUrl || null  // correctedUrl은 이미 전체 URL
            });
          }

          return {
            ...f,
            status: 'completed',
            pageResults: updatedPageResults,
            totalPages: f.totalPages || updatedPageResults.length // ⭐ totalPages 설정
          };
        } else {
          // 🎯 이미지 파일: extractedData + flashData/proData 업데이트
          return {
            ...f,
            status: 'completed',
            extractedData: finalResult,
            flashData: finalResult,   // PageBasedGridPanel 호환
            proData: finalResult,     // PageBasedGridPanel 호환
            boundingBoxes: updatedLabels,
            ocrImageSize: paddleData.ocrImageSize
          };
        }
      }));

      setOcrPipelineStep('');
      addLog(`✅ OCR 파이프라인 완료 (${(elapsed / 1000).toFixed(1)}초)`);

      // 🔧 OCR 완료 후 grid 탭 자동 전환 비활성화
      // console.log('🔄 [OCR 완료] 문서검증 탭으로 자동 전환');
      // setActiveTab('grid');

    } catch (error) {
      console.error('[OCR Pipeline Error]', error);
      setOcrPipelineStep('');
      addLog(`❌ OCR 파이프라인 오류: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // 옵션5: 일괄 병렬 처리 (분류 + Flash + Pro 병렬)
  const handleBatchFullProcess = async () => {
    if (loading) {
      console.warn('[일괄 처리] 이미 실행 중입니다. 중복 클릭 무시.');
      return;
    }

    const checkedFiles = fileList.map((f, idx) => ({ ...f, index: idx })).filter(f => f.checked);

    if (checkedFiles.length === 0) {
      alert('처리할 파일을 선택해주세요');
      return;
    }

    // 🚀 취소 상태 초기화 및 AbortController 생성
    setIsCancelled(false);
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setProcessingCount(checkedFiles.length);
    setShowLogs(true); // 로그 영역 표시
    setProcessingLogs([]); // 이전 로그 초기화
    addLog(`일괄 처리 시작... (${ocrProcessingMode === 'speed' ? '⚡속도' : '📍좌표'} 모드)`);

    // 🚀 속도 모드: PDF 파일은 네이티브 처리
    if (ocrProcessingMode === 'speed') {
      try {
        const pdfFiles = checkedFiles.filter(f =>
          f.name.toLowerCase().endsWith('.pdf') || f.file?.type === 'application/pdf'
        );
        const imageFiles = checkedFiles.filter(f =>
          !(f.name.toLowerCase().endsWith('.pdf') || f.file?.type === 'application/pdf')
        );

        addLog(`⚡ [속도 모드] PDF: ${pdfFiles.length}개, 이미지: ${imageFiles.length}개`);

        // 템플릿 정보 가져오기
        const templateObj = selectedOcrTemplate
          ? Object.values(templates).find(t => t.name === selectedOcrTemplate)
          : null;
        const templateId = templateObj?.id || '';

        // PDF 파일들 병렬 처리
        if (pdfFiles.length > 0) {
          const pdfResults = await Promise.allSettled(
            pdfFiles.map(async (fileItem, i) => {
              addLog(`  ⚡ [${i + 1}/${pdfFiles.length}] ${fileItem.name} PDF 네이티브 처리 중...`);

              const formData = new FormData();
              formData.append('file', fileItem.file);
              formData.append('templateId', templateId);
              // 📘 선택된 추출 모델 전달 (langextract 등)
              formData.append('selectedValidationModels', JSON.stringify(selectedValidationModels));

              const res = await fetch(`${API_BASE_URL}/api/ocr/gemini-extract-pdf-native`, {
                method: 'POST',
                body: formData,
                signal: abortControllerRef.current?.signal
              });
              const data = await res.json();

              if (data.success && data.data) {
                const result = {
                  documentType: templateObj?.name || '미분류',
                  ...data.data,
                  metadata: { mode: 'pdf-native', elapsed: data.metadata?.elapsed }
                };

                // 🔍 [DEBUG] API 응답에서 groundedFields 확인
                const receivedGrounding = data.data.groundedFields || {};
                const groundedCount = Object.keys(receivedGrounding).length;
                const withBbox = Object.values(receivedGrounding).filter(v => v?.bbox?.coordinates).length;
                console.log('📘 [API Response] groundedFields:', {
                  totalFields: groundedCount,
                  fieldsWithBbox: withBbox,
                  hasGrounding: data.metadata?.hasGrounding,
                  sampleKeys: Object.keys(receivedGrounding).slice(0, 5)
                });

                // 🔍 Debug: 파일 인덱스 확인
                console.log('📦 [setFileList] Saving groundedFields to fileItem.index:', {
                  fileItemIndex: fileItem.index,
                  fileName: fileItem.name,
                  fileListLength: pdfFiles.length
                });

                setFileList(prev => prev.map((f, idx) =>
                  idx === fileItem.index
                    ? {
                      ...f,
                      status: 'completed',
                      extractedData: result,
                      flashData: result,
                      proData: result,
                      groundedFields: receivedGrounding, // ⭐ [Grounding] LangExtract 필드 위치 정보
                      pageDimensions: data.data.pageDimensions || {} // ⭐ [Grounding] PDF 페이지별 크기 정보
                    }
                    : f
                ));

                addLog(`  ✅ [${i + 1}/${pdfFiles.length}] ${fileItem.name} 완료 (${(data.metadata?.elapsed / 1000).toFixed(1)}초)`);
                return { fileItem, result };
              } else {
                throw new Error(data.error || 'PDF 처리 실패');
              }
            })
          );

          const successCount = pdfResults.filter(r => r.status === 'fulfilled').length;
          addLog(`✅ [속도 모드] PDF 처리 완료: ${successCount}/${pdfFiles.length} 성공`);
        }

        // 이미지 파일이 없으면 종료
        if (imageFiles.length === 0) {
          setLoading(false);
          setProcessingCount(0);
          // 🔧 setActiveTab('grid'); - 비활성화
          addLog(`✅ 전체 처리 완료!`);
          return;
        }

        // 이미지 파일들은 기존 방식으로 계속 진행
        addLog(`📍 이미지 파일 ${imageFiles.length}개는 좌표 모드로 처리...`);
        // checkedFiles를 imageFiles로 교체하여 아래 기존 로직 실행
        // (아래 Promise.allSettled에서 imageFiles 사용)
      } catch (speedModeError) {
        console.error('❌ [속도 모드] 오류:', speedModeError);
        addLog(`⚠️ 속도 모드 오류, 기존 방식으로 전환...`, 'warning');
        // 폴백: 기존 방식으로 계속 진행
      }
    }

    try {
      // ✨ 전체 파일을 한 번에 처리 (분류 + Flash + Pro 병렬)
      addLog(`${checkedFiles.length}개 파일 전체 추출 시작...`);

      const results = await Promise.allSettled(
        checkedFiles.map(async (fileItem, i) => {
          const fileStartTime = Date.now();

          return new Promise(async (resolve, reject) => {
            try {
              addLog(`  🔄 [${i + 1}/${checkedFiles.length}] ${fileItem.name} 처리 중...`);

              const formData = new FormData();
              formData.append('image', fileItem.file);
              formData.append('useClaudeForValidation', useClaudeForValidation);
              formData.append('validationModel', validationModel); // 레거시 호환용
              formData.append('classificationFlashModel', classificationFlashModel); // 분류 모델 전달
              formData.append('flashModelVersion', classificationFlashModel); // ⭐ 분류 모델 버전/타입 전송 (Gemini vs Upstage)
              formData.append('speedMode', speedMode); // 레거시 호환용
              formData.append('selectedValidationModels', JSON.stringify(selectedValidationModels)); // 새로운 검증 모델 배열
              formData.append('templateExtractionModel', templateExtractionModel); // ⭐ 템플릿 추출 모델 (병렬 추출과 별개)

              // ⭐ ThinkingConfig 전달 (Gemini 2.5/3.0)
              formData.append('thinkingBudget', thinkingBudget.toString());
              formData.append('thinkingLevel', thinkingLevel);

              // ⭐ DeepSeek 설정 (selectedValidationModels에 포함되어 있으면 전송)
              if (selectedValidationModels.includes('deepseek')) {
                formData.append('deepseekMode', deepseekMode);
                formData.append('grounding', grounding.toString()); // ⭐ 명시적 문자열 변환
              }

              // ⭐ Datalab 설정 (selectedValidationModels에 포함되어 있으면 전송)
              if (selectedValidationModels.includes('datalab-marker')) {
                formData.append('datalabBoundingBox', datalabBoundingBox.toString());
              }

              // 🔧 수동 템플릿 선택 - 분류 건너뛰고 해당 템플릿으로 추출
              console.log('🔍 [Debug] Template selection check:', {
                selectedOcrTemplate: selectedOcrTemplate || 'EMPTY',
                templatesCount: Object.keys(templates).length,
                templateNames: Object.values(templates).map(t => t.name).slice(0, 5)
              });

              if (selectedOcrTemplate) {
                formData.append('templateName', selectedOcrTemplate);
                // ⭐ 템플릿 전체 객체도 전송 (Structured Output용)
                const templateObj = Object.values(templates).find(t => t.name === selectedOcrTemplate);
                if (templateObj) {
                  formData.append('template', JSON.stringify(templateObj));
                  console.log('✅ [Template] Structured Output 템플릿 전송:', templateObj.name);
                  console.log('   └── singleFields:', templateObj.singleFields?.length || 0);
                  console.log('   └── tableFields:', templateObj.tableFields?.length || 0);
                  console.log('   └── fields:', templateObj.fields?.length || 0);
                } else {
                  console.warn('⚠️ [Template] 템플릿을 찾을 수 없음:', selectedOcrTemplate);
                }
                addLog(`  📋 템플릿 지정: ${selectedOcrTemplate}`);
              } else {
                console.warn('⚠️ [Template] selectedOcrTemplate가 비어있음 - 자동 분류 모드');
              }

              // ⭐ 레거시 호환: ocrEngine === 'deepseek'이고 selectedValidationModels가 비어있으면 model 전송
              if (ocrEngine === 'deepseek' && selectedValidationModels.length === 0) {
                formData.append('model', 'deepseek');
              }

              formData.append('enableHybridOCR', enableHybridOCR); // ⭐ Hybrid Mode Flag 추가
              formData.append('enableClassification', enableClassification); // ⭐ 문서 분류 활성화 플래그


              let classifyResult = null;
              let flashResult = null;
              let proResult = null;
              let totalPages = 1;
              let pageResults = [];

              // Fetch with SSE response stream
              const response = await fetch(`${API_BASE_URL}/api/ocr/extract-stream`, {
                method: 'POST',
                headers: ocrEngine === 'deepseek' ? { 'X-DeepSeek-Server-URL': deepseekServerUrl } : {},
                body: formData,
                signal: abortControllerRef.current?.signal // 🚀 취소 가능하도록 signal 추가
              });

              if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
              }

              const reader = response.body.getReader();
              const decoder = new TextDecoder();
              let buffer = '';

              while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n\n');
                buffer = lines.pop(); // 마지막 불완전한 줄은 버퍼에 유지

                for (const line of lines) {
                  if (!line.trim()) continue;

                  const eventMatch = line.match(/^event: (.+)\ndata: (.+)$/s);
                  if (!eventMatch) continue;

                  const [, event, dataStr] = eventMatch;
                  const data = JSON.parse(dataStr);

                  if (event === 'progress') {
                    addLog(`    📊 [${i + 1}/${checkedFiles.length}] ${data.message || data.step}`);
                  } else if (event === 'pages') {
                    // PDF 페이지 개수 감지 - 실시간 업데이트
                    totalPages = data.totalPages;
                    addLog(`    📄 [${i + 1}/${checkedFiles.length}] ${data.totalPages}개 페이지 감지`);

                    setFileList(prev => {
                      const updated = [...prev];
                      const idx = updated.findIndex(f => f.id === fileItem.id);
                      if (idx !== -1) {
                        updated[idx] = {
                          ...updated[idx],
                          totalPages: data.totalPages,
                          status: 'processing'
                        };
                      }
                      return updated;
                    });
                  } else if (event === 'page-classify') {
                    // 페이지별 분류 결과 - 실시간 업데이트
                    addLog(`    ✅ [${i + 1}/${checkedFiles.length}] 페이지 ${data.pageNumber} 분류: ${data.documentType} 🔥`, 'success');

                    setFileList(prev => {
                      const updated = [...prev];
                      const idx = updated.findIndex(f => f.id === fileItem.id);
                      if (idx !== -1) {
                        const currentPageResults = updated[idx].pageResults || [];
                        const pageIdx = currentPageResults.findIndex(p => p.pageNumber === data.pageNumber);

                        if (pageIdx !== -1) {
                          // 기존 페이지 업데이트
                          currentPageResults[pageIdx] = {
                            ...currentPageResults[pageIdx],
                            documentType: data.documentType
                          };
                        } else {
                          // 새 페이지 추가
                          currentPageResults.push({
                            pageNumber: data.pageNumber,
                            documentType: data.documentType,
                            flashData: null,
                            proData: null
                          });
                        }

                        updated[idx] = {
                          ...updated[idx],
                          pageResults: currentPageResults.sort((a, b) => a.pageNumber - b.pageNumber),
                          status: 'processing'
                        };

                        // 🔧 첫 페이지 분류 완료 시 grid 탭 전환 비활성화
                        // if (data.pageNumber === 1 && activeTab === 'viewer') {
                        //   setActiveTab('grid');
                        // }
                      }
                      return updated;
                    });
                  } else if (event === 'page-extract') {
                    // 페이지별 추출 결과 - 실시간 업데이트
                    // ⭐ 디버그: SSE 파싱 직후 pngImage 확인
                    console.log(`🔍 [SSE Parse] page-extract data.pngImage: ${data.pngImage ? data.pngImage.substring(0, 50) + '...' : 'null/undefined'}, data.ocrImageSize:`, data.ocrImageSize);
                    const isPartial = data.partial === true; // Gemini만 완료된 부분 결과

                    // ⭐ 실제 사용된 모델명 표시 (동적)
                    let modelNames = selectedValidationModels.map(m => getModelNickname(m)).join(' + ');
                    if (!modelNames) modelNames = getModelNickname(validationModel); // 레거시
                    if (data.documentType === '기타') modelNames += ' (실패)';

                    const logMessage = isPartial
                      ? `    ⚡ [${i + 1}/${checkedFiles.length}] 페이지 ${data.pageNumber} 1차 추출 완료 (${modelNames} 처리 중...)`
                      : `    ✅ [${i + 1}/${checkedFiles.length}] 페이지 ${data.pageNumber} 추출 완료 (${modelNames})`;
                    addLog(logMessage, 'success');

                    setFileList(prev => {
                      const updated = [...prev];
                      const idx = updated.findIndex(f => f.id === fileItem.id);
                      if (idx !== -1) {
                        const currentPageResults = updated[idx].pageResults || [];
                        const pageIdx = currentPageResults.findIndex(p => p.pageNumber === data.pageNumber);

                        const pageData = {
                          pageNumber: data.pageNumber,
                          documentType: data.documentType,
                          flashData: data.flashData,
                          proData: data.proData || data.secondData,
                          partial: isPartial,
                          boundingBoxes: data.boundingBoxes || [],
                          paddleRaw: data.paddleRaw,
                          pngImage: data.pngImage,
                          differences: data.differences,
                          ocrImageSize: data.ocrImageSize
                        };

                        if (pageIdx !== -1) {
                          // 기존 페이지 업데이트
                          currentPageResults[pageIdx] = {
                            ...currentPageResults[pageIdx],
                            ...pageData,
                            proData: data.proData || data.secondData || currentPageResults[pageIdx].proData,
                            documentType: data.documentType || currentPageResults[pageIdx].documentType,
                            boundingBoxes: data.boundingBoxes || currentPageResults[pageIdx].boundingBoxes || [],
                            paddleRaw: data.paddleRaw || currentPageResults[pageIdx].paddleRaw,
                            pngImage: data.pngImage || currentPageResults[pageIdx].pngImage,
                            differences: data.differences || currentPageResults[pageIdx].differences,
                            ocrImageSize: data.ocrImageSize || currentPageResults[pageIdx].ocrImageSize
                          };
                        } else {
                          // 새 페이지 추가
                          currentPageResults.push(pageData);
                          // ⭐ 디버그: pngImage 확인
                          console.log('🔍 [SSE Received] pngImage:', data.pngImage ? data.pngImage.substring(0, 50) + '...' : 'null', 'ocrImageSize:', data.ocrImageSize);
                        }

                        // ⭐ SELLER 데이터 로그 확인 (사용자 요청)
                        const sellerBox = pageData.boundingBoxes?.find(b => b.key === 'SELLER');
                        if (sellerBox) {
                          console.log('📡 [SSE:page-extract] Received SELLER:', JSON.stringify(sellerBox));
                        }

                        // 기존 로직: 페이지 결과 임시 저장
                        // ⭐ 핵심 수정: 지역 변수 pageResults에도 저장 (complete 이벤트에서 참조용)
                        const localPageIdx = pageResults.findIndex(p => p.pageNumber === data.pageNumber);
                        if (localPageIdx !== -1) {
                          pageResults[localPageIdx] = pageData;
                        } else {
                          pageResults.push(pageData);
                        }

                        updated[idx] = {
                          ...updated[idx],
                          pageResults: currentPageResults.sort((a, b) => a.pageNumber - b.pageNumber),
                          status: 'extracting'
                        };

                        // 🔥 실시간 화면 업데이트: 첫 번째 페이지 결과가 도착하면 자동으로 선택
                        if (currentPageResults.length === 1 && data.pageNumber === 1) {
                          setSelectedFileIndex(idx);
                          setSelectedPageNumber(1);
                        }
                      }
                      return updated;
                    });

                    // ⭐ 100% 일치 시 자동 검증 처리 (Human-in-the-Loop Bypass)
                    if (data.matchRate === 100) {
                      setValidatedPages(prev => ({
                        ...prev,
                        [fileItem.id]: {
                          ...(prev[fileItem.id] || {}),
                          [data.pageNumber]: true
                        }
                      }));
                      console.log(`✅ [Auto-Validate] 페이지 ${data.pageNumber} 100% 일치로 자동 검증 완료`);
                    }
                  } else if (event === 'classify') {
                    classifyResult = {
                      data: {
                        documentType: data.documentType,
                        documentCode: DOC_TYPE_MAP[data.documentType] || 'U'
                      }
                    };
                    addLog(`    ✅ [${i + 1}/${checkedFiles.length}] 분류 완료: ${data.documentType} (${(data.duration / 1000).toFixed(1)}초)`);

                    // 실시간 업데이트: 분류 결과 반영
                    setFileList(prev => {
                      const updated = [...prev];
                      const idx = updated.findIndex(f => f.id === fileItem.id);
                      if (idx !== -1) {
                        updated[idx] = { ...updated[idx], classifyResult, status: 'processing' };
                      }
                      return updated;
                    });
                  } else if (event === 'flash') {
                    flashResult = { data: data.data };
                    console.log(`[Flash] documentType: ${data.data?.documentType}`, data.data);
                    addLog(`    ⚡ [${i + 1}/${checkedFiles.length}] Flash 추출 완료: ${data.data?.documentType || '타입 없음'}`);

                    // 실시간 업데이트: Flash 결과 반영
                    setFileList(prev => {
                      const updated = [...prev];
                      const idx = updated.findIndex(f => f.id === fileItem.id);
                      if (idx !== -1) {
                        updated[idx] = { ...updated[idx], flashResult, classifyResult, status: 'extracting' };
                      }
                      return updated;
                    });
                  } else if (event === 'pro') {
                    proResult = { data: data.data };
                    console.log(`[Pro] documentType: ${data.data?.documentType}`, data.data);
                    addLog(`    [${i + 1}/${checkedFiles.length}] Pro/Claude 추출 완료: ${data.data?.documentType || '타입 없음'}`);

                    // 실시간 업데이트: Pro 결과 반영
                    setFileList(prev => {
                      const updated = [...prev];
                      const idx = updated.findIndex(f => f.id === fileItem.id);
                      if (idx !== -1) {
                        updated[idx] = { ...updated[idx], proResult, flashResult, classifyResult, status: 'completed' };
                      }
                      return updated;
                    });
                  } else if (event === 'complete') {
                    const fileDuration = Date.now() - fileStartTime;
                    const durationSec = (fileDuration / 1000).toFixed(1);

                    addLog(`  ✅ [${i + 1}/${checkedFiles.length}] ${fileItem.name} 완료 (${durationSec}초)`);

                    // PDF 페이지별 결과 or 이미지 결과
                    if (data.pageResults) {
                      // PDF 페이지별 결과
                      // ⭐ 기존 page-extract에서 저장한 pngImage/ocrImageSize 병합
                      // ⭐ 수정: 지역 변수 pageResults 사용 (React 클로저 문제 해결)
                      const existingPageResults = pageResults || [];
                      console.log('🔍 [Complete] Local pageResults count:', existingPageResults.length, 'first pngImage:', existingPageResults[0]?.pngImage ? 'exists' : 'null');

                      const mergedPageResults = data.pageResults.map(newPage => {
                        const existingPage = existingPageResults.find(p => p.pageNumber === newPage.pageNumber);
                        return {
                          ...newPage,
                          // ⭐ 기존에 저장된 pngImage/ocrImageSize 유지 (complete 이벤트는 이 데이터를 포함하지 않음)
                          pngImage: newPage.pngImage || existingPage?.pngImage,
                          ocrImageSize: newPage.ocrImageSize || existingPage?.ocrImageSize,
                          boundingBoxes: newPage.boundingBoxes || existingPage?.boundingBoxes || [],
                          paddleRaw: newPage.paddleRaw || existingPage?.paddleRaw
                        };
                      });
                      pageResults = mergedPageResults;
                      console.log('🔍 [Complete] Merged pngImage:', mergedPageResults[0]?.pngImage ? mergedPageResults[0].pngImage.substring(0, 50) + '...' : 'null');
                      resolve({
                        fileId: fileItem.id,
                        duration: fileDuration,
                        totalPages: data.totalPages,
                        pageResults: mergedPageResults,
                        uploadedFilePath: data.uploadedFilePath || '', // ⭐ 파일 경로 저장
                        serverOriginalFileName: data.originalFileName || '' // ⭐ 서버에서 받은 원본 파일명
                      });
                    } else {
                      // 이미지 단일 결과
                      resolve({
                        fileId: fileItem.id,
                        duration: fileDuration,
                        classifyResult: classifyResult || {
                          data: {
                            documentType: data.documentType,
                            documentCode: data.documentCode
                          }
                        },
                        flashResult: flashResult || { data: data.flashData },
                        proResult: proResult || { data: data.secondData },
                        uploadedFilePath: data.uploadedFilePath || '', // ⭐ 파일 경로 저장
                        serverOriginalFileName: data.originalFileName || '' // ⭐ 서버에서 받은 원본 파일명
                      });
                    }
                    return;
                  } else if (event === 'error') {
                    throw new Error(data.error || 'Processing failed');
                  }
                }
              }

            } catch (error) {
              const fileDuration = Date.now() - fileStartTime;
              const durationSec = (fileDuration / 1000).toFixed(1);
              addLog(`  ❌ [${i + 1}/${checkedFiles.length}] ${fileItem.name} 실패 (${durationSec}초): ${error.message}`);
              reject(error);
            }
          });
        })
      );

      // 결과 업데이트
      const resultMap = new Map();
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          resultMap.set(result.value.fileId, result.value);
        }
      });

      // 첫 번째 완료된 파일의 ID 찾기
      const firstCompletedFileId = checkedFiles.find(fileItem => resultMap.has(fileItem.id))?.id;
      console.log('🔍 첫 번째 완료 파일 ID:', firstCompletedFileId);
      console.log('🔍 결과 맵:', Array.from(resultMap.entries()));

      setFileList(prev => {
        const updated = [...prev];

        checkedFiles.forEach((fileItem) => {
          const idx = updated.findIndex(f => f.id === fileItem.id);
          const resultData = resultMap.get(fileItem.id);
          if (idx !== -1) {
            if (resultData) {
              // PDF 페이지별 결과가 있는 경우
              if (resultData.pageResults) {
                console.log(`📦 PDF 결과 업데이트: ${fileItem.name}`, {
                  totalPages: resultData.totalPages,
                  pageResultsLength: resultData.pageResults?.length,
                  uploadedFilePath: resultData.uploadedFilePath, // ⭐ 디버깅용 로그
                  serverOriginalFileName: resultData.serverOriginalFileName // ⭐ 서버 원본 파일명
                });
                updated[idx] = {
                  ...updated[idx],
                  totalPages: resultData.totalPages,
                  pageResults: resultData.pageResults,
                  uploadedFilePath: resultData.uploadedFilePath || '', // ⭐ 파일 경로 저장
                  serverOriginalFileName: resultData.serverOriginalFileName || '', // ⭐ 서버 원본 파일명 저장
                  status: 'completed'
                };
              } else {
                // 이미지 단일 결과
                // Flash와 Pro 데이터 비교하여 일치율 계산
                const flashData = resultData.flashResult?.data || {};
                const proData = resultData.proResult?.data || {};

                // 공통 필드 찾기
                const allKeys = new Set([...Object.keys(flashData), ...Object.keys(proData)]);
                let matchedFields = 0;
                let totalFields = 0;

                allKeys.forEach(key => {
                  if (key === 'metadata' || key === 'timestamp' || key === 'source') return;
                  totalFields++;
                  if (flashData[key] === proData[key]) {
                    matchedFields++;
                  }
                });

                const matchRate = totalFields > 0 ? Math.round((matchedFields / totalFields) * 100) : 100;

                // Pro 결과를 기본 extractedData로 설정 (더 정확함)
                const extractedData = {
                  ...proData,
                  metadata: {
                    ...proData.metadata,
                    matchRate: matchRate,
                    flashData: flashData,
                    proData: proData,
                    autoValidated: true,
                    validatedAt: new Date().toISOString()
                  }
                };

                updated[idx] = {
                  ...updated[idx],
                  classifyResult: resultData.classifyResult,
                  flashResult: resultData.flashResult,
                  proResult: resultData.proResult,
                  extractedData: extractedData, // 자동으로 extractedData 설정
                  uploadedFilePath: resultData.uploadedFilePath || '', // ⭐ 파일 경로 저장
                  serverOriginalFileName: resultData.serverOriginalFileName || '', // ⭐ 서버 원본 파일명 저장
                  status: 'completed'
                };

                // ⭐ 100% 일치 시 자동 검증 처리 (Human-in-the-Loop Bypass)
                if (matchRate === 100) {
                  setValidatedPages(prev => ({
                    ...prev,
                    [fileItem.id]: {
                      ...(prev[fileItem.id] || {}),
                      1: true // 단일 이미지는 1페이지로 간주
                    }
                  }));
                  console.log(`✅ [Auto-Validate] 단일 파일 100% 일치로 자동 검증 완료`);
                }
              }
            } else {
              // 처리 실패한 파일은 에러 상태로 설정
              updated[idx] = {
                ...updated[idx],
                status: 'error'
              };
            }
          }
        });

        // 🔧 첫 번째 완료된 파일을 자동 선택 (업데이트된 리스트 사용)
        if (firstCompletedFileId) {
          console.log('🎯 자동 선택 로직 시작, firstCompletedFileId:', firstCompletedFileId);
          setTimeout(() => {
            const idx = updated.findIndex(f => f.id === firstCompletedFileId);
            console.log('🔍 찾은 인덱스:', idx);
            if (idx !== -1) {
              console.log(`📌 첫 번째 파일 자동 선택: index=${idx}, id=${firstCompletedFileId}, name=${updated[idx].name}`);
              console.log(`📋 선택된 파일 데이터:`, {
                classifyResult: updated[idx].classifyResult,
                flashResult: updated[idx].flashResult,
                proResult: updated[idx].proResult,
                pageResults: updated[idx].pageResults,
                totalPages: updated[idx].totalPages,
                hasPageResults: !!updated[idx].pageResults,
                pageResultsLength: updated[idx].pageResults?.length
              });

              setSelectedFileIndex(idx);

              // PDF 파일인 경우 페이지 체크 상태 명시적으로 초기화
              console.log('🔍 PDF 체크 조건:', {
                hasPageResults: !!updated[idx].pageResults,
                pageResultsLength: updated[idx].pageResults?.length,
                willInitialize: !!(updated[idx].pageResults && updated[idx].pageResults.length > 0)
              });

              if (updated[idx].pageResults && updated[idx].pageResults.length > 0) {
                const allPageNumbers = updated[idx].pageResults.map(p => p.pageNumber);
                console.log(`📄 PDF 페이지 자동 선택 초기화:`, allPageNumbers);
                setCheckedPages(allPageNumbers);
                setSelectedPageNumber(allPageNumbers[0] || 1);
                lastClickedPageRef.current = allPageNumbers[0] || 1;
              } else {
                console.warn('⚠️ pageResults가 없거나 비어있어서 페이지 초기화 스킵');
              }

              addLog(`📌 첫 번째 파일 자동 선택됨 (${updated[idx].name})`);
            } else {
              console.warn('⚠️ 파일을 찾을 수 없음, firstCompletedFileId:', firstCompletedFileId);
            }
          }, 100);
        } else {
          console.warn('⚠️ firstCompletedFileId가 없어서 자동 선택 스킵');
        }

        return updated;
      });

      const successCount = results.filter(r => r.status === 'fulfilled').length;

      // 처리 시간 통계
      const successResults = results.filter(r => r.status === 'fulfilled' && r.value);
      const durations = successResults.map(r => r.value.duration).filter(d => d);
      const totalDuration = durations.reduce((sum, d) => sum + d, 0);
      const avgDuration = durations.length > 0 ? totalDuration / durations.length : 0;
      const avgSec = (avgDuration / 1000).toFixed(1);

      addLog(`전체 처리 완료! (${successCount}/${checkedFiles.length}) - 평균 ${avgSec}초/건`);
      console.log('[일괄 처리] 전체 완료!');
    } catch (error) {
      // 🚀 취소에 의한 AbortError는 정상 흐름이므로 오류 처리하지 않음
      if (error.name === 'AbortError') {
        console.log('[일괄 처리] 사용자에 의해 취소됨');
        return;
      }
      console.error('[일괄 처리] 오류 발생:', error);
      addLog(`오류 발생: ${error.message}`);
      alert('일괄 처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
      setProcessingCount(0);
      abortControllerRef.current = null; // 🚀 AbortController 정리
    }
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 관리자 모드 핸들러 함수들
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  // 관리자: Step 1 - 분류
  const handleAdminClassify = async () => {
    if (selectedFileIndex === null) return;

    const file = fileList[selectedFileIndex];
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('flashModelVersion', classificationFlashModel);
      formData.append('image', file.file);

      const response = await fetch(`${API_BASE_URL}/api/admin/classify`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('분류 실패');
      }

      const result = await response.json();

      // 파일 목록 업데이트 - classifyResult 저장
      setFileList(prev => prev.map((f, idx) =>
        idx === selectedFileIndex
          ? { ...f, classifyResult: result, status: '분류 완료' }
          : f
      ));

    } catch (err) {
      console.error('분류 에러:', err);
      setError(err.message);
      setFileList(prev => prev.map((f, idx) =>
        idx === selectedFileIndex
          ? { ...f, status: 'error', error: err.message }
          : f
      ));
    } finally {
      setLoading(false);
    }
  };

  // 관리자: Step 2-1 - Flash 추출
  const handleAdminExtractFlash = async () => {
    if (selectedFileIndex === null) return;

    const file = fileList[selectedFileIndex];
    if (!file || !file.classifyResult) {
      setError('먼저 분류를 실행해주세요');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('image', file.file);
      formData.append('documentType', file.classifyResult.data.docCode);

      const response = await fetch(`${API_BASE_URL}/api/admin/extract-flash`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Flash 추출 실패');
      }

      const result = await response.json();

      // 파일 목록 업데이트 - flashResult 저장
      setFileList(prev => prev.map((f, idx) =>
        idx === selectedFileIndex
          ? { ...f, flashResult: result, status: 'Flash 추출 완료' }
          : f
      ));

    } catch (err) {
      console.error('Flash 추출 에러:', err);
      setError(err.message);
      setFileList(prev => prev.map((f, idx) =>
        idx === selectedFileIndex
          ? { ...f, status: 'error', error: err.message }
          : f
      ));
    } finally {
      setLoading(false);
    }
  };

  // 관리자: Step 2-2 - Pro 추출
  const handleAdminExtractPro = async () => {
    if (selectedFileIndex === null) return;

    const file = fileList[selectedFileIndex];
    if (!file || !file.classifyResult) {
      setError('먼저 분류를 실행해주세요');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('image', file.file);
      formData.append('documentType', file.classifyResult.data.docCode);

      const response = await fetch(`${API_BASE_URL}/api/admin/extract-pro`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Pro 추출 실패');
      }

      const result = await response.json();

      // 파일 목록 업데이트 - proResult 저장
      setFileList(prev => prev.map((f, idx) =>
        idx === selectedFileIndex
          ? { ...f, proResult: result, status: 'Pro 추출 완료' }
          : f
      ));

    } catch (err) {
      console.error('Pro 추출 에러:', err);
      setError(err.message);
      setFileList(prev => prev.map((f, idx) =>
        idx === selectedFileIndex
          ? { ...f, status: 'error', error: err.message }
          : f
      ));
    } finally {
      setLoading(false);
    }
  };

  // 관리자: Step 3 - 검증 (Flash vs Pro 비교)
  const handleAdminValidate = async () => {
    if (selectedFileIndex === null) return;

    const file = fileList[selectedFileIndex];
    if (!file || !file.flashResult || !file.proResult) {
      setError('먼저 Flash와 Pro 추출을 완료해주세요');
      return;
    }

    // 클라이언트에서 간단한 비교 수행
    const flashData = file.flashResult.data;
    const proData = file.proResult.data;

    const fields = Object.keys(flashData);
    let matchCount = 0;
    const differences = {};

    fields.forEach(field => {
      const flashValue = String(flashData[field] || '').trim();
      const proValue = String(proData[field] || '').trim();

      if (flashValue === proValue) {
        matchCount++;
      } else {
        differences[field] = {
          flash: flashValue,
          pro: proValue
        };
      }
    });

    const matchRate = Math.round((matchCount / fields.length) * 100);

    const validatedData = {
      ...proData,
      metadata: {
        validated: true,
        matchRate,
        matchDetails: {
          matchCount,
          totalFields: fields.length
        }
      },
      differences
    };

    // extractedData 업데이트
    setFileList(prev => prev.map((f, idx) =>
      idx === selectedFileIndex
        ? {
          ...f,
          extractedData: validatedData,
          status: matchRate === 100 ? 'completed' : 'validated'
        }
        : f
    ));
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Prompt Playground 실행 핸들러
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleReExtract = async (customPrompt, model) => {
    if (selectedFileIndex === null) return;
    const file = fileList[selectedFileIndex];
    if (!file) return;

    setPlaygroundProcessing(true);

    try {
      const formData = new FormData();
      // ⭐ 필드 먼저 전송
      formData.append('classificationFlashModel', classificationFlashModel);
      formData.append('validationModel', validationModel);
      formData.append('image', file.file); // 'deepseek' or 'gemini-...'

      // 모델에 따라 파라미터 설정
      if (model === 'deepseek') {
        formData.append('deepseekMode', deepseekMode);
      } else {
        // Gemini 모델인 경우 selectedValidationModels에 추가하여 전달
        formData.append('selectedValidationModels', JSON.stringify([model]));
      }

      formData.append('grounding', grounding);
      formData.append('datalabBoundingBox', datalabBoundingBox);
      formData.append('enableHybridOCR', enableHybridOCR); // ⭐ Hybrid Mode Flag

      // SSE 연결
      const response = await fetch(`${API_BASE_URL}/api/ocr/extract-stream`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Extraction failed');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');

        // 마지막 청크가 불완전할 수 있으므로 buffer에 남김
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            const eventType = line.substring(7).trim();
            // data: 부분 찾기 (줄바꿈으로 분리된 다음 줄)
            const dataLineIndex = line.indexOf('\ndata: ');
            let dataLine = '';

            // event: ... \n data: ... 형식인 경우
            if (dataLineIndex !== -1) {
              dataLine = line.substring(dataLineIndex + 1);
            } else {
              // 다음 줄에 data: 가 있는 경우 (이 루프에서는 처리하기 어려움, 위 split 로직상 한 덩어리로 들어옴)
              // split('\n\n')을 했으므로 event: ...\ndata: ... 가 하나의 line임
              const parts = line.split('\ndata: ');
              if (parts.length >= 2) {
                dataLine = 'data: ' + parts[1];
              }
            }

            if (dataLine && dataLine.startsWith('data: ')) {
              try {
                const data = JSON.parse(dataLine.substring(6));

                if (eventType === 'page-extract') {
                  // 결과 업데이트
                  setFileList(prev => prev.map((f, idx) => {
                    if (idx !== selectedFileIndex) return f;

                    // 페이지 결과 업데이트
                    const newPageResults = [...(f.pageResults || [])];
                    const pageIndex = data.pageNumber - 1;

                    // 배열 크기 확보
                    while (newPageResults.length <= pageIndex) {
                      newPageResults.push({});
                    }

                    newPageResults[pageIndex] = {
                      ...(newPageResults[pageIndex] || {}),
                      pageNumber: data.pageNumber,
                      documentType: data.documentType,
                      flashData: data.flashData,
                      proData: data.proData,
                      extractedData: data.flashData, // Playground 결과는 즉시 반영
                      boundingBoxes: data.boundingBoxes,
                      pngImage: data.pngImage || newPageResults[pageIndex]?.pngImage
                    };

                    return {
                      ...f,
                      pageResults: newPageResults,
                      // 단일 페이지인 경우 메인 결과도 업데이트
                      flashResult: { data: data.flashData },
                      proResult: { data: data.proData },
                      extractedData: data.flashData,
                      status: 'processing'
                    };
                  }));
                } else if (eventType === 'complete') {
                  setFileList(prev => prev.map((f, idx) =>
                    idx === selectedFileIndex ? { ...f, status: 'completed' } : f
                  ));
                }
              } catch (e) {
                console.error('SSE Parse Error:', e);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Re-extraction failed:', error);
      alert(`실행 실패: ${error.message}`);
    } finally {
      setPlaygroundProcessing(false);
    }
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  // 1차 추출 실행 (옵션5: 분류 + Flash + Pro 병렬)
  // 차량 정보 데이터베이스 저장
  // 검증 관련 함수들
  const handleValidatePage = async (fileId, pageNumber, editedData = null) => {
    // ⭐ editedData가 있으면 수정된 데이터로 업데이트
    if (editedData) {
      console.log(`📝 페이지 ${pageNumber} 수정된 데이터로 검증:`, editedData);

      // 파일 리스트에서 해당 파일 찾기
      const fileIndex = fileList.findIndex(f => f.id === fileId);
      if (fileIndex !== -1) {
        const file = fileList[fileIndex];

        // 페이지별 결과 업데이트
        if (file.pageResults && file.pageResults.length > 0) {
          const pageIndex = pageNumber - 1;
          if (file.pageResults[pageIndex]) {
            const pageResult = file.pageResults[pageIndex];
            // ⭐ 기존 데이터 우선순위: extractedData > proData(추출2) > flashData(추출1)
            // extractedData가 없으면 추출2 모델(proData)을 기본값으로 사용
            const baseData = pageResult.extractedData || pageResult.proData || pageResult.flashData || {};

            // 수정된 데이터를 extractedData에 반영 (검증 후 최종 데이터)
            // ⭐ 사용자가 수정한 데이터가 최우선, 없으면 추출2 모델 값 사용
            let finalData = {
              ...baseData,
              ...editedData // ⭐ 사용자가 수정한 데이터가 최우선
            };

            // ⭐ 1. 주소 검증 가중치 저장
            if (finalData.address) {
              const ocrAddress = pageResult.flashData?.address || pageResult.proData?.address || finalData.address;
              const verifiedAddress = finalData.address;

              // OCR 인식 주소와 검증 주소가 다른 경우에만 저장
              if (ocrAddress !== verifiedAddress && ocrAddress) {
                try {
                  await fetch(`${API_BASE_URL}/api/address/validate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      ocrRecognized: ocrAddress,
                      verifiedAddress: verifiedAddress,
                      userId: 'system' // TODO: 실제 사용자 ID로 변경
                    })
                  });
                  console.log(`✅ 주소 검증 가중치 저장: ${ocrAddress} → ${verifiedAddress}`);
                } catch (err) {
                  console.error('❌ 주소 검증 가중치 저장 실패:', err);
                }
              }
            }

            // ⭐ 2. 차량 기초자료 조회 및 필드 채우기 (차량명이 있으면 항상 조회)
            if (finalData.model) {
              try {
                const carName = finalData.model; // 예: '쏘나타', '쏘나타(sonata)'
                const response = await fetch(`${API_BASE_URL}/api/vehicle-master/search/${encodeURIComponent(carName)}`);

                if (response.ok) {
                  const result = await response.json();

                  if (result.success && result.data) {
                    const masterData = result.data;
                    const filledFields = [];

                    // ⭐ 차량명을 항상 영문명으로 변경 (값이 다 채워져 있어도) - DB 컬럼명 사용
                    if (masterData.BASE_CAR_NM_ENG) {
                      finalData.model = masterData.BASE_CAR_NM_ENG.toUpperCase(); // 예: 'SONATA'
                      filledFields.push('차량명');
                    }

                    // 누락된 필드만 채우기 (중량이 이미 있으면 패스) - DB 컬럼명 사용
                    if (!finalData.brand && masterData.BASE_CAR_MAKER) {
                      finalData.brand = masterData.BASE_CAR_MAKER;
                      filledFields.push('브랜드');
                    }

                    if (!finalData.modelYear && masterData.BASE_CAR_YEAR) {
                      finalData.modelYear = masterData.BASE_CAR_YEAR;
                      filledFields.push('연식');
                    }

                    if (!finalData.displacement && masterData.BASE_CAR_CC) {
                      finalData.displacement = masterData.BASE_CAR_CC.toString();
                      filledFields.push('배기량');
                    }

                    // 중량이 없을 때만 채우기
                    if (!finalData.totalWeight && masterData.BASE_CAR_WEIGHT) {
                      finalData.totalWeight = masterData.BASE_CAR_WEIGHT.toString();
                      filledFields.push('중량');
                    }

                    if (!finalData.cbm && masterData.BASE_CAR_CBM) {
                      finalData.cbm = masterData.BASE_CAR_CBM.toString();
                      filledFields.push('CBM');
                    }

                    // ⭐ 값이 다 채워져 있어도 기초자료에 추가할 수 있도록 메타데이터 추가
                    finalData.metadata = finalData.metadata || {};
                    if (filledFields.length > 0) {
                      console.log(`✅ 기초자료에서 가져온 필드: ${filledFields.join(', ')}`);
                      finalData.metadata.filledFromMaster = filledFields;
                    }
                    // 기초자료에 있는 차량이므로 추가 옵션 제공
                    finalData.metadata.masterDataFound = true;
                    finalData.metadata.masterData = masterData; // 기초자료 데이터 저장
                  } else {
                    // 차량명이 기초자료에 없으면 사용자에게 추가 옵션 제공
                    console.log(`⚠️ 차량 기초자료에 없음: ${carName}`);
                    finalData.metadata = finalData.metadata || {};
                    finalData.metadata.masterDataNotFound = true;
                    finalData.metadata.carName = carName;
                  }
                }
              } catch (err) {
                console.error('❌ 차량 기초자료 조회 실패:', err);
              }
            }

            const updatedPageResults = [...file.pageResults];
            updatedPageResults[pageIndex] = {
              ...pageResult,
              extractedData: finalData
            };

            console.log(`📝 검증 데이터 업데이트:`, {
              pageNumber,
              baseData: baseData.plateNumber,
              editedData: editedData.plateNumber,
              finalData: finalData.plateNumber
            });

            // ⭐ 검증 완료된 final_data를 DB에 저장 (UCAR_OCR_RESULT 테이블)
            try {
              const ocrResultData = {
                fileName: file.name,
                fileType: file.isPDF ? 'PDF' : 'IMAGE',
                documentType: finalData.documentType || pageResult.documentType,
                flashData: pageResult.flashData || {},
                flashModel: 'gemini-2.5-flash',
                flashProcessingTime: pageResult.flashData?.metadata?.processingTime || null,
                proData: pageResult.proData || {},
                proModel: pageResult.proData?.metadata?.model || 'gemini-2.5-pro',
                proProcessingTime: pageResult.proData?.metadata?.processingTime || null,
                finalData: finalData, // ⭐ 최종 승인 데이터
                hasDifferences: false, // 검증 완료 시 차이 없음
                differenceCount: 0,
                differenceFields: [],
                userId: 'system', // TODO: 실제 사용자 ID로 변경
                groupId: '0001' // TODO: 실제 그룹 ID로 변경
              };

              const saveResponse = await fetch(`${API_BASE_URL}/api/ocr-result/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(ocrResultData)
              });

              if (saveResponse.ok) {
                const saveResult = await saveResponse.json();
                console.log(`✅ 검증 결과 DB 저장 완료: ocrId=${saveResult.ocrId}`);

                // 저장된 ocrId를 pageResult에 추가
                updatedPageResults[pageIndex].ocrId = saveResult.ocrId;
              } else {
                console.warn('⚠️ 검증 결과 DB 저장 실패 (계속 진행):', await saveResponse.text());
              }
            } catch (saveError) {
              console.error('❌ 검증 결과 DB 저장 오류 (계속 진행):', saveError);
              // DB 저장 실패해도 프론트엔드 state는 업데이트
            }

            setFileList(prev => prev.map((f, idx) =>
              idx === fileIndex
                ? { ...f, pageResults: updatedPageResults }
                : f
            ));
          }
        } else if (file.extractedData) {
          // 단일 이미지인 경우
          let finalData = { ...file.extractedData, ...editedData };

          // ⭐ 1. 주소 검증 가중치 저장
          if (finalData.address) {
            const ocrAddress = file.flashResult?.data?.address || file.proResult?.data?.address || finalData.address;
            const verifiedAddress = finalData.address;

            // OCR 인식 주소와 검증 주소가 다른 경우에만 저장
            if (ocrAddress !== verifiedAddress && ocrAddress) {
              try {
                await fetch(`${API_BASE_URL}/api/address/validate`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    ocrRecognized: ocrAddress,
                    verifiedAddress: verifiedAddress,
                    userId: 'system' // TODO: 실제 사용자 ID로 변경
                  })
                });
                console.log(`✅ 주소 검증 가중치 저장: ${ocrAddress} → ${verifiedAddress}`);
              } catch (err) {
                console.error('❌ 주소 검증 가중치 저장 실패:', err);
              }
            }
          }

          // ⭐ 2. 차량 기초자료 조회 및 필드 채우기 (차량명이 있으면 항상 조회)
          if (finalData.model) {
            try {
              const carName = finalData.model; // 예: '쏘나타', '쏘나타(sonata)'
              const response = await fetch(`${API_BASE_URL}/api/vehicle-master/search/${encodeURIComponent(carName)}`);

              if (response.ok) {
                const result = await response.json();

                if (result.success && result.data) {
                  const masterData = result.data;
                  const filledFields = [];

                  // ⭐ 차량명을 항상 영문명으로 변경 (값이 다 채워져 있어도) - DB 컬럼명 사용
                  if (masterData.BASE_CAR_NM_ENG) {
                    finalData.model = masterData.BASE_CAR_NM_ENG.toUpperCase(); // 예: 'SONATA'
                    filledFields.push('차량명');
                  }

                  // 누락된 필드만 채우기 (중량이 이미 있으면 패스) - DB 컬럼명 사용
                  if (!finalData.brand && masterData.BASE_CAR_MAKER) {
                    finalData.brand = masterData.BASE_CAR_MAKER;
                    filledFields.push('브랜드');
                  }

                  if (!finalData.modelYear && masterData.BASE_CAR_YEAR) {
                    finalData.modelYear = masterData.BASE_CAR_YEAR;
                    filledFields.push('연식');
                  }

                  if (!finalData.displacement && masterData.BASE_CAR_CC) {
                    finalData.displacement = masterData.BASE_CAR_CC.toString();
                    filledFields.push('배기량');
                  }

                  // 중량이 없을 때만 채우기
                  if (!finalData.totalWeight && masterData.BASE_CAR_WEIGHT) {
                    finalData.totalWeight = masterData.BASE_CAR_WEIGHT.toString();
                    filledFields.push('중량');
                  }

                  if (!finalData.cbm && masterData.BASE_CAR_CBM) {
                    finalData.cbm = masterData.BASE_CAR_CBM.toString();
                    filledFields.push('CBM');
                  }

                  // ⭐ 값이 다 채워져 있어도 기초자료에 추가할 수 있도록 메타데이터 추가
                  finalData.metadata = finalData.metadata || {};
                  if (filledFields.length > 0) {
                    console.log(`✅ 기초자료에서 가져온 필드: ${filledFields.join(', ')}`);
                    finalData.metadata.filledFromMaster = filledFields;
                  }
                  // 기초자료에 있는 차량이므로 추가 옵션 제공
                  finalData.metadata.masterDataFound = true;
                  finalData.metadata.masterData = masterData; // 기초자료 데이터 저장
                } else {
                  // 차량명이 기초자료에 없으면 사용자에게 추가 옵션 제공
                  console.log(`⚠️ 차량 기초자료에 없음: ${carName}`);
                  finalData.metadata = finalData.metadata || {};
                  finalData.metadata.masterDataNotFound = true;
                  finalData.metadata.carName = carName;
                }
              }
            } catch (err) {
              console.error('❌ 차량 기초자료 조회 실패:', err);
            }
          }

          // ⭐ 검증 완료된 final_data를 DB에 저장
          try {
            const ocrResultData = {
              fileName: file.name,
              fileType: 'IMAGE',
              documentType: finalData.documentType,
              flashData: file.flashResult?.data || {},
              flashModel: 'gemini-2.5-flash',
              flashProcessingTime: file.flashResult?.responseTime || null,
              proData: file.proResult?.data || {},
              proModel: file.proResult?.model || 'gemini-2.5-pro',
              proProcessingTime: file.proResult?.responseTime || null,
              finalData: finalData,
              hasDifferences: false,
              differenceCount: 0,
              differenceFields: [],
              userId: 'system',
              groupId: '0001'
            };

            const saveResponse = await fetch(`${API_BASE_URL}/api/ocr-result/save`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(ocrResultData)
            });

            if (saveResponse.ok) {
              const saveResult = await saveResponse.json();
              console.log(`✅ 검증 결과 DB 저장 완료: ocrId=${saveResult.ocrId}`);
            }
          } catch (saveError) {
            console.error('❌ 검증 결과 DB 저장 오류:', saveError);
          }

          setFileList(prev => prev.map((f, idx) =>
            idx === fileIndex
              ? {
                ...f,
                extractedData: finalData,
                proResult: f.proResult ? { ...f.proResult, data: { ...f.proResult.data, ...editedData } } : f.proResult
              }
              : f
          ));
        }
      }
    }

    setValidatedPages(prev => {
      const updated = {
        ...prev,
        [fileId]: {
          ...(prev[fileId] || {}),
          [pageNumber]: true
        }
      };

      // ⭐ 모든 페이지 검증 완료 체크
      const file = fileList.find(f => f.id === fileId);
      if (file && file.pageResults && file.pageResults.length > 0) {
        const totalPages = file.pageResults.length;
        const validatedCount = Object.keys(updated[fileId] || {}).filter(
          pageNum => updated[fileId][pageNum] === true
        ).length;

        if (validatedCount === totalPages) {
          // 모든 페이지 검증 완료
          setTimeout(() => {
            const confirmed = window.confirm(
              `✅ 모든 페이지(${totalPages}건) 검증이 완료되었습니다.\n\n` +
              `검증 결과 화면으로 이동하시겠습니까?`
            );
            if (confirmed) {
              setActiveTab('mapping'); // 차량등록매핑 탭으로 이동
            }
          }, 100);
        }
      }

      return updated;
    });
    console.log(`✅ 페이지 ${pageNumber} 검증 완료 (파일 ID: ${fileId})`);
  };

  const handleValidateMultiplePages = (fileId, pageNumbers) => {
    setValidatedPages(prev => {
      const fileValidations = { ...(prev[fileId] || {}) };
      pageNumbers.forEach(pageNum => {
        fileValidations[pageNum] = true;
      });
      return {
        ...prev,
        [fileId]: fileValidations
      };
    });
    console.log(`✅ ${pageNumbers.length}개 페이지 일괄 검증 완료 (파일 ID: ${fileId})`);
  };

  const handleValidateAllPages = async (fileId, totalPages, editedData = null) => {
    // ⭐ 전체 검증 시에도 editedData를 첫 번째 페이지에 적용
    if (editedData) {
      await handleValidatePage(fileId, 1, editedData);
    }

    // 모든 페이지에 대해 검증 처리
    const file = fileList.find(f => f.id === fileId);
    if (file && file.pageResults) {
      for (let i = 0; i < file.pageResults.length; i++) {
        const pageResult = file.pageResults[i];
        const pageNumber = pageResult.pageNumber || (i + 1);

        // 각 페이지의 extractedData로 검증 처리 (아직 검증 안 된 경우만)
        if (pageResult.extractedData && !validatedPages[fileId]?.[pageNumber]) {
          await handleValidatePage(fileId, pageNumber, pageResult.extractedData);
        }
      }
    }

    const allPageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
    handleValidateMultiplePages(fileId, allPageNumbers);
    console.log(`✅ 전체 ${totalPages}개 페이지 검증 완료 (파일 ID: ${fileId})`);

    // 전체검증 그리드 표시
    setShowBatchValidationGrid(true);
  };

  const isPageValidated = (fileId, pageNumber) => {
    return validatedPages[fileId]?.[pageNumber] === true;
  };

  const handleSaveVehicle = async (vehicleData, pageNumber, groupCode, userId) => {
    try {
      console.log('📝 차량 정보 저장 시작:', { vehicleData, pageNumber, groupCode, userId });

      // 현재 파일 정보 가져오기
      const currentFile = selectedFileIndex !== null ? fileList[selectedFileIndex] : null;
      const currentPage = currentFile?.pages?.[pageNumber - 1];

      // OCR 원본 결과 및 프롬프트 정보 수집
      const ocrRawResult = currentPage?.extractedData || currentPage?.flashResult || currentPage?.proResult;
      const documentType = currentPage?.classifyResult?.documentType || 'unknown';

      // 사용된 프롬프트 결정 (실제 사용한 모델에 따라)
      let ocrPrompt = '';
      if (currentPage?.proResult) {
        ocrPrompt = '차량등록원부 OCR 추출 (Gemini 2.5 Pro)';
      } else if (currentPage?.flashResult) {
        ocrPrompt = '차량등록원부 OCR 추출 (Gemini 1.5 Flash)';
      }

      const response = await fetch(`${API_BASE_URL}/api/vehicle/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vehicleData,
          ocrRawResult,
          userId: userId || 'jmkim',
          groupId: groupCode || '0001',
          documentType,
          pdfFilePath: currentFile?.path,
          ocrPrompt
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '차량 저장 실패');
      }

      const result = await response.json();

      console.log('✅ 차량 저장 성공:', result);
      alert(`차량 정보가 성공적으로 저장되었습니다!\n차량 키: ${result.carKey}\n검증 이력 ID: ${result.validationHistoryId || 'N/A'}`);

      // 현재 파일의 상태 업데이트
      if (selectedFileIndex !== null) {
        setFileList(fileList.map((f, idx) =>
          idx === selectedFileIndex
            ? { ...f, savedCarKey: result.carKey, savedAt: new Date().toISOString() }
            : f
        ));
      }

      return result;

    } catch (error) {
      console.error('❌ 차량 저장 실패:', error);
      throw error;
    }
  };

  const handleExtractSelected = async () => {
    if (selectedFileIndex === null) return;

    setLoading(true);
    setError(null);

    const selectedFile = fileList[selectedFileIndex];

    // 상태 업데이트: processing
    setFileList(prev => prev.map((f, idx) =>
      idx === selectedFileIndex ? { ...f, status: 'extracting' } : f
    ));

    try {
      // 옵션5: 전체 처리 (분류 1초 + Flash+Pro 병렬 3-4초 = 총 5-6초)
      const extractedData = await fullExtract(selectedFile.file);

      // 상태 업데이트: completed (전체 완료)
      setFileList(prev => prev.map((f, idx) =>
        idx === selectedFileIndex
          ? { ...f, status: 'completed', extractedData: extractedData, error: null }
          : f
      ));

    } catch (err) {
      setError(err.message);

      // 상태 업데이트: error
      setFileList(prev => prev.map((f, idx) =>
        idx === selectedFileIndex
          ? { ...f, status: 'error', error: err.message }
          : f
      ));

    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFile = (index) => {
    setFileList(prev => {
      const newList = prev.filter((_, idx) => idx !== index);

      // 선택 인덱스 조정
      if (selectedFileIndex === index) {
        setSelectedFileIndex(newList.length > 0 ? 0 : null);
      } else if (selectedFileIndex > index) {
        setSelectedFileIndex(selectedFileIndex - 1);
      }

      return newList;
    });
  };

  // 전체 초기화 (파일은 유지, 추출 결과만 클리어)
  const handleReset = () => {
    if (fileList.length === 0 && processingLogs.length === 0) {
      return; // 이미 초기화된 상태
    }

    const confirmed = window.confirm('추출된 결과를 초기화하시겠습니까?\n(업로드한 파일은 유지됩니다)');
    if (confirmed) {
      // 파일 리스트는 유지하되, 추출 결과만 초기화
      setFileList(prev => prev.map(file => ({
        ...file,
        extractedData: null,
        status: 'pending',
        error: null,
        pageResults: null
      })));

      setProcessingLogs([]);
      setCheckedPages([]);
      setSelectedPageNumber(1);
      addLog('🔄 추출 결과 초기화 완료 (파일은 유지됨)', 'info');
    }
  };

  // 검증 모델 토글 핸들러 (최대 2개까지 선택 가능)
  const toggleValidationModel = (modelId) => {
    setSelectedValidationModels(prev => {
      if (prev.includes(modelId)) {
        // 이미 선택된 경우 - 제거 (단, 최소 1개는 유지)
        if (prev.length > 1) {
          return prev.filter(m => m !== modelId);
        } else {
          alert('최소 1개의 검증 모델을 선택해야 합니다.');
          return prev;
        }
      } else {
        // 선택되지 않은 경우 - 추가 (최대 3개까지)
        if (prev.length < 3) {
          return [...prev, modelId];
        } else {
          alert('최대 3개의 검증 모델만 선택할 수 있습니다.');
          return prev;
        }
      }
    });
  };

  // 검증 모델 이름 가져오기 (닉네임으로 표시)
  const getModelDisplayName = (modelId) => {
    // ⭐ UI에는 닉네임 표시 (개발자만 알 수 있도록)
    return getModelNickname(modelId) || modelId;
  };

  const handleFieldChange = (field, value) => {
    if (selectedFileIndex === null) return;

    setFileList(prev => prev.map((f, idx) =>
      idx === selectedFileIndex && f.extractedData
        ? {
          ...f,
          extractedData: {
            ...f.extractedData,
            [field]: value || ''
          }
        }
        : f
    ));
  };

  // 필드 값 토글 (Flash <-> Pro)
  const toggleFieldValue = (field) => {
    if (!extractedData?.differences?.[field]) return;

    const diff = extractedData.differences[field];
    const currentValue = extractedData[field] || '';

    // 현재 값이 Flash 값이면 Pro로, Pro 값이면 Flash로
    const newValue = currentValue === (diff.flash || '') ? (diff.pro || '') : (diff.flash || '');
    handleFieldChange(field, newValue);
  };

  const handleConfirm = async () => {
    if (selectedFileIndex === null) return;

    const selectedFile = fileList[selectedFileIndex];
    const extractedData = selectedFile.extractedData;

    if (!extractedData) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/ocr/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          finalData: extractedData,
          originalImageId: extractedData.metadata.processedAt
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('✅ UPS2LOGIN 시스템에 입력되었습니다!');

        // 완료된 파일 제거
        handleRemoveFile(selectedFileIndex);
      } else {
        alert('❌ 저장 실패: ' + result.error);
      }
    } catch (err) {
      alert('❌ 시스템 오류: ' + err.message);
    }
  };

  // 필드 렌더링 (차이 표시 포함 - Human-in-the-Loop)
  const renderField = (label, field, type = 'text') => {
    // ⭐ 숫자 필드는 0도 유효한 값이므로 || '' 사용하지 않음
    const numericFields = ['mileage', 'displacement', 'totalWeight', 'length', 'width', 'height', 'cbm', 'modelYear'];
    const value = numericFields.includes(field)
      ? (extractedData[field] !== null && extractedData[field] !== undefined ? extractedData[field] : '')
      : (extractedData[field] || '');
    const docType = extractedData?.documentType;

    // ⭐ 부분 결과 플래그 확인 (Gemini만 완료, DeepSeek 대기 중)
    const isPartialResult = extractedData?.partial === true;

    // 프롬프트에 정의된 필드인지 확인 (DOCUMENT_FIELDS_MAP 기준)
    const fieldApplicable = isFieldApplicable(docType, field);

    // Flash vs Final 차이 정보 (백엔드에서 전달받음)
    const diff = extractedData?.differences?.[field];
    if (field === 'mileage' || field === 'displacement') { // ⭐ 디버깅용 로그 (주행거리, 배기량)
      console.log(`[renderField] ${field}:`, {
        value,
        diff,
        extractedDataDifferences: extractedData?.differences,
        isPartialResult
      });
    }
    const hasDifference = !!diff;

    // 미인식 판단: Flash나 Pro 중 하나라도 값이 있었는데 최종 결과 없음
    const flashValue = diff?.flash;
    const proValue = diff?.pro;
    const hadAnyValue = flashValue || proValue;
    const isUnrecognized = fieldApplicable && !value && hadAnyValue;

    // ✅ Flash vs Final 비교 박스 표시 조건:
    // 1. 차이가 있거나 (match === false)
    // 2. Flash 또는 Pro 중 하나라도 값이 있는 경우
    // 3. 단, 미인식이 우선 (미인식이면 비교 박스 숨김)
    // 4. ⭐ 부분 결과일 때는 차이점 표시 안 함 (Gemini만 완료된 상태)
    const showDifference = !isPartialResult && hasDifference && !isUnrecognized && (!diff.match || hadAnyValue);

    // ⭐ 천 단위 콤마 적용 대상 필드 (연식 제외)
    const numericFieldsWithComma = ['mileage', 'displacement', 'totalWeight', 'length', 'width', 'height', 'cbm'];
    const isNumericField = numericFieldsWithComma.includes(field);

    // 표시용 값: 천 단위 콤마 적용
    let displayValue = value;
    if (isNumericField) {
      // 값이 있는 경우에만 처리 (빈 문자열, null, undefined 제외)
      if (value !== null && value !== undefined && value !== '') {
        try {
          // 콤마 제거 후 숫자로 변환
          const cleanValue = String(value).replace(/,/g, '').trim();
          const numValue = parseFloat(cleanValue);
          // 숫자 변환 성공 시 천 단위 콤마 적용
          if (!isNaN(numValue) && isFinite(numValue)) {
            displayValue = numValue.toLocaleString('ko-KR');
          }
        } catch (e) {
          console.warn(`[천단위콤마] 필드 ${field} 변환 실패:`, value, e);
        }
      }
    }

    // 입력 핸들러: 콤마 제거 후 저장
    const handleNumericChange = (e) => {
      const rawValue = e.target.value.replace(/,/g, '');
      handleFieldChange(field, rawValue);
    };

    return (
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: '#0A1037' }}>
          {label}
          {showDifference && (
            <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded" style={{
              backgroundColor: '#FFF9E6',
              color: '#0A1037'
            }}>
              ⚠️ 차이 발견
            </span>
          )}
          {isUnrecognized && (
            <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded" style={{
              backgroundColor: '#FFEDD5',
              color: '#9A3412'
            }}>
              미인식
            </span>
          )}
        </label>
        <div className="relative">
          <input
            type={type}
            value={displayValue}
            onChange={isNumericField ? handleNumericChange : (e) => handleFieldChange(field, e.target.value)}
            className={`w-full px-3 py-2 text-sm border rounded focus:ring-2 focus:outline-none ${isUnrecognized
              ? 'border-orange-500 bg-orange-50 font-semibold placeholder-orange-400'
              : showDifference
                ? 'font-medium' // 차이점 표시
                : 'border-gray-300'
              }`}
            style={showDifference ? {
              borderColor: '#EA8600',
              backgroundColor: '#FEF7E0',
              borderWidth: '2px',
              color: '#202124' // 부드러운 텍스트 색상
            } : {}}
            placeholder={
              isUnrecognized
                ? '값을 입력해주세요'
                : ''
            }
          />

          {/* Human-in-the-Loop: Flash vs Final 비교 */}
          {showDifference && (
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              {/* Flash (연습) */}
              <button
                type="button"
                onClick={() => handleFieldChange(field, diff.flash || '')}
                className="p-2 rounded text-left transition-all hover:shadow-md"
                style={{
                  backgroundColor: value === diff.flash ? '#FFFFFF' : '#F9FAFB',
                  border: `2px solid ${value === diff.flash ? '#E94948' : '#E5E7EB'}`,
                  cursor: 'pointer'
                }}
              >
                <div className="font-semibold mb-1" style={{ color: '#0A1037' }}>
                  Flash (연습)
                </div>
                <div className="font-mono break-all" style={{
                  color: value === diff.flash ? '#E94948' : '#6B7280'
                }}>
                  {diff.flash || '—'}
                </div>
                {value === diff.flash && (
                  <div className="mt-1 text-xs font-bold" style={{ color: '#E94948' }}>
                    ✓ 현재 선택됨
                  </div>
                )}
              </button>

              {/* Final (연습) - Pro 우선 */}
              <button
                type="button"
                onClick={() => handleFieldChange(field, diff.final || '')}
                className="p-2 rounded text-left transition-all hover:shadow-md"
                style={{
                  backgroundColor: value === diff.final ? '#FFFFFF' : '#F9FAFB',
                  border: `2px solid ${value === diff.final ? '#1EE9B6' : '#E5E7EB'}`,
                  cursor: 'pointer'
                }}
              >
                <div className="font-semibold mb-1" style={{ color: '#0A1037' }}>
                  Final (연습)
                </div>
                <div className="font-mono break-all" style={{
                  color: value === diff.final ? '#1EE9B6' : '#6B7280'
                }}>
                  {diff.final || '—'}
                </div>
                {value === diff.final && (
                  <div className="mt-1 text-xs font-bold" style={{ color: '#1EE9B6' }}>
                    ✓ 현재 선택됨
                  </div>
                )}
              </button>
            </div>
          )}

          {/* 미인식 안내 */}
          {isUnrecognized && (
            <div className="mt-2 text-xs p-2 rounded" style={{
              backgroundColor: '#FFF7ED',
              border: '1px solid #FDBA74'
            }}>
              <div className="font-semibold mb-1" style={{ color: '#9A3412' }}>
                프롬프트에 정의된 필드이지만 인식하지 못했습니다.
              </div>
              <div style={{ color: '#6B7280' }}>
                문서를 확인하고 값을 입력해주세요.
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: '대기', color: 'bg-gray-100 text-gray-600' },
      processing: { text: '분류 완료', color: 'bg-gray-600 text-white' },
      extracting: { text: '추출 중', color: 'bg-gray-700 text-white' },
      extracted: { text: '추출 완료', color: 'bg-gray-700 text-white' },
      validating: { text: '검증 중', color: 'bg-gray-800 text-white' },
      completed: { text: '검증 완료', color: 'bg-gray-900 text-white' },
      error: { text: '오류', color: 'bg-[#FCE8E6] text-[#C5221F]' }
    };
    return badges[status] || badges.pending;
  };

  // ⭐ 실제 검증 상태 확인 (validatedPages 기반)
  const getActualValidationStatus = (file) => {
    if (!file || !file.id) return { text: '미검증', color: 'bg-yellow-100 text-yellow-700' };

    const fileValidations = validatedPages[file.id] || {};

    if (file.pageResults && file.pageResults.length > 0) {
      // PDF인 경우: 모든 페이지가 검증되었는지 확인
      const totalPages = file.pageResults.length;
      const validatedPagesCount = Object.keys(fileValidations).filter(
        pageNum => fileValidations[pageNum] === true
      ).length;

      if (validatedPagesCount === 0) {
        return { text: '미검증', color: 'bg-yellow-100 text-yellow-700' };
      } else if (validatedPagesCount === totalPages) {
        return { text: '검증 완료', color: 'bg-green-100 text-green-700' };
      } else {
        return { text: `검증 중 (${validatedPagesCount}/${totalPages})`, color: 'bg-blue-100 text-blue-700' };
      }
    } else {
      // 단일 이미지인 경우
      return fileValidations[1] === true
        ? { text: '검증 완료', color: 'bg-green-100 text-green-700' }
        : { text: '미검증', color: 'bg-yellow-100 text-yellow-700' };
    }
  };

  // 오른쪽 패널 안전 바인딩: 선택 인덱스가 없으면 첫 번째 체크된 항목으로 바인딩
  const derivedSelectedIndex = selectedFileIndex !== null
    ? selectedFileIndex
    : (fileList.findIndex(f => f.checked) !== -1 ? fileList.findIndex(f => f.checked) : null);
  const selectedFile = derivedSelectedIndex !== null ? fileList[derivedSelectedIndex] : null;
  // ⭐ PDF 페이지별 바운딩 박스 및 PNG 이미지 처리
  const currentPageData = selectedFile?.pageResults?.find(p => p.pageNumber === selectedPageNumber);

  // ⭐ PDF인 경우 현재 페이지의 데이터를 extractedData로 사용 (차이점 정보 포함)
  let extractedData = selectedFile?.extractedData;
  if (currentPageData) {
    const baseData = currentPageData.extractedData || currentPageData.proData || currentPageData.flashData || {};
    extractedData = {
      ...baseData,
      differences: currentPageData.differences, // ⭐ 차이점 객체 병합
      partial: currentPageData.partial,
      documentType: currentPageData.documentType
    };
  }
  const selectedFileWithBoundingBoxes = selectedFile ? {
    ...selectedFile,
    // PDF인 경우 현재 페이지의 바운딩 박스, 이미지인 경우 파일 레벨의 바운딩 박스
    boundingBoxes: currentPageData?.boundingBoxes || selectedFile.boundingBoxes || [],
    // PDF 페이지의 이미지 크기 정보 사용
    imageWidth: currentPageData?.imageWidth || selectedFile.imageWidth,
    imageHeight: currentPageData?.imageHeight || selectedFile.imageHeight,
    // ⭐ 상세 모드용: PDF 페이지의 PNG 이미지 (base64)
    pngImage: currentPageData?.pngImage
  } : null;

  // ⚠️ 주의: 조건부 return 이후에 hooks를 사용할 수 없으므로 제거됨
  // 디버깅용 useEffect는 조건부 return 전으로 이동하거나 제거해야 함
  // extractedData와 selectedFileWithBoundingBoxes는 렌더링 중에 직접 사용 가능

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 업데이트 알림 */}
      <UpdateNotification />

      {/* 🔍 청크 디버그 팝업 */}
      <ChunkDebugModal
        isOpen={showChunkDebugModal}
        onClose={() => setShowChunkDebugModal(false)}
        chunkDetails={chunkDebugData?.chunkDetails}
        referenceExample={chunkDebugData?.referenceExample}
        totalPages={chunkDebugData?.totalPages}
      />

      {/* 헤더 - 워크플로우 통합 */}
      <header className="px-6 py-2 flex-shrink-0" style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E5E7EB' }}>
        <div className="flex items-center justify-between">
          {/* 왼쪽: 로고 */}
          <div className="flex items-center">
            {/* ucar 로고 - 기존 스타일 유지 */}
            <h1 className="brand-logo lowercase tracking-tight">
              ucar
            </h1>
          </div>

          {/* 가운데: 워크플로우 탭 - 🔧 임시 숨김 (hidden) */}
          <nav className="hidden flex items-center gap-1 absolute left-1/2 transform -translate-x-1/2" style={{ marginLeft: '-120px' }}>
            <button
              onClick={() => setActiveTab('viewer')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'viewer'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${activeTab === 'viewer' ? 'bg-white text-blue-600' : 'bg-gray-200 text-gray-600'
                }`}>1</span>
              <span>문서인식</span>
            </button>

            <ChevronRight className="w-4 h-4 text-gray-300" />

            <button
              onClick={() => setActiveTab('grid')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'grid'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${activeTab === 'grid' ? 'bg-white text-blue-600' : 'bg-gray-200 text-gray-600'
                }`}>2</span>
              <span>문서검증</span>
            </button>

            <ChevronRight className="w-4 h-4 text-gray-300" />

            <button
              onClick={() => setActiveTab('mapping')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'mapping'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${activeTab === 'mapping' ? 'bg-white text-blue-600' : 'bg-gray-200 text-gray-600'
                }`}>3</span>
              <span>차량등록</span>
            </button>
          </nav>

          {/* 오른쪽: 레이아웃 토글 + DB 상태 + 사용자 정보 + 설정 */}
          <div className="flex items-center gap-3">
            {/* 🔧 레이아웃 모드 토글 버튼 */}
            <div className="flex items-center gap-0.5 bg-gray-100 rounded-md p-0.5">
              <button
                onClick={() => {
                  setLayoutMode('vertical');
                  localStorage.setItem('layoutMode', 'vertical');
                }}
                className={`p-1.5 rounded transition-colors ${layoutMode === 'vertical'
                  ? 'bg-white shadow-sm text-blue-600'
                  : 'text-gray-400 hover:text-gray-600'
                  }`}
                title="세로 레이아웃 (좌: 파일 | 중: PDF | 우: 그리드)"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="1" y="1" width="4" height="14" rx="1" />
                  <rect x="6" y="1" width="4" height="14" rx="1" />
                  <rect x="11" y="1" width="4" height="14" rx="1" />
                </svg>
              </button>
              <button
                onClick={() => {
                  setLayoutMode('horizontal');
                  localStorage.setItem('layoutMode', 'horizontal');
                  // 🔧 가로 모드 전환 시 AUIGrid 확장
                  setTimeout(() => {
                    if (window.AUIGrid) {
                      const gridId = '#horizontal_grid';
                      if (window.AUIGrid.isCreated(gridId)) {
                        window.AUIGrid.resize(gridId);
                        if (window.AUIGrid.fitColumnWidthToContainer) {
                          window.AUIGrid.fitColumnWidthToContainer(gridId);
                        }
                        console.log('📐 [Horizontal Mode] AUIGrid 리사이즈 완료');
                      }
                    }
                  }, 350);
                }}
                className={`p-1.5 rounded transition-colors ${layoutMode === 'horizontal'
                  ? 'bg-white shadow-sm text-blue-600'
                  : 'text-gray-400 hover:text-gray-600'
                  }`}
                title="가로 레이아웃 (상: 그리드 | 하: PDF)"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="1" y="1" width="14" height="6" rx="1" />
                  <rect x="1" y="9" width="14" height="6" rx="1" />
                </svg>
              </button>
            </div>

            {/* 🤖 AI 패널 토글 버튼 (인라인 사이드바 토글) */}
            <button
              onClick={() => {
                setShowAIPanel(prev => !prev);
                window.dispatchEvent(new CustomEvent('toggleAIPanel'));
              }}
              className={`p-1.5 rounded transition-colors flex items-center gap-1.5 ${showAIPanel
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              title={showAIPanel ? 'AI 어시스턴트 닫기' : 'AI 어시스턴트 열기'}
            >
              <Sparkles size={14} />
              <span className="text-xs font-medium">AI</span>
            </button>

            {/* DB 연결 상태 인디케이터 - 🔧 TODO: 추후 삭제 예정, 현재 hidden */}
            <div
              className="hidden px-3 py-1.5 rounded-md flex items-center gap-2 text-xs font-medium cursor-pointer transition-all"
              style={{
                backgroundColor: dbStatus.connected ? '#E7F4E8' : (dbStatus.loading ? '#FFF4E6' : '#FFE6E6'),
                color: dbStatus.connected ? '#0F5132' : (dbStatus.loading ? '#996600' : '#721C24'),
                border: `1px solid ${dbStatus.connected ? '#A8D8B5' : (dbStatus.loading ? '#FFD699' : '#FFB3B3')}`
              }}
              onClick={() => { setSettingsModalTab('db'); setShowSettings(true); }}
              title={dbStatus.connected ? `연결됨: ${dbStatus.server}` : (dbStatus.loading ? 'DB 상태 확인 중...' : 'DB 연결 끊김')}
            >
              <Database className="w-4 h-4" />
              {dbStatus.loading ? (
                'DB 확인 중...'
              ) : dbStatus.connected ? (
                <>
                  {dbStatus.server?.includes('d136.tlogin.net') ? '🖥️ IDC DB' :
                    dbStatus.server?.includes('211.198.4.245') ? '🏢 사무실 DB' :
                      '✅ DB 연결됨'}
                </>
              ) : (
                '❌ DB 끊김'
              )}
            </div>

            {/* 구분선 */}
            <div className="h-6 w-px bg-gray-200"></div>

            {/* 사용자 정보 */}
            <div className="text-xs text-gray-500">
              {currentGroupCode} | {currentUserId}
            </div>

            {/* 템플릿 관리 버튼 추가 */}
            <button
              onClick={() => setShowTemplateManagement(true)}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              title="템플릿 관리"
            >
              <LayoutTemplate className="w-5 h-5" />
            </button>

            {/* 프롬프트 설정 버튼 */}
            <button
              onClick={() => setShowPromptManager(true)}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              title="프롬프트 설정"
            >
              <FileText className="w-5 h-5" />
            </button>

            {/* 설정 버튼 */}
            <button
              onClick={() => { setSettingsModalTab('ocr'); setShowSettings(true); }}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              title="설정"
            >
              <Settings className="w-5 h-5" />
            </button>

            {/* 로그아웃 버튼 */}
            <button
              onClick={handleLogout}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title={`로그아웃`}
            >
              로그아웃
            </button>
          </div>
        </div>
      </header>

      {/* 설정 모달 - 탭 기반 */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4" onClick={() => setShowSettings(false)}>
          <div className="bg-white rounded-xl w-full flex flex-col shadow-2xl" style={{ maxWidth: '900px', height: '85vh' }} onClick={(e) => e.stopPropagation()}>
            {/* 헤더 */}
            <div className="flex items-center justify-between px-6 py-4 flex-shrink-0 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5 text-gray-700" />
                <h2 className="text-lg font-semibold" style={{ color: '#0A1037' }}>설정</h2>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 탭 + 내용 */}
            <div className="flex-1 flex overflow-hidden">
              {/* 왼쪽 탭 네비게이션 */}
              <div className="w-44 bg-gray-50 border-r border-gray-200 p-4 flex-shrink-0">
                <nav className="space-y-1">
                  <button
                    onClick={() => setSettingsModalTab('ocr')}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${settingsModalTab === 'ocr' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    <FileText size={18} className={settingsModalTab === 'ocr' ? 'text-blue-600' : 'text-gray-500'} />
                    OCR 설정
                  </button>
                  <button
                    onClick={() => setSettingsModalTab('basic')}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${settingsModalTab === 'basic' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    <Shield size={18} className={settingsModalTab === 'basic' ? 'text-blue-600' : 'text-gray-500'} />
                    기초설정
                  </button>
                  <button
                    onClick={() => setSettingsModalTab('db')}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${settingsModalTab === 'db' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    <Database size={18} className={settingsModalTab === 'db' ? 'text-blue-600' : 'text-gray-500'} />
                    DB 설정
                  </button>
                  <button
                    onClick={() => setSettingsModalTab('finetuning')}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${settingsModalTab === 'finetuning' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    <RefreshCw size={18} className={settingsModalTab === 'finetuning' ? 'text-blue-600' : 'text-gray-500'} />
                    파인튜닝
                  </button>
                  <button
                    onClick={() => {
                      setShowSettings(false);
                      setShowMigrationModal(true);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-gray-600 hover:bg-gray-100"
                  >
                    <FileText size={18} className="text-gray-500" />
                    📊 마이그레이션
                  </button>
                </nav>
              </div>

              {/* 오른쪽 탭 내용 */}
              <div className="flex-1 overflow-hidden flex flex-col">
                {/* OCR 설정 탭 */}
                {settingsModalTab === 'ocr' && (
                  <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                    {/* ⭐ Hybrid OCR 설정 (사용자 요청) */}
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#0A1037' }}>
                        OCR 설정
                      </label>
                      <div className="space-y-2 mb-4">
                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{ borderColor: enableHybridOCR ? '#339AF0' : '#E0E0E0', backgroundColor: enableHybridOCR ? '#E7F5FF' : 'white' }}
                        >
                          <input
                            type="checkbox"
                            checked={enableHybridOCR}
                            onChange={(e) => {
                              setEnableHybridOCR(e.target.checked);
                              localStorage.setItem('enableHybridOCR', e.target.checked);
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                              Hybrid Mode (PaddleOCR + LLM)
                            </div>
                            <div className="text-xs text-gray-500">
                              활성화 시 바운딩 박스 시각화 및 향상된 좌표 추출을 지원합니다.
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* 문서 분류 활성화 체크박스 */}
                    <div>
                      <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors mb-3"
                        style={{ borderColor: enableClassification ? '#339AF0' : '#E0E0E0', backgroundColor: enableClassification ? '#E7F5FF' : 'white' }}
                      >
                        <input
                          type="checkbox"
                          checked={enableClassification}
                          onChange={(e) => {
                            setEnableClassification(e.target.checked);
                            localStorage.setItem('enableClassification', e.target.checked);
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                            문서 분류 사용
                          </div>
                          <div className="text-xs text-gray-500">
                            비활성화 시 템플릿이 선택되어 있으면 분류를 건너뛰고 바로 추출합니다. (속도 향상)
                          </div>
                        </div>
                      </label>
                    </div>

                    {/* OCR 엔진 선택 → 추출 모델 선택으로 변경 */}
                    <div style={{ opacity: enableClassification ? 1 : 0.5, pointerEvents: enableClassification ? 'auto' : 'none' }}>
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#0A1037' }}>
                        문서 분류 모델 {!enableClassification && <span className="text-xs text-gray-400">(비활성화됨)</span>}
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{ borderColor: classificationFlashModel === '2.0' ? '#339AF0' : '#E0E0E0', backgroundColor: classificationFlashModel === '2.0' ? '#E7F5FF' : 'white' }}
                        >
                          <input
                            type="radio"
                            name="classificationFlashModel"
                            checked={classificationFlashModel === '2.0'}
                            onChange={() => setClassificationFlashModel('2.0')}
                            className="w-4 h-4"
                          />
                          <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                            Gemini 2.0 Flash
                          </div>
                        </label>

                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{ borderColor: classificationFlashModel === '2.5' ? '#339AF0' : '#E0E0E0', backgroundColor: classificationFlashModel === '2.5' ? '#E7F5FF' : 'white' }}
                        >
                          <input
                            type="radio"
                            name="classificationFlashModel"
                            checked={classificationFlashModel === '2.5'}
                            onChange={() => setClassificationFlashModel('2.5')}
                            className="w-4 h-4"
                          />
                          <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                            Gemini 2.5 Flash
                          </div>
                        </label>

                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{ borderColor: classificationFlashModel === 'upstage' ? '#339AF0' : '#E0E0E0', backgroundColor: classificationFlashModel === 'upstage' ? '#E7F5FF' : 'white' }}
                        >
                          <input
                            type="radio"
                            name="classificationFlashModel"
                            checked={classificationFlashModel === 'upstage'}
                            onChange={() => setClassificationFlashModel('upstage')}
                            className="w-4 h-4"
                          />
                          <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                            Upstage Document AI
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* ⭐ 템플릿 추출 모델 선택 */}
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#0A1037' }}>
                        템플릿 추출 모델
                      </label>
                      <div className="text-xs text-gray-600 mb-2">
                        💡 템플릿 기반 추출 시 사용할 모델을 선택합니다
                      </div>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{ borderColor: templateExtractionModel === 'gemini-2.5-pro' ? '#339AF0' : '#E0E0E0', backgroundColor: templateExtractionModel === 'gemini-2.5-pro' ? '#E7F5FF' : 'white' }}
                        >
                          <input
                            type="radio"
                            name="templateExtractionModel"
                            checked={templateExtractionModel === 'gemini-2.5-pro'}
                            onChange={() => {
                              setTemplateExtractionModel('gemini-2.5-pro');
                              localStorage.setItem('templateExtractionModel', 'gemini-2.5-pro');
                            }}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                              Gemini 2.5 Pro
                            </div>
                            <div className="text-xs text-gray-500">안정적, 복잡한 테이블 추출에 강함</div>
                          </div>
                        </label>

                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{ borderColor: templateExtractionModel === 'gemini-3-flash-preview' ? '#339AF0' : '#E0E0E0', backgroundColor: templateExtractionModel === 'gemini-3-flash-preview' ? '#E7F5FF' : 'white' }}
                        >
                          <input
                            type="radio"
                            name="templateExtractionModel"
                            checked={templateExtractionModel === 'gemini-3-flash-preview'}
                            onChange={() => {
                              setTemplateExtractionModel('gemini-3-flash-preview');
                              localStorage.setItem('templateExtractionModel', 'gemini-3-flash-preview');
                            }}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                              Gemini 3.0 Flash ⚡️
                            </div>
                            <div className="text-xs text-gray-500">빠른 속도 + 강력한 추론, 최신 모델</div>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* 병렬 추출 모델 선택 */}
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#0A1037' }}>
                        병렬 추출 모델 (최소 1개, 최대 3개 선택)
                      </label>
                      <div className="text-xs text-gray-600 mb-2">
                        💡 복수 선택 시 병렬 추출 후 차이점을 노란색으로 표시하여 검증합니다
                      </div>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{
                            borderColor: selectedValidationModels.includes('gemini-2.5-flash') ? '#339AF0' : '#E0E0E0',
                            backgroundColor: selectedValidationModels.includes('gemini-2.5-flash') ? '#E7F5FF' : 'white'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedValidationModels.includes('gemini-2.5-flash')}
                            onChange={() => toggleValidationModel('gemini-2.5-flash')}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                              Gemini 2.5 Flash
                            </div>
                            <div className="text-xs text-gray-500">빠른 속도, 저렴함</div>
                          </div>
                        </label>

                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{
                            borderColor: selectedValidationModels.includes('gemini-2.5-pro') ? '#339AF0' : '#E0E0E0',
                            backgroundColor: selectedValidationModels.includes('gemini-2.5-pro') ? '#E7F5FF' : 'white'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedValidationModels.includes('gemini-2.5-pro')}
                            onChange={() => toggleValidationModel('gemini-2.5-pro')}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                              Gemini 2.5 Pro
                            </div>
                            <div className="text-xs text-gray-500">Stable Version, 복잡한 문서</div>
                          </div>
                        </label>

                        {/* ⭐ Gemini 3.0 Pro (2025.11.18 출시) - 추론 우선 아키텍처 */}
                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{
                            borderColor: selectedValidationModels.includes('gemini-3-pro-preview') ? '#339AF0' : '#E0E0E0',
                            backgroundColor: selectedValidationModels.includes('gemini-3-pro-preview') ? '#E7F5FF' : 'white'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedValidationModels.includes('gemini-3-pro-preview')}
                            onChange={() => toggleValidationModel('gemini-3-pro-preview')}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                              Gemini 3.0 Pro 🧠
                            </div>
                            <div className="text-xs text-gray-500">추론 우선, 복잡한 에이전트 작업</div>
                          </div>
                        </label>

                        {/* ⭐ Gemini 3.0 Flash (2025.12.17 출시) - 속도 + 추론 */}
                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{
                            borderColor: selectedValidationModels.includes('gemini-3-flash-preview') ? '#339AF0' : '#E0E0E0',
                            backgroundColor: selectedValidationModels.includes('gemini-3-flash-preview') ? '#E7F5FF' : 'white'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedValidationModels.includes('gemini-3-flash-preview')}
                            onChange={() => toggleValidationModel('gemini-3-flash-preview')}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                              Gemini 3.0 Flash ⚡️
                            </div>
                            <div className="text-xs text-gray-500">빠른 속도 + 강력한 추론, 최신 모델</div>
                          </div>
                        </label>

                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{
                            borderColor: selectedValidationModels.includes('claude-haiku') ? '#339AF0' : '#E0E0E0',
                            backgroundColor: selectedValidationModels.includes('claude-haiku') ? '#E7F5FF' : 'white'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedValidationModels.includes('claude-haiku')}
                            onChange={() => toggleValidationModel('claude-haiku')}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                              Claude Haiku 4.5
                            </div>
                            <div className="text-xs text-gray-500">초고속, 간단한 문서</div>
                          </div>
                        </label>

                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{
                            borderColor: selectedValidationModels.includes('claude-sonnet') ? '#339AF0' : '#E0E0E0',
                            backgroundColor: selectedValidationModels.includes('claude-sonnet') ? '#E7F5FF' : 'white'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedValidationModels.includes('claude-sonnet')}
                            onChange={() => toggleValidationModel('claude-sonnet')}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                              Claude Sonnet 4.5
                            </div>
                            <div className="text-xs text-gray-500">최고 정확도, 프리미엄</div>
                          </div>
                        </label>

                        {/* ⭐ DeepSeek-OCR 추가 */}
                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{
                            borderColor: selectedValidationModels.includes('deepseek') ? '#339AF0' : '#E0E0E0',
                            backgroundColor: selectedValidationModels.includes('deepseek') ? '#E7F5FF' : 'white'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedValidationModels.includes('deepseek')}
                            onChange={() => toggleValidationModel('deepseek')}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                              DeepSeek-OCR
                            </div>
                            <div className="text-xs text-gray-500">로컬 GPU, 빠름, $0.01/건</div>
                          </div>
                        </label>

                        {/* ⭐ Datalab Marker 추가 - Prompt API로 정확도 개선 */}
                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{
                            borderColor: selectedValidationModels.includes('datalab-marker') ? '#339AF0' : '#E0E0E0',
                            backgroundColor: selectedValidationModels.includes('datalab-marker') ? '#E7F5FF' : 'white'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedValidationModels.includes('datalab-marker')}
                            onChange={() => toggleValidationModel('datalab-marker')}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                              Datalab Marker 📄
                            </div>
                            <div className="text-xs text-gray-500">프롬프트 최적화, 테이블 병합</div>
                          </div>
                        </label>

                        {/* 🏷️ PaddleOCR 라벨 기반 추가 */}
                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{
                            borderColor: selectedValidationModels.includes('paddleocr-label') ? '#10B981' : '#E0E0E0',
                            backgroundColor: selectedValidationModels.includes('paddleocr-label') ? '#ECFDF5' : 'white'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedValidationModels.includes('paddleocr-label')}
                            onChange={() => toggleValidationModel('paddleocr-label')}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                              PaddleOCR (라벨 기반) 🏷️
                            </div>
                            <div className="text-xs text-gray-500">라벨 영역만 초고속 추출</div>
                          </div>
                        </label>

                        {/* 🔮 Mistral OCR 추가 */}
                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{
                            borderColor: selectedValidationModels.includes('mistral-ocr') ? '#A855F7' : '#E0E0E0',
                            backgroundColor: selectedValidationModels.includes('mistral-ocr') ? '#FAF5FF' : 'white'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedValidationModels.includes('mistral-ocr')}
                            onChange={() => toggleValidationModel('mistral-ocr')}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                              Mistral OCR 🔮
                            </div>
                            <div className="text-xs text-gray-500">문서 이해 특화, 빠른 처리</div>
                          </div>
                        </label>

                        {/* 📘 LangExtract (Docling + LangExtract 파이프라인) */}
                        <label className="flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{
                            borderColor: selectedValidationModels.includes('langextract') ? '#0EA5E9' : '#E0E0E0',
                            backgroundColor: selectedValidationModels.includes('langextract') ? '#F0F9FF' : 'white'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedValidationModels.includes('langextract')}
                            onChange={() => toggleValidationModel('langextract')}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ color: '#0A1037' }}>
                              LangExtract 📘
                            </div>
                            <div className="text-xs text-gray-500">Docling + LangExtract, 좌표 그라운딩</div>
                          </div>
                        </label>
                      </div>

                      {/* ⭐ Gemini ThinkingConfig 설정 (Gemini 2.5/3.0 선택 시 표시) */}
                      {(selectedValidationModels.some(m => m.includes('gemini-2.5') || m.includes('gemini-3'))) && (
                        <div className="mt-3 p-3 rounded-lg border" style={{ borderColor: '#E0E0E0', backgroundColor: '#F9FAFB' }}>
                          <div className="text-sm font-semibold mb-2" style={{ color: '#0A1037' }}>
                            🧠 사고 수준 설정 (Thinking Config)
                          </div>

                          {/* Gemini 2.5 - thinkingBudget */}
                          {selectedValidationModels.some(m => m.includes('gemini-2.5')) && (
                            <div className="mb-3">
                              <label className="block text-xs font-medium mb-1" style={{ color: '#0A1037' }}>
                                Gemini 2.5 - 사고 예산 (Thinking Budget)
                              </label>
                              <div className="grid grid-cols-3 gap-2">
                                <button
                                  onClick={() => setThinkingBudget(0)}
                                  className={`px-2 py-1.5 rounded border text-xs ${thinkingBudget === 0 ? 'bg-orange-50 border-orange-300 font-semibold' : 'border-gray-200'}`}
                                >
                                  🚀 비활성화 (0)
                                </button>
                                <button
                                  onClick={() => setThinkingBudget(-1)}
                                  className={`px-2 py-1.5 rounded border text-xs ${thinkingBudget === -1 ? 'bg-blue-50 border-blue-300 font-semibold' : 'border-gray-200'}`}
                                >
                                  🔄 동적 (-1)
                                </button>
                                <button
                                  onClick={() => setThinkingBudget(8192)}
                                  className={`px-2 py-1.5 rounded border text-xs ${thinkingBudget > 0 ? 'bg-purple-50 border-purple-300 font-semibold' : 'border-gray-200'}`}
                                >
                                  🎯 8K 토큰
                                </button>
                              </div>
                              <div className="text-[10px] text-gray-500 mt-1">
                                0: Flash 스타일 (빠름) | -1: 동적 사고 (기본) | 8K: 제한된 심층 추론
                              </div>
                            </div>
                          )}

                          {/* Gemini 3.0 - thinkingLevel */}
                          {selectedValidationModels.includes('gemini-3-pro-preview') && (
                            <div className="mb-2">
                              <label className="block text-xs font-medium mb-1" style={{ color: '#0A1037' }}>
                                Gemini 3.0 Pro - 사고 수준 (Thinking Level)
                              </label>
                              <div className="grid grid-cols-2 gap-2">
                                <button
                                  onClick={() => setThinkingLevel('HIGH')}
                                  className={`px-2 py-1.5 rounded border text-xs ${thinkingLevel === 'HIGH' ? 'bg-purple-50 border-purple-300 font-semibold' : 'border-gray-200'}`}
                                >
                                  🧠 HIGH (심층 추론)
                                </button>
                                <button
                                  onClick={() => setThinkingLevel('LOW')}
                                  className={`px-2 py-1.5 rounded border text-xs ${thinkingLevel === 'LOW' ? 'bg-green-50 border-green-300 font-semibold' : 'border-gray-200'}`}
                                >
                                  ⚡ LOW (빠른 응답)
                                </button>
                              </div>
                              <div className="text-[10px] text-gray-500 mt-1">
                                HIGH: 복잡한 문서, 다단계 추론 | LOW: 단순 문서, 빠른 처리
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* DeepSeek 설정 (DeepSeek 선택 시만 표시) */}
                      {selectedValidationModels.includes('deepseek') && (
                        <div className="mt-3 p-3 rounded-lg border" style={{ borderColor: '#E0E0E0', backgroundColor: '#F9FAFB' }}>
                          <div className="text-sm font-semibold mb-2" style={{ color: '#0A1037' }}>
                            DeepSeek 설정
                          </div>

                          {/* DeepSeek 서버 선택 */}
                          <div className="mb-3">
                            <label className="block text-xs font-medium mb-1" style={{ color: '#0A1037' }}>
                              서버 선택
                            </label>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              <button
                                onClick={() => setDeepseekServerUrl('http://localhost:8001')}
                                className={`px-2 py-1.5 rounded border text-xs ${deepseekServerUrl.includes('localhost:8001') ? 'bg-blue-50 border-blue-300 font-semibold' : 'border-gray-200'}`}
                              >
                                🖥️ 로컬
                              </button>
                              <button
                                onClick={() => setDeepseekServerUrl('http://35.186.162.229:8003')}
                                className={`px-2 py-1.5 rounded border text-xs ${deepseekServerUrl.includes('8003') ? 'bg-green-50 border-green-300 font-semibold' : 'border-gray-200'}`}
                              >
                                VM
                              </button>
                            </div>
                          </div>

                          {/* DeepSeek 속도 모드 */}
                          <div className="mb-3">
                            <label className="block text-xs font-medium mb-1" style={{ color: '#0A1037' }}>
                              속도 모드
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                              <button onClick={() => setDeepseekMode('default')} className={`px-2 py-1.5 rounded border text-xs ${deepseekMode === 'default' ? 'bg-blue-50 border-blue-300' : 'border-gray-200'}`}>기본</button>
                              <button onClick={() => setDeepseekMode('optimized')} className={`px-2 py-1.5 rounded border text-xs ${deepseekMode === 'optimized' ? 'bg-blue-50 border-blue-300' : 'border-gray-200'}`}>최적화</button>
                              <button onClick={() => setDeepseekMode('fast')} className={`px-2 py-1.5 rounded border text-xs ${deepseekMode === 'fast' ? 'bg-blue-50 border-blue-300' : 'border-gray-200'}`}>빠른</button>
                            </div>
                          </div>

                          {/* 그라운딩 설정 (DeepSeek/Gemini) */}
                          <div className="p-2 rounded-lg border" style={{ borderColor: '#E0E0E0', backgroundColor: grounding ? '#E7F5FF' : '#FFF3E0' }}>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={grounding}
                                onChange={(e) => setGrounding(e.target.checked)}
                                className="w-4 h-4 cursor-pointer"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-xs" style={{ color: '#0A1037' }}>
                                  바운딩 박스 (그라운딩)
                                </div>
                                <div className="text-[10px] text-gray-600 mt-0.5">
                                  {grounding ? 'ON: 위치 정보 포함' : 'OFF: 텍스트만'}
                                </div>
                              </div>
                            </label>
                          </div>
                        </div>
                      )}

                      {/* ⭐ Datalab 바운딩 박스 설정 (Datalab 선택 시에만 표시) */}
                      {selectedValidationModels.includes('datalab-marker') && (
                        <div className="mt-3">
                          <div className="p-2 rounded-lg border" style={{ borderColor: '#E0E0E0', backgroundColor: datalabBoundingBox ? '#E7F5FF' : '#FFF3E0' }}>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={datalabBoundingBox}
                                onChange={(e) => setDatalabBoundingBox(e.target.checked)}
                                className="w-4 h-4 cursor-pointer"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-xs" style={{ color: '#0A1037' }}>
                                  Datalab 바운딩 박스
                                </div>
                                <div className="text-[10px] text-gray-600 mt-0.5">
                                  {datalabBoundingBox ? 'ON: OCR API로 위치 정보 추출' : 'OFF: Marker만 사용'}
                                </div>
                              </div>
                            </label>
                          </div>
                        </div>
                      )}

                      {/* 선택된 모델 표시 */}
                      {selectedValidationModels.length > 0 && (
                        <div className="mt-3 p-2 rounded-lg bg-blue-50 border border-blue-200">
                          <div className="text-xs font-medium text-blue-700 mb-1">
                            선택된 모델 ({selectedValidationModels.length}개):
                          </div>
                          <div className="text-xs text-blue-600">
                            {selectedValidationModels.map(m => m === 'deepseek' ? 'DeepSeek-OCR' : getModelDisplayName(m)).join(' + ')}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 모델명 매핑 정보 (개발자용) */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#0A1037' }}>
                        📋 모델명 매핑 (개발자용)
                      </label>
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="text-xs text-gray-600 space-y-1">
                          {Object.entries(getAllModelNicknames()).map(([modelName, nickname]) => (
                            <div key={modelName} className="flex items-center justify-between">
                              <span className="font-mono text-gray-700">{nickname}</span>
                              <span className="text-gray-500">→</span>
                              <span className="text-gray-700">{modelName}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* OCR 설정 저장 버튼 */}
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => {
                          // localStorage에 설정 저장
                          try {
                            localStorage.setItem('classificationFlashModel', classificationFlashModel);
                            localStorage.setItem('selectedValidationModels', JSON.stringify(selectedValidationModels));
                            localStorage.setItem('ocrEngine', ocrEngine);
                            // ⭐ ThinkingConfig 저장
                            localStorage.setItem('thinkingBudget', thinkingBudget.toString());
                            localStorage.setItem('thinkingLevel', thinkingLevel);
                            if (ocrEngine === 'deepseek') {
                              localStorage.setItem('deepseekMode', deepseekMode);
                              localStorage.setItem('deepseekServerUrl', deepseekServerUrl);
                              localStorage.setItem('grounding', grounding.toString());
                            }
                            if (selectedValidationModels.includes('datalab-marker')) {
                              localStorage.setItem('datalabBoundingBox', datalabBoundingBox.toString());
                            }
                            console.log('✅ 설정 저장 완료 (ThinkingBudget:', thinkingBudget, ', ThinkingLevel:', thinkingLevel, ')');
                          } catch (error) {
                            console.error('설정 저장 실패:', error);
                          }

                          setShowSettings(false);

                          // 로그 메시지
                          if (ocrEngine === 'deepseek') {
                            const modeLabel = deepseekMode === 'optimized' ? '최적화' : deepseekMode === 'fast' ? '빠른' : '기본';
                            const groundingLabel = grounding ? 'Grounding ON' : 'Grounding OFF';
                            addLog(`✅ 설정: [DeepSeek-OCR - ${modeLabel} 모드, ${groundingLabel}]`, 'success');
                          } else {
                            const extractionText = selectedValidationModels.length === 1
                              ? `${selectedValidationModels.map(m => m === 'deepseek' ? 'DeepSeek-OCR' : getModelDisplayName(m))[0]} (단일)`
                              : `${selectedValidationModels.map(m => m === 'deepseek' ? 'DeepSeek-OCR' : getModelDisplayName(m)).join(' + ')} (병렬 추출 + 검증)`;
                            const classText = classificationFlashModel === '2.0' ? 'Gemini 2.0 Flash' : 'Gemini 2.5 Flash';
                            addLog(`✅ 설정: [분류: ${classText}] [추출: ${extractionText}]`, 'success');
                          }
                        }}
                        className="w-full px-4 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: '#FFDD33', color: '#0A1037' }}
                      >
                        설정 저장
                      </button>
                    </div>
                  </div>
                )}

                {/* 기초설정 탭 */}
                {settingsModalTab === 'basic' && (
                  <div className="flex-1 overflow-hidden">
                    <VehicleMasterManagement API_BASE_URL={API_BASE_URL} />
                  </div>
                )}

                {/* DB 설정 탭 */}
                {settingsModalTab === 'db' && (
                  <div className="flex-1 overflow-auto">
                    <DBSettings />
                  </div>
                )}

                {/* 파인튜닝 탭 */}
                {settingsModalTab === 'finetuning' && (
                  <div className="flex-1 overflow-auto">
                    <FineTuningManagement />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3단 레이아웃 - 통합 (세로/가로 모드 지원) */}
      {true && (
        <div className="flex h-[calc(100vh-81px)] relative">
          {/* 🧭 사이드바 네비게이션 (최좌측) */}
          <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />

          {/* 패널 토글 버튼들은 각 리사이저 핸들 내부로 이동 */}

          {/* 왼쪽 패널: 파일 리스트 (레거시 거래처 등록 시 숨김) */}
          {activeTab !== 'legacy-registration' && activeTab !== 'talkcloud' && (
            <div
              ref={leftPanelRef}
              className={`${isLeftPanelCollapsed ? 'w-0' : ''} bg-white border-r border-gray-200 flex flex-col transition-all duration-300 relative overflow-hidden`}
              style={{ width: isLeftPanelCollapsed ? 0 : `${leftPanelWidth}px` }}
            >
              {/* 상단 고정: 문서 추출 시작 버튼 */}
              <div className="p-4 flex-shrink-0" style={{ borderBottom: '1px solid #F4F4F4' }}>
                <div className="space-y-2">
                  {/* 워크플로우 안내 */}
                  {fileList.length === 0 && (
                    <div className="text-[10px] text-gray-500 text-center py-1">
                      1. 파일 업로드 → 2. 파일 선택 → 3. 추출 시작
                    </div>
                  )}
                  {fileList.length > 0 && checkedCount === 0 && (
                    <div className="text-[10px] text-gray-500 text-center py-1">
                      처리할 파일을 선택해주세요
                    </div>
                  )}
                  {checkedCount > 0 && (
                    <div className="text-[10px] text-gray-600 text-center py-1 font-medium">
                      {checkedCount}개 파일 선택됨
                    </div>
                  )}

                  {/* 🚀 처리 모드 선택 (속도 vs 좌표) */}
                  <div className="mb-2 p-2 bg-gray-50 rounded-lg">
                    <div className="text-[11px] text-gray-500 mb-1 font-medium">처리 모드</div>
                    <div className="flex gap-2">
                      <label className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-medium cursor-pointer transition-all ${ocrProcessingMode === 'speed'
                        ? 'bg-green-100 text-green-700 border border-green-400'
                        : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'
                        }`}>
                        <input
                          type="radio"
                          name="ocrMode"
                          value="speed"
                          checked={ocrProcessingMode === 'speed'}
                          onChange={(e) => {
                            setOcrProcessingMode(e.target.value);
                            // 🚀 속도 모드 → Gemini 3.0 Flash 자동 선택
                            if (!selectedValidationModels.includes('gemini-3-flash-preview')) {
                              setSelectedValidationModels(['gemini-3-flash-preview']);
                            }
                          }}
                          className="sr-only"
                        />
                        속도
                      </label>
                      <label className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-medium cursor-pointer transition-all ${ocrProcessingMode === 'coordinate'
                        ? 'bg-red-100 text-red-600 border border-red-400'
                        : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'
                        }`}>
                        <input
                          type="radio"
                          name="ocrMode"
                          value="coordinate"
                          checked={ocrProcessingMode === 'coordinate'}
                          onChange={(e) => {
                            setOcrProcessingMode(e.target.value);
                            // 📍 좌표 모드 → LangExtract 자동 선택
                            if (!selectedValidationModels.includes('langextract')) {
                              setSelectedValidationModels(['langextract']);
                            }
                          }}
                          className="sr-only"
                        />
                        좌표
                      </label>
                    </div>
                    <div className="text-[9px] text-gray-400 mt-1">
                      {ocrProcessingMode === 'speed'
                        ? 'PDF 직접 처리, 빠른 추출'
                        : 'Bbox 좌표 포함, 검증용'}
                    </div>

                    {/* 🔧 청크 디버그 토글 삭제됨 */}
                  </div>

                  {/* 🔧 템플릿 선택 드롭다운 (수동 선택) */}
                  <div className="mb-2">
                    <select
                      value={selectedOcrTemplate}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedOcrTemplate(value);

                        // 🧙 자동 분류 선택 + 파일이 있으면 마법사 열기
                        if (value === '' && fileList.length > 0) {
                          // 첫 번째 선택된 파일 또는 첫 파일의 미리보기
                          const targetFile = selectedFileIndex !== null
                            ? fileList[selectedFileIndex]
                            : fileList[0];
                          if (targetFile?.preview) {
                            setWizardFirstPage(targetFile.preview);
                            setShowDocumentWizard(true);
                          }
                        }
                      }}
                      className="w-full px-3 py-2 text-sm border rounded bg-white"
                      style={{ borderColor: selectedOcrTemplate ? '#339AF0' : '#E0E0E0' }}
                    >
                      <option value="">🧙 자동 분류 (마법사)</option>
                      {/* 카테고리별로 그룹화된 템플릿 목록 */}
                      {(() => {
                        const templateList = Object.values(templates);
                        // 카테고리별로 그룹화
                        const grouped = templateList.reduce((acc, tpl) => {
                          const cat = tpl.category || '기타';
                          if (!acc[cat]) acc[cat] = [];
                          acc[cat].push(tpl);
                          return acc;
                        }, {});

                        // 카테고리 순서 정의
                        const categoryOrder = ['공통 (GENERAL)', '선적서류', '중고차', '거래처', '일반서류', '기타'];

                        // 정렬된 카테고리 순회
                        const sortedCategories = Object.keys(grouped).sort((a, b) => {
                          const aIdx = categoryOrder.indexOf(a);
                          const bIdx = categoryOrder.indexOf(b);
                          return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
                        });

                        return sortedCategories.map(cat => (
                          <optgroup key={cat} label={`${cat} (${grouped[cat].length})`}>
                            {grouped[cat].map(tpl => (
                              <option key={tpl.id} value={tpl.name}>{tpl.name}</option>
                            ))}
                          </optgroup>
                        ));
                      })()}
                    </select>
                    {selectedOcrTemplate && (
                      <div className="text-[10px] text-blue-600 mt-1 text-center">
                        📋 "{selectedOcrTemplate}" 템플릿으로 추출
                      </div>
                    )}
                  </div>

                  {/* 문서 추출 시작/취소 버튼 (Google AI Studio 스타일) */}
                  {loading ? (
                    <button
                      onClick={handleCancelProcessing}
                      className="w-full px-4 py-3 text-sm font-medium rounded transition flex items-center justify-center gap-2"
                      style={{
                        backgroundColor: '#5F6368',
                        color: '#FFFFFF',
                        border: '1px solid #3C4043',
                        cursor: 'pointer'
                      }}
                      title="처리를 취소합니다"
                    >
                      <X className="w-4 h-4" /> {ocrPipelineStep || '처리 취소'}
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      {/* 문서 추출 버튼 (통합: OCR + 추출) */}
                      <button
                        onClick={async () => {
                          // 체크된 파일 우선, 없으면 현재 선택된 파일
                          const filesToProcess = fileList.filter(f => (f.checked || f.isChecked));
                          const targetFiles = filesToProcess.length > 0
                            ? filesToProcess
                            : (selectedFileIndex !== null ? [fileList[selectedFileIndex]] : []);

                          if (targetFiles.length === 0) {
                            addLog('⚠️ 처리할 파일을 선택하세요', 'warning');
                            return;
                          }

                          const startTime = Date.now();
                          addLog(`🚀 문서 추출 시작: ${targetFiles.length}개 파일`);

                          // ⭐ 왼쪽 패널 자동 닫힘 (시인성 향상)
                          if (autoCollapseOnExtract) {
                            setIsLeftPanelCollapsed(true);
                          }

                          // ⭐ 총 페이지 수 계산 (PDF는 totalPages 사용, 이미지는 1페이지)
                          const calculateTotalPages = () => {
                            return targetFiles.reduce((sum, file) => {
                              if (file.isPDF || file.file?.type === 'application/pdf') {
                                return sum + (file.totalPages || 3); // PDF 기본 3페이지 추정
                              }
                              return sum + 1; // 이미지는 1페이지
                            }, 0);
                          };
                          const totalPagesCount = calculateTotalPages();
                          const estimatedTime = totalPagesCount * 6; // 페이지당 ~6초
                          const progressStartTime = Date.now();

                          // ⭐ 진행률 초기화
                          setProcessingProgress({
                            currentPage: 0,
                            totalPages: totalPagesCount,
                            step: 'OCR 처리 중...',
                            fileName: targetFiles[0]?.name || '',
                            percentage: 0,
                            startTime: progressStartTime,
                            estimatedSeconds: estimatedTime
                          });

                          // ⭐ 시간 기반 점진적 진행률 업데이트 (0% → 90%)
                          const progressInterval = setInterval(() => {
                            const elapsed = (Date.now() - progressStartTime) / 1000;
                            // 예상 시간 대비 경과 비율 (최대 90%까지만)
                            const progressPercent = Math.min(90, Math.round((elapsed / estimatedTime) * 100));
                            const currentPage = Math.min(totalPagesCount, Math.ceil((progressPercent / 100) * totalPagesCount));
                            setProcessingProgress(prev => ({
                              ...prev,
                              currentPage: currentPage,
                              percentage: progressPercent
                            }));
                          }, 500); // 0.5초마다 업데이트

                          // 🔥 병렬 처리 + 결과 수집 (returnResult=true)
                          const results = await Promise.allSettled(
                            targetFiles.map((file, i) => {
                              addLog(`📄 [${i + 1}/${targetFiles.length}] ${file.name} 처리 시작`);
                              return handleOcrPipeline(file, true);  // returnResult=true
                            })
                          );

                          // ⭐ 인터벌 정지
                          clearInterval(progressInterval);

                          // 🎯 결과 필터링 (성공한 것만)
                          const successResults = results
                            .filter(r => r.status === 'fulfilled' && r.value)
                            .map(r => r.value);
                          const failCount = results.filter(r => r.status === 'rejected' || !r.value).length;

                          console.log('📦 [문서 추출] 결과 수집 완료:', successResults.length, '개 성공');
                          // 🔍 디버그: 각 결과의 boundingBoxes 확인
                          successResults.forEach((r, i) => {
                            console.log(`📋 [결과 ${i + 1}] fileId=${r.fileId}, labels=${r.updatedLabels?.length || 0}, isPDF=${r.isPDF}`);
                          });

                          // 🔥 일괄 적용: 모든 결과를 한 번에 setFileList에 적용
                          if (successResults.length > 0) {
                            setFileList(prev => prev.map(f => {
                              // 이 파일에 해당하는 OCR 결과 찾기
                              const result = successResults.find(r => r.fileId === f.id);
                              if (!result) return f;

                              if (f.isPDF || result.isPDF) {
                                // PDF 파일: pageResults 업데이트

                                // 🔧 PDF 네이티브 모드: pageResults가 이미 올바른 형태로 제공됨
                                if (result.mode === 'pdf-native' && result.pageResults && result.pageResults.length > 0) {
                                  console.log('📄 [일괄처리] PDF 네이티브 모드 저장:', result.pageResults.length, '페이지');

                                  // PDF 네이티브 결과 구조: { pageNumber, CBM, 길 이, ... }
                                  // pageResults를 그대로 사용
                                  const formattedPageResults = result.pageResults.map(pr => ({
                                    ...pr,
                                    pageNumber: pr.pageNumber || pr.P,
                                    // 폼 뷰 호환을 위해 extractedData, flashData, proData 복사
                                    extractedData: pr,
                                    flashData: pr,
                                    proData: pr
                                  }));

                                  return {
                                    ...f,
                                    status: 'completed',
                                    pageResults: formattedPageResults,
                                    totalPages: result.totalPages || formattedPageResults.length,
                                    // extractedData는 첫 페이지 데이터로 설정 (폼 뷰 호환)
                                    extractedData: result.finalResult,
                                    flashData: result.finalResult,
                                    proData: result.finalResult,
                                    groundedFields: result.finalResult?.groundedFields || result.groundedFields || {}, // ⭐ [Grounding]
                                    pageDimensions: result.finalResult?.pageDimensions || result.pageDimensions || {} // ⭐ [Grounding]
                                  };
                                }

                                // 🔍 배치 처리에서 첫 번째 PDF의 청크 디버그 표시
                                const firstPdfResult = successResults.find(r => r.mode === 'pdf-native' && r.chunkDetails);
                                if (showChunkDebug && firstPdfResult?.chunkDetails && !showChunkDebugModal) {
                                  setChunkDebugData({
                                    chunkDetails: firstPdfResult.chunkDetails,
                                    referenceExample: firstPdfResult.referenceExample,
                                    totalPages: firstPdfResult.totalPages || 0
                                  });
                                  setShowChunkDebugModal(true);
                                }

                                // ⭐ 멀티페이지 PDF (PaddleOCR): paddlePageResults 사용
                                if (result.multiPage && result.paddlePageResults && result.paddlePageResults.length > 1 && result.mode !== 'pdf-native') {
                                  console.log('📄 [일괄처리] 멀티페이지 PDF 저장 (PaddleOCR):', result.totalPages, '페이지');

                                  const allPageResults = result.paddlePageResults.map((pr, prIdx) => {
                                    const pageNum = pr.pageNumber || (prIdx + 1);

                                    // ⭐ 해당 페이지의 LLM 추출 결과 찾기
                                    const pageExtraction = result.allPageExtractedData?.find(p => p.pageNumber === pageNum);
                                    const pageData = pageExtraction?.data || null;

                                    return {
                                      pageNumber: pageNum,
                                      ocrImageSize: pr.ocrImageSize,
                                      boundingBoxes: (pr.labels || []).map((label, labelIdx) => ({
                                        id: label.id || `ocr-page${pageNum}-${labelIdx}`,
                                        text: label.text || '',
                                        bbox: label.bbox,
                                        confidence: label.confidence,
                                        fieldKey: label.fieldKey || null,
                                        pageNumber: pageNum
                                      })),
                                      correctedImageUrl: pr.correctedImageUrl || null,
                                      pngImage: pr.correctedImageUrl || null,
                                      // ⭐ 각 페이지의 LLM 결과 저장 (fallback: 첫 페이지는 finalResult)
                                      flashData: pageData || (pageNum === 1 ? result.finalResult : null),
                                      proData: pageData || (pageNum === 1 ? result.finalResult : null),
                                      extractedData: pageData || (pageNum === 1 ? result.finalResult : null)
                                    };
                                  });

                                  return {
                                    ...f,
                                    status: 'completed',
                                    pageResults: allPageResults,
                                    totalPages: result.totalPages, // ⭐ totalPages 설정!
                                    mergedGroups: result.mergedGroups // 📄 머지된 그룹
                                  };
                                }

                                // 단일 페이지 PDF 처리
                                const pageNum = result.pageNumber || 1;
                                const existingPageResults = f.pageResults || [];

                                let pageResultUpdated = false;
                                const updatedPageResults = existingPageResults.map(pr => {
                                  if (pr.pageNumber === pageNum) {
                                    pageResultUpdated = true;
                                    return {
                                      ...pr,
                                      flashData: result.finalResult,
                                      proData: result.finalResult,
                                      extractedData: result.finalResult,
                                      boundingBoxes: result.updatedLabels,
                                      ocrImageSize: result.ocrImageSize,
                                      pngImage: result.correctedUrl || pr.pngImage
                                    };
                                  }
                                  return pr;
                                });

                                if (!pageResultUpdated) {
                                  updatedPageResults.push({
                                    pageNumber: pageNum,
                                    flashData: result.finalResult,
                                    proData: result.finalResult,
                                    extractedData: result.finalResult,
                                    boundingBoxes: result.updatedLabels,
                                    ocrImageSize: result.ocrImageSize,
                                    pngImage: result.correctedUrl || null
                                  });
                                }

                                return {
                                  ...f,
                                  status: 'completed',
                                  pageResults: updatedPageResults,
                                  totalPages: result.totalPages || f.totalPages || updatedPageResults.length
                                };
                              } else {
                                // 이미지 파일
                                return {
                                  ...f,
                                  status: 'completed',
                                  extractedData: result.finalResult,
                                  flashData: result.finalResult,
                                  proData: result.finalResult,
                                  boundingBoxes: result.updatedLabels,
                                  ocrImageSize: result.ocrImageSize
                                };
                              }
                            }));
                          }

                          const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
                          const completionMessage = `✅ 추출 완료: ${successResults.length}개 성공${failCount > 0 ? `, ${failCount}개 실패` : ''} (${elapsed}초)`;
                          addLog(completionMessage);

                          // ⭐ 진행률 완료 상태로 업데이트 후 리셋
                          setProcessingProgress(prev => ({
                            ...prev,
                            currentPage: prev.totalPages,
                            step: '완료!',
                            percentage: 100
                          }));

                          // ⭐ 완료 메시지를 하단 바에 표시
                          setLastCompletionLog(completionMessage);

                          // 🔧 문서 추출 완료 후 패널 비율 3.5:6.5 (PDF:그리드) 적용
                          // 가로 모드로 전환하고 적절한 비율 설정
                          setTimeout(() => {
                            // 현재 전체 너비 기준 6.5 비율의 rightPanelWidth 계산
                            const containerWidth = window.innerWidth - 230; // 왼쪽 패널 제외
                            const newRightPanelWidth = Math.floor(containerWidth * 0.65); // 65% = 6.5 비율
                            setRightPanelWidth(Math.max(600, newRightPanelWidth)); // 최소 600px

                            console.log('📐 [추출완료] 패널 비율 3.5:6.5 적용, rightPanelWidth:', newRightPanelWidth);

                            // 🔧 DOM 업데이트 후 AUIGrid 리사이즈 (2단계)
                            setTimeout(() => {
                              if (window.AUIGrid && window.AUIGrid.isCreated('#horizontal_grid')) {
                                window.AUIGrid.resize('#horizontal_grid');
                                console.log('📐 [추출완료] AUIGrid 1차 리사이즈');

                                // 🔧 한번 더 리사이즈 (React 렌더링 완료 보장)
                                setTimeout(() => {
                                  window.AUIGrid.resize('#horizontal_grid');
                                  console.log('📐 [추출완료] AUIGrid 2차 리사이즈');
                                }, 200);
                              }
                            }, 150);
                          }, 300);

                          // 1초 후 진행률 리셋
                          setTimeout(() => {
                            setProcessingProgress({ currentPage: 0, totalPages: 0, step: '', fileName: '', percentage: 0, startTime: null, estimatedSeconds: 0 });
                          }, 1000);

                          // 5초 후 완료 메시지 숨김
                          setTimeout(() => {
                            setLastCompletionLog(null);
                          }, 5000);

                          // 🔧 문서검증 탭으로 자동 전환 비활성화
                          // setActiveTab('grid');
                        }}
                        disabled={checkedCount === 0 && selectedFileIndex === null}
                        className="w-full px-4 py-3 text-sm font-bold rounded transition flex items-center justify-center gap-2"
                        style={{
                          backgroundColor: (checkedCount > 0 || selectedFileIndex !== null) ? '#1E40AF' : '#F4F4F4',
                          color: (checkedCount > 0 || selectedFileIndex !== null) ? '#FFFFFF' : '#808080',
                          cursor: (checkedCount > 0 || selectedFileIndex !== null) ? 'pointer' : 'not-allowed'
                        }}
                        title="선택한 파일의 문서 추출을 시작합니다 (PaddleOCR → AI 분석)"
                      >
                        <Scan className="w-4 h-4" />
                        {checkedCount > 0 ? `문서 추출 (${checkedCount})` : '문서 추출'}
                      </button>
                    </div>
                  )}

                  {/* 자동 추출 시작 토글 */}
                  <div className="flex items-center justify-center mt-2 gap-4">
                    <label className="flex items-center space-x-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={isAutoExtractionEnabled}
                        onChange={(e) => setIsAutoExtractionEnabled(e.target.checked)}
                        className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-xs text-gray-600 font-medium">자동 추출</span>
                    </label>
                    {/* ⭐ 추출 시 패널 자동 닫힘 옵션 */}
                    <label className="flex items-center space-x-2 cursor-pointer select-none" title="문서 추출 시 왼쪽 패널을 자동으로 닫아 시인성을 높입니다">
                      <input
                        type="checkbox"
                        checked={autoCollapseOnExtract}
                        onChange={(e) => {
                          setAutoCollapseOnExtract(e.target.checked);
                          localStorage.setItem('autoCollapseOnExtract', e.target.checked.toString());
                        }}
                        className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-xs text-gray-600 font-medium">추출 시 패널 닫힘</span>
                    </label>
                  </div>

                  {/* 파일 목록 헤더 + 전체 체크박스 */}
                  {fileList.length > 0 && (
                    <div className="pt-2 mt-2" style={{ borderTop: '1px solid #F4F4F4' }}>
                      <div className="flex items-center justify-between px-1">
                        <h2 className="text-xs font-semibold flex items-center gap-1.5" style={{ color: '#0A1037' }}>
                          <File className="w-3.5 h-3.5" />
                          파일 목록 ({fileList.length})
                        </h2>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={fileList.length > 0 && fileList.every(f => f.checked)}
                            onChange={toggleAllChecks}
                            className="w-3.5 h-3.5 rounded"
                            style={{ accentColor: '#0A1037' }}
                          />
                          <span className="text-[10px]" style={{ color: '#0A1037' }}>전체</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 중간: 파일 리스트 - 스크롤 영역 (전체가 드롭존) */}
              <div
                className="flex-1 overflow-y-auto relative"
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                {/* Gmail 스타일 드래그 오버레이 */}
                {isDragging && (
                  <div className="absolute inset-0 z-50 bg-blue-50 bg-opacity-95 border-2 border-dashed border-blue-400 rounded-lg flex flex-col items-center justify-center pointer-events-none">
                    <Upload className="w-16 h-16 text-blue-500 mb-3 animate-bounce" />
                    <p className="text-lg font-semibold text-blue-600">파일을 여기에 놓으세요</p>
                    <p className="text-sm text-blue-500 mt-1">이미지 또는 PDF 파일</p>
                  </div>
                )}

                {/* 숨겨진 파일 입력 */}
                <input
                  type="file"
                  id="main-file-upload"
                  className="hidden"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={handleMultipleFileUpload}
                  disabled={loading}
                />

                {fileList.length === 0 ? (
                  <div
                    onClick={() => document.getElementById('main-file-upload').click()}
                    className="h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-all"
                  >
                    <Upload className="w-12 h-12 mb-3 text-gray-300" />
                    <p className="text-sm text-gray-500">파일을 업로드해주세요</p>
                    <p className="text-xs text-gray-400 mt-1">클릭하거나 파일을 드래그하세요</p>
                  </div>
                ) : (
                  <>
                    <div className="divide-y divide-gray-200">
                      {fileList.map((file, index) => (
                        <div
                          key={file.id}
                          className={`p-2 transition ${selectedFileIndex === index
                            ? 'bg-gray-100 border-l-[3px] border-gray-900'
                            : 'hover:bg-gray-50'
                            }`}
                        >
                          <div className="flex items-start gap-2">
                            {/* 체크박스 */}
                            <input
                              type="checkbox"
                              checked={file.checked}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleFileCheck(index);
                              }}
                              className="mt-0.5 w-3.5 h-3.5 text-gray-900 rounded cursor-pointer"
                            />

                            {/* 파일 정보 */}
                            <div
                              className="flex-1 min-w-0 cursor-pointer"
                              onClick={() => {
                                setSelectedFileIndex(index);
                                const currentlyChecked = !!fileList[index].checked;
                                setFileList(prevList => prevList.map((f, i) => {
                                  // 클릭한 아이템은 토글하고,
                                  if (i === index) {
                                    return { ...f, checked: !currentlyChecked };
                                  }
                                  // 다른 모든 아이템은 체크 해제합니다. (단일 선택)
                                  return { ...f, checked: false };
                                }));
                              }}
                            >
                              <p className="text-xs font-medium text-gray-900 truncate leading-tight">
                                {file.name}
                              </p>
                              <div className="mt-0.5 flex items-center gap-1.5">
                                <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded ${getStatusBadge(file.status).color
                                  }`}>
                                  {getStatusBadge(file.status).text}
                                </span>

                                {/* 일치율 표시 */}
                                {file.extractedData?.metadata?.matchRate !== undefined && (
                                  <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded font-semibold ${file.extractedData.metadata.matchRate === 100
                                    ? 'bg-green-100 text-green-800 border border-green-300'
                                    : file.extractedData.metadata.matchRate >= 90
                                      ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                                      : 'bg-red-100 text-red-800 border border-red-300'
                                    }`}>
                                    일치율: {file.extractedData.metadata.matchRate}%
                                  </span>
                                )}
                              </div>
                              {file.error && (
                                <p className="mt-0.5 text-[10px] text-red-600 truncate leading-tight">
                                  {file.error}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFile(index);
                              }}
                              className="p-0.5 text-gray-400 hover:text-red-600 transition"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))
                      }
                    </div>

                    {/* 파일 추가 가이드 (파일 목록 하단) */}
                    <div
                      onClick={() => document.getElementById('main-file-upload').click()}
                      className="py-3 px-4 border-t border-dashed border-gray-300 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-all flex items-center justify-center gap-2 text-gray-500 hover:text-gray-700"
                    >
                      <Upload className="w-4 h-4" />
                      <span className="text-xs">클릭하거나 파일을 드래그하여 추가</span>
                    </div>
                  </>
                )}
              </div>

              {/* 하단: 진행 상황 로그 */}
              {showLogs && (processingLogs.length > 0 || loading) && (
                <div className={`bg-white flex flex-col transition-all duration-300`} style={{ height: '280px', borderTop: '1px solid #E5E5E5' }}>
                  <div className={`px-3 py-2 flex items-center justify-between border-b border-gray-200`}>
                    <span className="text-xs font-medium flex items-center gap-1.5" style={{ color: '#222428' }}>
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2" style={{ borderColor: '#222428' }}></div>
                          <span>처리중... ({processingLogs.length})</span>
                        </>
                      ) : (
                        <span>처리 로그 ({processingLogs.length})</span>
                      )}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setProcessingLogs([])}
                        className="text-[10px] px-2 py-1 font-medium transition-colors hover:text-gray-600"
                        style={{ color: '#222428' }}
                      >
                        지우기
                      </button>
                      <button
                        onClick={() => setShowLogs(false)}
                        className="text-[10px] px-2 py-1 font-medium transition-colors hover:text-gray-600"
                        style={{ color: '#222428' }}
                      >
                        숨기기
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto px-2 py-1.5 space-y-0.5">
                    {processingLogs.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-gray-400">
                        <div className="text-center">
                          <div className="text-sm">처리 대기 중...</div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {processingLogs.map((log, idx) => (
                          <div key={idx} className="text-[11px] flex items-start gap-2 px-1.5 py-0.5 rounded hover:bg-gray-100 transition-colors">
                            <span className="text-gray-500 flex-shrink-0 font-mono text-[10px]">{log.timestamp}</span>
                            <span className={`flex-1 leading-tight ${log.type === 'error' ? 'text-red-600 font-semibold' :
                              log.type === 'success' ? 'text-green-600 font-semibold' :
                                'text-gray-800'
                              }`}>
                              {log.message}
                            </span>
                          </div>
                        ))}
                        <div ref={logsEndRef} />
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* 로그 표시 버튼 (숨겨진 상태일 때) */}
              {!showLogs && (processingLogs.length > 0 || loading) && (
                <div className="p-2 bg-white" style={{ borderTop: '1px solid #E5E5E5' }}>
                  <button
                    onClick={() => setShowLogs(true)}
                    className="w-full text-[10px] px-2 py-1.5 font-medium transition-all flex items-center justify-center gap-2 hover:text-gray-600"
                    style={{ color: '#222428' }}
                  >
                    {loading && <div className="animate-spin rounded-full h-3 w-3 border-b-2" style={{ borderColor: '#222428' }}></div>}
                    {loading ? '처리중...' : '처리 로그 보기'} ({processingLogs.length})
                  </button>
                </div>
              )}
            </div>
          )}



          {/* 왼쪽 패널 리사이저 (레거시 거래처 등록 시 숨김) */}
          {activeTab !== 'legacy-registration' && !isLeftPanelCollapsed ? (
            <div
              className="relative w-1 cursor-col-resize flex-shrink-0 group"
              onMouseDown={(e) => {
                // ⭐ 버튼 클릭인 경우 리사이징 방지
                const target = e.target;
                const isButton = target.closest('button');
                if (isButton) {
                  return; // 버튼 클릭은 리사이징 처리하지 않음
                }

                e.preventDefault();
                e.stopPropagation();

                const startX = e.clientX;
                const startWidth = leftPanelWidth;
                const panelElement = leftPanelRef.current;

                if (!panelElement) return;

                // 드래그 중 선택 방지
                document.body.style.userSelect = 'none';
                document.body.style.cursor = 'col-resize';
                document.body.style.pointerEvents = 'none';
                if (panelElement) {
                  panelElement.style.pointerEvents = 'auto';
                }

                // ⭐ requestAnimationFrame으로 매끄러운 업데이트
                const handleMouseMove = (moveEvent) => {
                  moveEvent.preventDefault();

                  // 기존 애니메이션 프레임 취소
                  if (resizeAnimationFrameRef.current) {
                    cancelAnimationFrame(resizeAnimationFrameRef.current);
                  }

                  // 즉시 DOM에 반영 (상태 업데이트 없이)
                  resizeAnimationFrameRef.current = requestAnimationFrame(() => {
                    const delta = moveEvent.clientX - startX;
                    const newWidth = Math.max(200, Math.min(600, startWidth + delta));

                    // DOM에 직접 적용 (즉시 반응)
                    if (panelElement) {
                      panelElement.style.width = `${newWidth}px`;
                    }
                  });
                };

                const handleMouseUp = () => {
                  // 마지막 상태 업데이트 (상태 동기화)
                  if (panelElement) {
                    const finalWidth = parseInt(panelElement.style.width) || leftPanelWidth;
                    setLeftPanelWidth(finalWidth);
                  }

                  // 🔧 AUIGrid 리사이즈 (컨테이너 크기에 맞게)
                  setTimeout(() => {
                    if (window.AUIGrid && window.AUIGrid.isCreated('#horizontal_grid')) {
                      window.AUIGrid.resize('#horizontal_grid');
                      console.log('📐 [Left Splitter] AUIGrid resize');
                    }
                  }, 100);

                  // 이벤트 리스너 제거
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);

                  // 애니메이션 프레임 취소
                  if (resizeAnimationFrameRef.current) {
                    cancelAnimationFrame(resizeAnimationFrameRef.current);
                    resizeAnimationFrameRef.current = null;
                  }

                  // 드래그 종료 후 스타일 복원
                  document.body.style.userSelect = '';
                  document.body.style.cursor = '';
                  document.body.style.pointerEvents = '';
                };

                // ⭐ passive: false로 즉시 반응
                document.addEventListener('mousemove', handleMouseMove, { passive: false });
                document.addEventListener('mouseup', handleMouseUp, { passive: false });
              }}
              title="드래그하여 크기 조절"
            >
              {/* 🎨 세로 구분선 (Google AI Studio Style) */}
              <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[3px] bg-gray-400 opacity-50 group-hover:bg-blue-500 group-hover:opacity-100 transition-all rounded-full" />

              {/* 토글 버튼 (위아래 2개) */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1.5 z-40">
                {/* 파일 목록 접기/펴기 버튼 */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsLeftPanelCollapsed(!isLeftPanelCollapsed);
                  }}
                  style={{ pointerEvents: 'auto' }}
                  className="w-5 h-8 bg-gradient-to-r from-gray-50 via-white to-gray-50 border border-gray-300 rounded-full flex items-center justify-center text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:shadow-md shadow-sm transition-all hover:scale-105 cursor-pointer"
                  title="파일 목록 접기/펴기"
                >
                  {isLeftPanelCollapsed ? (
                    <ChevronRight size={14} strokeWidth={2} />
                  ) : (
                    <ChevronLeft size={14} strokeWidth={2} />
                  )}
                </button>
              </div>
            </div>

          ) : (
            /* 패널이 닫혀있을 때 토글 버튼 - 레거시 모드에서는 숨김 */
            activeTab !== 'legacy-registration' && (
              <div className="relative w-[6px] flex-shrink-0 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 hover:from-blue-50 hover:via-blue-100 hover:to-blue-50 transition-all">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1.5 z-40">
                  {/* 파일 목록 접기/펴기 버튼 */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setIsLeftPanelCollapsed(!isLeftPanelCollapsed);
                    }}
                    style={{ pointerEvents: 'auto' }}
                    className="w-5 h-8 bg-gradient-to-r from-gray-50 via-white to-gray-50 border border-gray-300 rounded-full flex items-center justify-center text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:shadow-md shadow-sm transition-all hover:scale-105 cursor-pointer"
                    title="파일 목록 접기/펴기"
                  >
                    {isLeftPanelCollapsed ? (
                      <ChevronRight size={14} strokeWidth={2} />
                    ) : (
                      <ChevronLeft size={14} strokeWidth={2} />
                    )}
                  </button>
                </div>
              </div>
            )
          )
          }


          {/* ✏️ 메인 콘텐츠 + AI 패널 Wrapper */}
          <div className="flex-1 flex flex-row overflow-hidden">
            {/* PDF 뷰어 + 오른쪽 패널 Wrapper (layoutMode에 따라 flex 방향 변경) */}
            <div className={`flex-1 flex ${layoutMode === 'horizontal' ? 'flex-col' : 'flex-row'} overflow-hidden`}>

              {/* 가운데 패널: 문서 뷰어 또는 검증 결과 그리드 */}
              <div
                className={`${isCenterPanelCollapsed ? 'w-0' : layoutMode === 'horizontal' ? 'h-[60%]' : 'flex-1'} flex flex-col relative transition-all duration-300 overflow-hidden`}
                style={{ backgroundColor: '#F4F4F4' }}
              >
                {/* ⭐ 하단 고정 심플 프로그레스바 (처리 중일 때만 표시) */}
                {loading && processingProgress.totalPages > 0 && (
                  <div className="absolute bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg px-4 py-3">
                    <div className="flex items-center gap-4">
                      {/* 스피너 */}
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent flex-shrink-0"></div>

                      {/* 프로그레스바 */}
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-gray-600 truncate max-w-[200px]" title={processingProgress.fileName}>
                            📄 {processingProgress.fileName}
                          </span>
                          <span className="text-xs font-bold text-blue-600">
                            {processingProgress.percentage}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300 ease-out"
                            style={{ width: `${processingProgress.percentage}%` }}
                          />
                        </div>
                      </div>

                      {/* 진행률 정보 + 시간 */}
                      <div className="text-right flex-shrink-0" style={{ minWidth: '160px' }}>
                        <div className="text-xs font-semibold text-gray-700">
                          {processingProgress.currentPage}/{processingProgress.totalPages} 페이지
                        </div>
                        <div className="text-xs text-gray-500">
                          {/* 경과 시간 */}
                          경과: {processingProgress.startTime
                            ? Math.floor((Date.now() - processingProgress.startTime) / 1000)
                            : 0}초
                          {/* 예상 시간 */}
                          <span className="text-gray-400 ml-2">
                            / 예상 ~{processingProgress.estimatedSeconds}초
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ⭐ 완료 메시지 하단 바 (처리 완료 후 표시) */}
                {!loading && lastCompletionLog && (
                  <div className="absolute bottom-0 left-0 right-0 z-50 bg-green-50 border-t border-green-200 px-4 py-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-green-700 font-medium">{lastCompletionLog}</span>
                      <button
                        onClick={() => setLastCompletionLog(null)}
                        className="text-gray-400 hover:text-gray-600 text-sm px-2"
                        title="닫기"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
                {!isCenterPanelCollapsed && (
                  <>
                    {/* 탭 내용 영역 - 고정 레이아웃 (스크롤 제거) */}
                    <div className="flex-1 overflow-hidden flex flex-col">
                      {activeTab === 'viewer' && (
                        <div className="h-full flex flex-col">
                          {/* 🎯 템플릿/문서 탭 헤더 */}
                          <div className="flex items-center bg-white border-b border-gray-200 px-4 py-1 gap-1 flex-shrink-0">
                            <button
                              onClick={() => setCenterViewTab('template')}
                              className={`px-3 py-1.5 text-xs font-medium rounded-t transition flex items-center gap-1 ${centerViewTab === 'template'
                                ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                              <LayoutTemplate size={14} />
                              템플릿
                            </button>
                            <button
                              onClick={() => setCenterViewTab('document')}
                              className={`px-3 py-1.5 text-xs font-medium rounded-t transition flex items-center gap-1 ${centerViewTab === 'document'
                                ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                              <FileText size={14} />
                              문서
                            </button>
                            {/* OCR 문서 탭 및 선택된 템플릿 표시 삭제됨 */}
                          </div>

                          {/* 템플릿 탭 콘텐츠 */}
                          {centerViewTab === 'template' && (
                            <div className="flex-1 overflow-hidden p-6 flex flex-col">
                              {(() => {
                                // 선택된 템플릿 찾기
                                const selectedTemplate = Object.values(templates).find(t => t.name === selectedOcrTemplate);
                                const sampleImageUrl = selectedTemplate?.sampleImage
                                  ? `${API_BASE_URL}${selectedTemplate.sampleImage}`
                                  : null;

                                // 🎯 템플릿 관리에서 파일 업로드 시 우선 표시
                                // ⚠️ 중요: selectedFile은 derivedSelectedIndex를 사용하므로 항상 값이 있을 수 있음
                                // 따라서 직접 selectedFileIndex !== null을 체크해야 함
                                const showUploadedFile = showTemplateManagement && selectedFileIndex !== null && selectedFile;

                                // 디버그 로그
                                console.log('🎯 [템플릿 탭] 렌더링:', {
                                  showUploadedFile,
                                  selectedFileIndex,
                                  selectedFile: selectedFile?.name,
                                  selectedOcrTemplate,
                                  sampleImageUrl,
                                  showTemplateManagement
                                });

                                // 🎯 1순위: 템플릿 관리에서 업로드한 파일 표시
                                if (showUploadedFile) {
                                  const fileType = selectedFile.file?.type || '';
                                  const isPdf = fileType === 'application/pdf';

                                  return (
                                    <div className="bg-white rounded-lg shadow-sm border-2 border-blue-300 overflow-hidden relative flex-1 flex flex-col" style={{ minHeight: 0 }}>
                                      {isPdf ? (
                                        <div className="relative w-full h-full flex-1" style={{ minHeight: 0 }}>
                                          <PDFViewer
                                            file={selectedFile.preview}
                                            pageNumber={selectedPageNumber}
                                            onLoadSuccess={() => console.log('📋 템플릿 PDF 로드 완료')}
                                          />
                                        </div>
                                      ) : (
                                        <div className="relative w-full h-full flex-1 overflow-auto flex items-center justify-center p-4" style={{ minHeight: 0 }}>
                                          <img
                                            src={selectedFile.preview}
                                            alt={selectedFile.name}
                                            className="max-w-full max-h-full object-contain"
                                          />
                                        </div>
                                      )}
                                      <div className="absolute bottom-2 left-2 bg-orange-500 text-white px-2 py-1 rounded text-xs font-medium">
                                        📋 새 샘플 (저장 대기중)
                                      </div>
                                    </div>
                                  );
                                }

                                // 🎯 2순위: 템플릿의 sampleImage 표시
                                if (sampleImageUrl) {
                                  return (
                                    <div className="bg-white rounded-lg shadow-sm border-2 border-gray-300 overflow-hidden relative flex-1 flex flex-col" style={{ minHeight: 0 }}>
                                      <TemplateSampleViewer
                                        url={sampleImageUrl}
                                        templateName={selectedOcrTemplate}
                                      />
                                      <div className="absolute bottom-2 left-2 bg-blue-500 text-white px-2 py-1 rounded text-xs font-medium pointer-events-none">
                                        📋 {selectedOcrTemplate}
                                      </div>
                                    </div>
                                  );
                                }


                                // 템플릿 미선택 시 (sampleImage도 없고 uploadedFile도 없는 경우)
                                return (
                                  <div className="h-full flex items-center justify-center text-gray-500">
                                    <div className="text-center">
                                      <LayoutTemplate className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                                      <p className="font-medium">템플릿을 선택해주세요</p>
                                      <p className="text-sm mt-2">왼쪽 패널에서 템플릿을 선택하면<br />해당 템플릿의 샘플 문서가 표시됩니다</p>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}

                          {/* OCR 문서 탭 콘텐츠 */}
                          {centerViewTab === 'ocr' && (
                            <div className="flex-1 overflow-hidden p-6 flex flex-col relative" style={{ minHeight: 0 }}>
                              {selectedFile ? (
                                <div className="bg-white rounded-lg shadow-sm border-2 border-gray-300 overflow-hidden relative flex-1 flex flex-col" style={{ minHeight: 0 }}>
                                  <OCRLabelingViewer
                                    imageUrl={
                                      // ⭐ OCR PNG 이미지 우선 사용 (좌표 정확도)
                                      (() => {
                                        // ⭐ 수정: selectedPageNumber에 맞는 페이지 데이터 사용
                                        const currentPage = selectedFile.pageResults?.find(p => p.pageNumber === selectedPageNumber) || selectedFile.pageResults?.[0];
                                        const pngImg = currentPage?.pngImage;
                                        console.log('🔍 [App→OCRViewer] pngImage:', pngImg ? pngImg.substring(0, 50) + '...' : 'null', 'pageNumber:', selectedPageNumber);
                                        return pngImg || selectedFile.preview;
                                      })()
                                    }
                                    fileType={
                                      (() => {
                                        const currentPage = selectedFile.pageResults?.find(p => p.pageNumber === selectedPageNumber) || selectedFile.pageResults?.[0];
                                        return currentPage?.pngImage ? 'image/png' : (selectedFile.file?.type || '');
                                      })()
                                    }
                                    initialBoundingBoxes={
                                      (() => {
                                        const currentPage = selectedFile.pageResults?.find(p => p.pageNumber === selectedPageNumber) || selectedFile.pageResults?.[0];
                                        return currentPage?.boundingBoxes ||
                                          selectedFile.extractedData?.boundingBoxes ||
                                          selectedFile.flashResult?.data?.boundingBoxes ||
                                          [];
                                      })()
                                    }
                                    ocrImageSize={
                                      (() => {
                                        const currentPage = selectedFile.pageResults?.find(p => p.pageNumber === selectedPageNumber) || selectedFile.pageResults?.[0];
                                        return currentPage?.ocrImageSize ||
                                          selectedFile.extractedData?.ocrImageSize ||
                                          null;
                                      })()
                                    }
                                    isHybridMode={
                                      !!selectedFile.extractedData?.paddleRaw ||
                                      !!(selectedFile.pageResults && selectedFile.pageResults.some(p => p.paddleRaw)) ||
                                      !!(selectedFile.flashResult?.data?.paddleRaw)
                                    }
                                    onSave={(newBoxes) => {
                                      // Update local extracted data with edited boxes
                                      // Update fileList directly as extractedData is derived from it
                                      if (selectedFileIndex !== null) {
                                        setFileList(prev => prev.map((f, idx) =>
                                          idx === selectedFileIndex && f.extractedData ? {
                                            ...f,
                                            extractedData: {
                                              ...f.extractedData,
                                              boundingBoxes: newBoxes
                                            }
                                          } : f
                                        ));
                                      }
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="h-full flex items-center justify-center text-gray-500">
                                  <div className="text-center">
                                    <Scan className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                                    <p className="font-medium">문서를 선택해주세요</p>
                                    <p className="text-sm mt-2">왼쪽 패널에서 문서를 선택하고<br />OCR 추출을 실행하면 결과가 표시됩니다</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* 문서 탭 콘텐츠 (기존 PDF 뷰어 영역) */}
                          {centerViewTab === 'document' && (
                            <div className="flex-1 overflow-hidden p-6 flex flex-col">
                              {selectedFile ? (
                                <>
                                  {/* 바운딩 박스 토글 버튼 (PDF Canvas 또는 이미지) */}
                                  {((selectedValidationModels.includes('deepseek') && grounding) || (selectedValidationModels.includes('datalab-marker') && datalabBoundingBox)) && selectedFileWithBoundingBoxes?.boundingBoxes && selectedFileWithBoundingBoxes.boundingBoxes.length > 0 && (
                                    <div className="mb-3 flex items-center justify-between">
                                      <button
                                        onClick={() => setShowBoundingBoxes(!showBoundingBoxes)}
                                        className={`px-4 py-2 rounded-lg border-2 font-medium text-sm transition-all ${showBoundingBoxes
                                          ? 'bg-blue-50 border-blue-400 text-blue-700'
                                          : 'bg-gray-50 border-gray-300 text-gray-600'
                                          }`}
                                      >
                                        {showBoundingBoxes ? '✅' : '⬜'} 바운딩 박스 표시 ({selectedFileWithBoundingBoxes.boundingBoxes.length}개)
                                      </button>
                                      <span className="text-xs text-gray-500">
                                        💡 오른쪽 필드에 마우스를 올리면 해당 위치를 골드색으로 하이라이트합니다
                                      </span>
                                    </div>
                                  )}

                                  {/* 에러만 최소 표시 (로딩은 왼쪽 패널 로그에서 확인) */}
                                  {error && (
                                    <div className="mb-2">
                                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200 text-xs">
                                        ⚠️ {error}
                                      </span>
                                    </div>
                                  )}

                                  <div className="bg-white rounded-lg shadow-sm border-2 border-gray-300 overflow-hidden relative flex-1 flex flex-col" style={{ minHeight: 0 }}>
                                    {/* PDF 파일: iframe 뷰어 + Canvas 하이라이트 오버레이 */}
                                    {selectedFile.file.type === 'application/pdf' ? (
                                      <div className="relative w-full h-full flex-1 flex flex-col" style={{ minHeight: 0 }}>
                                        {/* 📄 멀티페이지 PDF 네비게이션 (PDF 뷰어용) */}

                                        {/* 네비게이션 바 삭제됨 - @react-pdf-viewer 내장 네비게이션 사용 */}

                                        {/* 📘 @react-pdf-viewer 기반 PDF 뷰어 + Grounding 하이라이트 */}
                                        <div className="flex-1 relative" style={{ minHeight: 0 }}>
                                          <ReactPDFViewer
                                            file={selectedFile.file}
                                            pageNumber={selectedPageNumber}
                                            pageJumpToken={pageJumpToken}
                                            onLoadSuccess={(info) => {
                                              console.log('📘 [ReactPDFViewer] 로드 완료:', info.numPages, '페이지');
                                              setTotalPdfPages(info.numPages);
                                            }}
                                            onPageChange={(page) => {
                                              setSelectedPageNumber(page);
                                            }}
                                            highlightedField={groundingHighlight?.fieldKey || highlightedField}
                                            groundingCoordinates={groundingHighlight?.coordinates || null}
                                            highlightText={groundingHighlight?.fieldValue || null}
                                            pdfDimensions={selectedFile.pageDimensions?.[`page_${selectedPageNumber - 1}`] || null}
                                          />
                                        </div>

                                        {/* Canvas 하이라이트 오버레이 */}
                                        {highlightedField && selectedFileWithBoundingBoxes?.boundingBoxes && selectedFileWithBoundingBoxes.boundingBoxes.length > 0 && (
                                          <FieldHighlightOverlay
                                            boundingBoxes={selectedFileWithBoundingBoxes.boundingBoxes}
                                            highlightedField={highlightedField}
                                            fieldValueMap={selectedFile.extractedData?.fields || {}}
                                            imageWidth={800} // PDF 기본 너비
                                            imageHeight={1131} // PDF 기본 높이 (A4 비율)
                                            visible={true}
                                            autoScroll={clickedField === highlightedField}
                                          />
                                        )}
                                      </div>
                                    ) : (
                                      <div className="relative w-full h-full flex-1 overflow-hidden flex flex-col" style={{ minHeight: 0 }}>
                                        {/* 📄 멀티페이지 PDF 네비게이션 */}
                                        {totalPdfPages > 1 && (
                                          <div className="flex items-center justify-center gap-2 py-2 px-4 bg-gray-100 border-b border-gray-300">
                                            <button
                                              onClick={() => handlePdfPageChange(currentPdfPage - 1)}
                                              disabled={currentPdfPage <= 1}
                                              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${currentPdfPage <= 1
                                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                : 'bg-blue-500 text-white hover:bg-blue-600'
                                                }`}
                                            >
                                              ◀ 이전
                                            </button>

                                            <div className="flex items-center gap-1">
                                              {Array.from({ length: totalPdfPages }, (_, i) => i + 1).map(pageNum => (
                                                <button
                                                  key={pageNum}
                                                  onClick={() => handlePdfPageChange(pageNum)}
                                                  className={`w-8 h-8 rounded text-sm font-medium transition-colors ${pageNum === currentPdfPage
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-white text-gray-700 hover:bg-blue-100 border border-gray-300'
                                                    }`}
                                                >
                                                  {pageNum}
                                                </button>
                                              ))}
                                            </div>

                                            <button
                                              onClick={() => handlePdfPageChange(currentPdfPage + 1)}
                                              disabled={currentPdfPage >= totalPdfPages}
                                              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${currentPdfPage >= totalPdfPages
                                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                : 'bg-blue-500 text-white hover:bg-blue-600'
                                                }`}
                                            >
                                              다음 ▶
                                            </button>

                                            <span className="ml-2 text-sm text-gray-600">
                                              {currentPdfPage} / {totalPdfPages} 페이지
                                            </span>
                                          </div>
                                        )}

                                        <div className="flex-1 overflow-hidden flex items-center justify-center">
                                          <img
                                            id={`image-viewer-${selectedFile.id}`}
                                            src={
                                              // 🎯 OCR 파이프라인 보정 이미지 우선
                                              correctedImageUrl
                                                ? correctedImageUrl
                                                // ⭐ 상세 모드 + PDF: PNG 이미지 사용, 그 외: 원본 프리뷰
                                                : (selectedFile.file.type === 'application/pdf' && showDetailMode && selectedFileWithBoundingBoxes?.pngImage)
                                                  ? selectedFileWithBoundingBoxes.pngImage
                                                  : selectedFile.preview
                                            }
                                            alt={selectedFile.name}
                                            className="max-w-full max-h-full object-contain"
                                            onLoad={(e) => {
                                              console.log(`🖼️ 이미지 로드: naturalWidth=${e.target.naturalWidth}, naturalHeight=${e.target.naturalHeight}`);

                                              // ⭐ 이미지 크기 정보 저장 (DeepSeek 바운딩 박스용)
                                              if (grounding && selectedFile.extractedData?.text) {
                                                const boundingBoxes = parseBoundingBoxes(selectedFile.extractedData.text);
                                                console.log(`📦 바운딩 박스 ${boundingBoxes.length}개 감지 (DeepSeek)`);

                                                // 파일 객체에 바운딩 박스 데이터 저장
                                                setFileList(prev => prev.map(f =>
                                                  f.id === selectedFile.id
                                                    ? { ...f, boundingBoxes, imageWidth: e.target.naturalWidth, imageHeight: e.target.naturalHeight }
                                                    : f
                                                ));
                                              }

                                              // ⭐ 이미지 파일 또는 상세 모드의 바운딩 박스 데이터가 있으면 이미지 크기 저장
                                              if (selectedFileWithBoundingBoxes?.boundingBoxes && selectedFileWithBoundingBoxes.boundingBoxes.length > 0) {
                                                // ⭐ 상세 모드 + PDF: 페이지별 이미지 크기 저장
                                                if (selectedFile.file.type === 'application/pdf' && showDetailMode) {
                                                  setFileList(prev => prev.map(f => {
                                                    if (f.id === selectedFile.id && f.pageResults) {
                                                      const updatedPageResults = f.pageResults.map(p =>
                                                        p.pageNumber === selectedPageNumber
                                                          ? { ...p, imageWidth: e.target.naturalWidth, imageHeight: e.target.naturalHeight }
                                                          : p
                                                      );
                                                      return { ...f, pageResults: updatedPageResults };
                                                    }
                                                    return f;
                                                  }));
                                                  console.log(`📦 [페이지 ${selectedPageNumber}] 이미지 크기 저장: ${e.target.naturalWidth}x${e.target.naturalHeight}`);
                                                } else {
                                                  // ⭐ 이미지 파일: 파일 레벨 이미지 크기 저장
                                                  setFileList(prev => prev.map(f => {
                                                    if (f.id === selectedFile.id) {
                                                      return { ...f, imageWidth: e.target.naturalWidth, imageHeight: e.target.naturalHeight };
                                                    }
                                                    return f;
                                                  }));
                                                  console.log(`📦 이미지 크기 저장: ${e.target.naturalWidth}x${e.target.naturalHeight}`);
                                                }
                                              }

                                              // 🎯 OCR 파이프라인: 보정 이미지 로드 완료 시 표시된 크기 저장
                                              if (ocrLabels.length > 0) {
                                                console.log(`🎯 [OCR Labels] 이미지 로드 완료: display=${e.target.width}x${e.target.height}, natural=${e.target.naturalWidth}x${e.target.naturalHeight}`);
                                                // 이미지 표시 크기 저장하여 SVG 리렌더링 유발
                                                setOcrImageSize(prev => ({
                                                  ...prev,
                                                  displayWidth: e.target.width,
                                                  displayHeight: e.target.height
                                                }));
                                              }
                                            }}
                                          />

                                          {/* 바운딩 박스 오버레이 (이미지 또는 상세 모드, DeepSeek/Datalab) */}
                                          {((selectedValidationModels.includes('deepseek') && grounding) || (selectedValidationModels.includes('datalab-marker') && datalabBoundingBox)) && showBoundingBoxes && selectedFileWithBoundingBoxes?.boundingBoxes && selectedFileWithBoundingBoxes.boundingBoxes.length > 0 && (
                                            <BoundingBoxOverlay
                                              boundingBoxes={scaleBoundingBoxes(
                                                selectedFileWithBoundingBoxes.boundingBoxes,
                                                selectedFileWithBoundingBoxes.imageWidth || 1,
                                                selectedFileWithBoundingBoxes.imageHeight || 1,
                                                document.getElementById(`image-viewer-${selectedFile.id}`)?.width || 1,
                                                document.getElementById(`image-viewer-${selectedFile.id}`)?.height || 1
                                              )}
                                              imageWidth={document.getElementById(`image-viewer-${selectedFile.id}`)?.width || 0}
                                              imageHeight={document.getElementById(`image-viewer-${selectedFile.id}`)?.height || 0}
                                              visible={showBoundingBoxes}
                                              color="#339AF0"
                                            />
                                          )}

                                          {/* ⭐ 필드별 하이라이트 오버레이 (호버 시) */}
                                          {highlightedField && selectedFileWithBoundingBoxes?.boundingBoxes && selectedFileWithBoundingBoxes.boundingBoxes.length > 0 && (
                                            <FieldHighlightOverlay
                                              boundingBoxes={scaleBoundingBoxes(
                                                selectedFileWithBoundingBoxes.boundingBoxes,
                                                selectedFileWithBoundingBoxes.imageWidth || 1,
                                                selectedFileWithBoundingBoxes.imageHeight || 1,
                                                document.getElementById(`image-viewer-${selectedFile.id}`)?.width || 1,
                                                document.getElementById(`image-viewer-${selectedFile.id}`)?.height || 1
                                              )}
                                              highlightedField={highlightedField}
                                              fieldValueMap={{
                                                [highlightedField]: highlightedFieldValue
                                              }}
                                              imageWidth={document.getElementById(`image-viewer-${selectedFile.id}`)?.width || 0}
                                              imageHeight={document.getElementById(`image-viewer-${selectedFile.id}`)?.height || 0}
                                              visible={true}
                                              autoScroll={clickedField === highlightedField}
                                            />
                                          )}

                                          {/* 🎯 OCR 파이프라인 Labels 오버레이 - OCRLabelingModal과 동일한 bbox 형식 */}
                                          {ocrLabels.length > 0 && ocrImageSize.displayWidth && ocrImageSize.displayHeight && (() => {
                                            // 📄 멀티페이지: 현재 페이지의 labels만 필터링
                                            const currentPageLabels = totalPdfPages > 1
                                              ? ocrLabels.filter(label => label.pageNumber === currentPdfPage)
                                              : ocrLabels;

                                            return currentPageLabels.length > 0 && (
                                              <svg
                                                style={{
                                                  position: 'absolute',
                                                  // 🎯 SVG를 이미지와 동일한 크기로 설정
                                                  width: ocrImageSize.displayWidth,
                                                  height: ocrImageSize.displayHeight,
                                                  // 🎯 이미지가 중앙 정렬되므로 SVG도 중앙 정렬
                                                  top: '50%',
                                                  left: '50%',
                                                  transform: 'translate(-50%, -50%)',
                                                  pointerEvents: 'none'
                                                }}
                                                // 🎯 viewBox를 사용하여 1:1 좌표 매칭 (OCRLabelingModal과 동일)
                                                viewBox={`0 0 ${ocrImageSize.width || ocrImageSize.displayWidth} ${ocrImageSize.height || ocrImageSize.displayHeight}`}
                                                preserveAspectRatio="xMidYMid slice"
                                              >
                                                {currentPageLabels.map((label, idx) => {
                                                  // bbox: {x, y, width, height} 형식 (OCRLabelingModal과 동일)
                                                  if (!label.bbox) return null;

                                                  // 🎯 1:1 좌표 사용 - viewBox가 스케일링 처리
                                                  const x = label.bbox.x;
                                                  const y = label.bbox.y;
                                                  const width = label.bbox.width;
                                                  const height = label.bbox.height;

                                                  // 색상: 녹색 점선 (라벨링 팝업과 동일)
                                                  const color = '#22C55E';

                                                  return (
                                                    <g key={idx}>
                                                      <rect
                                                        x={x}
                                                        y={y}
                                                        width={width}
                                                        height={height}
                                                        fill="none"
                                                        stroke={color}
                                                        strokeWidth={1.5}
                                                        strokeDasharray="4 2"
                                                        rx={2}
                                                      />
                                                      {/* 필드명 라벨 (있는 경우) */}
                                                      {label.fieldKey && (
                                                        <text
                                                          x={x}
                                                          y={y - 3}
                                                          fill={color}
                                                          fontSize="12"
                                                          fontWeight="bold"
                                                        >
                                                          {label.fieldKey}
                                                        </text>
                                                      )}
                                                    </g>
                                                  );
                                                })}
                                              </svg>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <div className="h-full flex items-center justify-center text-gray-500">
                                  <div className="text-center">
                                    <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                                    <p>파일을 선택해주세요</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          {/* PDF 뷰어 영역 종료 */}


                        </div>
                      )}

                      {/* 검증 결과 그리드 탭 */}
                      {activeTab === 'grid' && (
                        <BatchValidationGrid
                          fileList={fileList}
                          API_BASE_URL={API_BASE_URL}
                          templates={templates}
                          // ⭐ 왼쪽 패널 템플릿 선택을 그리드로 전달 (이름 → ID 변환)
                          selectedTemplateId={Object.values(templates).find(t => t.name === selectedOcrTemplate)?.id || ''}
                          onClose={() => setActiveTab('viewer')}
                          onRefresh={() => {
                            // 파일 리스트 새로고침 또는 상태 업데이트
                          }}
                          // ⭐ 그리드 행 선택 시 오른쪽 폼 연동
                          onRowSelect={(fileId, pageNumber, vehicle) => {
                            console.log('📋 그리드 행 선택:', { fileId, pageNumber, vehicle });
                            // 파일 선택
                            const fileIndex = fileList.findIndex(f => f.id === fileId);
                            if (fileIndex !== -1) {
                              setSelectedFileIndex(fileIndex);
                              // 페이지 선택 (PDF인 경우)
                              if (pageNumber) {
                                setSelectedPageNumber(pageNumber);
                                // 체크된 페이지도 업데이트
                                setCheckedPages([pageNumber]);
                              }
                            }
                          }}
                        />
                      )}


                      {/* 전체검증 그리드 (전체검증 후 표시) */}
                      {
                        showBatchValidationGrid && (
                          <BatchValidationGrid
                            fileList={fileList}
                            API_BASE_URL={API_BASE_URL}
                            templates={templates}
                            // ⭐ 왼쪽 패널 템플릿 선택을 그리드로 전달
                            selectedTemplateId={Object.values(templates).find(t => t.name === selectedOcrTemplate)?.id || ''}
                            onClose={() => setShowBatchValidationGrid(false)}
                            onRefresh={() => {
                              // 파일 리스트 새로고침 또는 상태 업데이트
                              setShowBatchValidationGrid(false);
                              setTimeout(() => setShowBatchValidationGrid(true), 100);
                            }}
                            // ⭐ 그리드 행 선택 시 오른쪽 폼 연동
                            onRowSelect={(fileId, pageNumber, vehicle) => {
                              console.log('📋 그리드 행 선택:', { fileId, pageNumber, vehicle });
                              // 파일 선택
                              const fileIndex = fileList.findIndex(f => f.id === fileId);
                              if (fileIndex !== -1) {
                                setSelectedFileIndex(fileIndex);
                                // 페이지 선택 (PDF인 경우)
                                if (pageNumber) {
                                  setSelectedPageNumber(pageNumber);
                                  // 체크된 페이지도 업데이트
                                  setCheckedPages([pageNumber]);
                                }
                              }
                            }}
                          />
                        )
                      }

                      {/* 차량등록매핑 탭 */}
                      {
                        activeTab === 'mapping' && !showBatchValidationGrid && (
                          <VehicleMatchingPanel
                            fileList={fileList}
                            apiBaseUrl={API_BASE_URL}
                            isRightPanelCollapsed={isRightPanelCollapsed}
                            isMainLeftPanelCollapsed={isLeftPanelCollapsed}
                            setIsMainLeftPanelCollapsed={setIsLeftPanelCollapsed}
                            isVehicleRegistrationPanelCollapsed={isVehicleRegistrationPanelCollapsed}
                            setIsVehicleRegistrationPanelCollapsed={setIsVehicleRegistrationPanelCollapsed}
                          />
                        )
                      }

                      {/* 🏛️ 레거시 거래처 등록 탭 */}
                      {
                        activeTab === 'legacy-registration' && (
                          <div className="flex-1 overflow-hidden bg-gray-50" style={{ height: 'calc(100vh - 64px)' }}>
                            <LegacyCustomerRegistration API_BASE_URL={API_BASE_URL} />
                          </div>
                        )
                      }

                      {/* 📎 톡클라우드 문서관리 탭 */}
                      {
                        activeTab === 'talkcloud' && (
                          <div className="flex-1 overflow-hidden bg-gray-50" style={{ height: 'calc(100vh - 64px)' }}>
                            <TalkCloudDocManager API_BASE_URL={API_BASE_URL} />
                          </div>
                        )
                      }

                      {/* OLD 차량등록매핑 탭 (백업) */}
                      {
                        activeTab === 'mapping_old' && (
                          <div className="h-full p-6 overflow-auto">
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                              {/* 필터 및 액션 */}
                              <div className="p-4 border-b border-gray-200 bg-gray-50">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        className="w-4 h-4 text-gray-900 rounded"
                                      />
                                      <span className="text-sm text-gray-700">매핑 완료만 표시</span>
                                    </label>
                                    <span className="text-sm text-gray-500">
                                      전체 {fileList.filter(f => f.extractedData?.documentType &&
                                        ['말소등록증', '자동차등록증', '폐차증', '자동차등록원부', '경락확인서'].includes(f.extractedData.documentType)).length}건 차량서류
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition font-medium">
                                      📥 엑셀 다운로드
                                    </button>
                                    <button className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition font-medium">
                                      🚗 선택 항목 차량등록
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {/* 파일 업로드 영역 (레거시 거래처 등록 시 숨김) */}
                              {activeTab !== 'legacy-registration' && fileList.length === 0 && (
                                <div className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer min-h-[400px]"
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    setIsDragging(true);
                                  }}
                                  onClick={() => document.getElementById('fileInput')?.click()}
                                >
                                  <Upload className="w-12 h-12 text-gray-400 mb-4" />
                                  <p className="text-gray-500 font-medium text-lg">파일을 업로드해주세요</p>
                                  <p className="text-gray-400 text-sm mt-1">클릭하거나 파일을 드래그하세요</p>
                                </div>
                              )}

                              {/* 매핑 리스트 */}
                              <div className="overflow-x-auto">
                                {(() => {
                                  // 차량 서류 5가지 타입 (정확한 문서 타입명 사용)
                                  const vehicleDocTypes = ['말소등록증', '자동차등록증', '폐차증', '자동차등록원부', '경락확인서'];

                                  // 1단계: 차량 서류만 필터링
                                  // ⭐ PDF의 경우 pageResults의 각 페이지를 개별 차량으로 처리
                                  const vehicleDocs = fileList.flatMap((file, index) => {
                                    // PDF인 경우: pageResults의 각 페이지를 개별 차량으로 처리
                                    if (file.pageResults && file.pageResults.length > 0) {
                                      return file.pageResults
                                        .map((pageResult, pageIdx) => {
                                          // ⭐ 데이터 우선순위: extractedData > proData > flashData
                                          // extractedData는 검증 후 수정된 최종 데이터
                                          const extractedData = pageResult.extractedData || {};
                                          const proData = pageResult.proData || {};
                                          const flashData = pageResult.flashData || {};

                                          // 필드별로 우선순위 적용하여 병합 (extractedData가 최우선)
                                          // ⭐ 명시적 필드 할당이 스프레드 연산자보다 우선하도록 순서 조정
                                          const mergedData = {
                                            // 1단계: flashData와 proData를 먼저 병합
                                            ...flashData,
                                            ...proData,
                                            // 2단계: extractedData로 덮어쓰기 (검증 후 수정된 값)
                                            ...extractedData,
                                            // 3단계: 개별 필드 명시적 우선순위 적용 (null/undefined/빈 문자열 처리)
                                            modelYear: extractedData.modelYear ?? proData.modelYear ?? flashData.modelYear ?? null,
                                            totalWeight: extractedData.totalWeight ?? proData.totalWeight ?? flashData.totalWeight ?? null,
                                            length: extractedData.length ?? proData.length ?? flashData.length ?? null,
                                            width: extractedData.width ?? proData.width ?? flashData.width ?? null,
                                            height: extractedData.height ?? proData.height ?? flashData.height ?? null,
                                            cbm: extractedData.cbm ?? proData.cbm ?? flashData.cbm ?? null
                                          };

                                          // ⭐ 디버깅 로그 제거 (무한 루프 방지)

                                          // ⭐ CBM 계산: 길이, 너비, 높이가 모두 있으면 CBM 계산
                                          // CBM = FLOOR(길이 × 너비 × 높이 ÷ 1,000,000,000, 1)
                                          if (!mergedData.cbm && mergedData.length && mergedData.width && mergedData.height) {
                                            const lengthNum = parseFloat(String(mergedData.length).replace(/[^0-9.]/g, ''));
                                            const widthNum = parseFloat(String(mergedData.width).replace(/[^0-9.]/g, ''));
                                            const heightNum = parseFloat(String(mergedData.height).replace(/[^0-9.]/g, ''));
                                            if (!isNaN(lengthNum) && !isNaN(widthNum) && !isNaN(heightNum)) {
                                              // CBM = INT(길이 × 너비 × 높이 ÷ 1,000,000,000)
                                              const cbmValue = (lengthNum * widthNum * heightNum) / 1000000000;
                                              mergedData.cbm = Math.floor(cbmValue).toString(); // 정수로 변환 (예: 13)
                                            }
                                          }

                                          if (!mergedData.documentType) return null;
                                          if (!vehicleDocTypes.includes(mergedData.documentType)) return null;

                                          return {
                                            ...file,
                                            fileIndex: index,
                                            pageNumber: pageResult.pageNumber,
                                            extractedData: mergedData, // ⭐ 병합된 데이터 사용
                                            proData: pageResult.proData, // ⭐ proData도 저장 (필드 값이 없을 경우 대체)
                                            flashData: pageResult.flashData, // ⭐ flashData도 저장 (필드 값이 없을 경우 대체)
                                            pageIndex: pageIdx
                                          };
                                        })
                                        .filter(Boolean); // null 제거
                                    }

                                    // 단일 이미지인 경우: file.extractedData 사용
                                    if (file.extractedData && vehicleDocTypes.includes(file.extractedData.documentType)) {
                                      const extractedData = file.extractedData || {};
                                      const proData = file.proResult?.data || {};
                                      const flashData = file.flashResult?.data || {};

                                      // 필드별로 우선순위 적용하여 병합
                                      const mergedData = {
                                        ...flashData,
                                        ...proData,
                                        ...extractedData,
                                        totalWeight: extractedData.totalWeight ?? proData.totalWeight ?? flashData.totalWeight,
                                        length: extractedData.length ?? proData.length ?? flashData.length,
                                        width: extractedData.width ?? proData.width ?? flashData.width,
                                        height: extractedData.height ?? proData.height ?? flashData.height,
                                        cbm: extractedData.cbm ?? proData.cbm ?? flashData.cbm
                                      };

                                      // ⭐ CBM 계산
                                      // CBM = FLOOR(길이 × 너비 × 높이 ÷ 1,000,000,000, 1)
                                      if (!mergedData.cbm && mergedData.length && mergedData.width && mergedData.height) {
                                        const lengthNum = parseFloat(String(mergedData.length).replace(/[^0-9.]/g, ''));
                                        const widthNum = parseFloat(String(mergedData.width).replace(/[^0-9.]/g, ''));
                                        const heightNum = parseFloat(String(mergedData.height).replace(/[^0-9.]/g, ''));
                                        if (!isNaN(lengthNum) && !isNaN(widthNum) && !isNaN(heightNum)) {
                                          // CBM = INT(길이 × 너비 × 높이 ÷ 1,000,000,000)
                                          const cbmValue = (lengthNum * widthNum * heightNum) / 1000000000;
                                          mergedData.cbm = Math.floor(cbmValue).toString(); // 정수로 변환 (예: 13)
                                        }
                                      }

                                      return [{
                                        ...file,
                                        fileIndex: index,
                                        pageNumber: 1,
                                        extractedData: mergedData,
                                        proData: file.proResult?.data,
                                        flashData: file.flashResult?.data
                                      }];
                                    }

                                    return [];
                                  });

                                  // 2단계: 이름별로 관련 서류 매핑 생성
                                  // ⭐ PDF의 경우 pageResults의 각 페이지를 개별 문서로 처리
                                  const relatedDocsMap = {};
                                  fileList.forEach((file, index) => {
                                    // PDF인 경우: pageResults의 각 페이지를 개별 문서로 처리
                                    if (file.pageResults && file.pageResults.length > 0) {
                                      file.pageResults.forEach((pageResult, pageIdx) => {
                                        // ⭐ 데이터 우선순위: extractedData > proData > flashData
                                        const data = pageResult.extractedData || pageResult.proData || pageResult.flashData || {};
                                        if (!data || !data.documentType) return;

                                        // 이름 키 생성
                                        const nameKey = data.ownerName || data.name || data.businessName;
                                        if (!nameKey) return;

                                        if (!relatedDocsMap[nameKey]) {
                                          relatedDocsMap[nameKey] = {
                                            identity: null,      // 신분증
                                            businessReg: null,   // 사업자등록증
                                            contract: null,      // 계약서
                                            taxInvoice: null     // 세금계산서
                                          };
                                        }

                                        // 관련 서류 분류
                                        if (data.documentType === '신분증') {
                                          relatedDocsMap[nameKey].identity = { ...data, fileIndex: index, fileName: file.name, pageNumber: pageResult.pageNumber };
                                        } else if (data.documentType === '사업자등록증') {
                                          relatedDocsMap[nameKey].businessReg = { ...data, fileIndex: index, fileName: file.name, pageNumber: pageResult.pageNumber };
                                        } else if (data.documentType === '자동차매매계약서') {
                                          relatedDocsMap[nameKey].contract = { ...data, fileIndex: index, fileName: file.name, pageNumber: pageResult.pageNumber };
                                        } else if (data.documentType === '세금계산서') {
                                          relatedDocsMap[nameKey].taxInvoice = { ...data, fileIndex: index, fileName: file.name, pageNumber: pageResult.pageNumber };
                                        }
                                      });
                                    } else {
                                      // 단일 이미지인 경우: file.extractedData 사용
                                      const data = file.extractedData;
                                      if (!data) return;

                                      // 이름 키 생성
                                      const nameKey = data.ownerName || data.name || data.businessName;
                                      if (!nameKey) return;

                                      if (!relatedDocsMap[nameKey]) {
                                        relatedDocsMap[nameKey] = {
                                          identity: null,      // 신분증
                                          businessReg: null,   // 사업자등록증
                                          contract: null,      // 계약서
                                          taxInvoice: null     // 세금계산서
                                        };
                                      }

                                      // 관련 서류 분류
                                      if (data.documentType === '신분증') {
                                        relatedDocsMap[nameKey].identity = { ...data, fileIndex: index, fileName: file.name };
                                      } else if (data.documentType === '사업자등록증') {
                                        relatedDocsMap[nameKey].businessReg = { ...data, fileIndex: index, fileName: file.name };
                                      } else if (data.documentType === '자동차매매계약서') {
                                        relatedDocsMap[nameKey].contract = { ...data, fileIndex: index, fileName: file.name };
                                      } else if (data.documentType === '세금계산서') {
                                        relatedDocsMap[nameKey].taxInvoice = { ...data, fileIndex: index, fileName: file.name };
                                      }
                                    }
                                  });

                                  // 3단계: 각 차량 서류에 관련 서류 매핑
                                  const mappedVehicles = vehicleDocs.map(vehicle => {
                                    // ⭐ 필드별 우선순위 적용: extractedData > proData > flashData
                                    // (vehicle.extractedData는 이미 병합된 데이터이지만, proData와 flashData도 보존)
                                    const extractedData = vehicle.extractedData || {};
                                    const proData = vehicle.proData || {};
                                    const flashData = vehicle.flashData || {};

                                    // 필드별로 우선순위 적용하여 병합 (extractedData가 최우선)
                                    // ⭐ 명시적 필드 할당이 스프레드 연산자보다 우선하도록 순서 조정
                                    const mergedData = {
                                      // 1단계: flashData와 proData를 먼저 병합
                                      ...flashData,
                                      ...proData,
                                      // 2단계: extractedData로 덮어쓰기 (검증 후 수정된 값)
                                      ...extractedData,
                                      // 3단계: 개별 필드 명시적 우선순위 적용 (null/undefined/빈 문자열 처리)
                                      modelYear: extractedData.modelYear ?? proData.modelYear ?? flashData.modelYear ?? null,
                                      totalWeight: extractedData.totalWeight ?? proData.totalWeight ?? flashData.totalWeight ?? null,
                                      length: extractedData.length ?? proData.length ?? flashData.length ?? null,
                                      width: extractedData.width ?? proData.width ?? flashData.width ?? null,
                                      height: extractedData.height ?? proData.height ?? flashData.height ?? null,
                                      cbm: extractedData.cbm ?? proData.cbm ?? flashData.cbm ?? null
                                    };

                                    // ⭐ CBM 계산: 길이, 너비, 높이가 모두 있으면 CBM 계산 (없는 경우에만)
                                    // CBM = FLOOR(길이 × 너비 × 높이 ÷ 1,000,000,000, 1)
                                    if (!mergedData.cbm && mergedData.length && mergedData.width && mergedData.height) {
                                      const lengthNum = parseFloat(String(mergedData.length).replace(/[^0-9.]/g, ''));
                                      const widthNum = parseFloat(String(mergedData.width).replace(/[^0-9.]/g, ''));
                                      const heightNum = parseFloat(String(mergedData.height).replace(/[^0-9.]/g, ''));
                                      if (!isNaN(lengthNum) && !isNaN(widthNum) && !isNaN(heightNum)) {
                                        // CBM = INT(길이 × 너비 × 높이 ÷ 1,000,000,000)
                                        const cbmValue = (lengthNum * widthNum * heightNum) / 1000000000;
                                        mergedData.cbm = Math.floor(cbmValue).toString(); // 정수로 변환 (예: 13)
                                      }
                                    }

                                    const ownerName = mergedData.ownerName || mergedData.name;
                                    const related = relatedDocsMap[ownerName] || {};

                                    return {
                                      ...vehicle,
                                      extractedData: mergedData, // ⭐ 병합된 데이터 사용
                                      ownerName,
                                      isCompany: isCompanyType(mergedData),
                                      identity: related.identity,
                                      businessReg: related.businessReg,
                                      contract: related.contract,
                                      taxInvoice: related.taxInvoice
                                    };
                                  });

                                  if (mappedVehicles.length === 0) {
                                    return (
                                      <div className="text-center py-12 text-gray-500">
                                        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                                        <p>매핑할 차량이 없습니다</p>
                                        <p className="text-xs mt-2">차량 서류를 업로드하고 검증을 완료하세요</p>
                                      </div>
                                    );
                                  }

                                  return (
                                    <table className="w-full text-sm" style={{ minWidth: '2200px' }}>
                                      <thead className="bg-gray-100 border-b border-gray-200">
                                        <tr>
                                          <th className="px-4 py-3 text-left font-semibold text-gray-700" style={{ minWidth: '120px' }}>소유자명</th>
                                          <th className="px-4 py-3 text-center font-semibold text-gray-700" style={{ minWidth: '60px' }}>구분</th>
                                          <th className="px-4 py-3 text-left font-semibold text-blue-700" style={{ minWidth: '130px' }}>주민번호<br /><span className="text-xs font-normal">(신분증)</span></th>
                                          <th className="px-4 py-3 text-left font-semibold text-orange-700" style={{ minWidth: '130px' }}>사업자번호<br /><span className="text-xs font-normal">(사업자등록증)</span></th>
                                          <th className="px-4 py-3 text-left font-semibold text-gray-700" style={{ minWidth: '200px' }}>주소</th>
                                          <th className="px-4 py-3 text-left font-semibold text-gray-700" style={{ minWidth: '90px' }}>차량번호</th>
                                          <th className="px-4 py-3 text-left font-semibold text-gray-700" style={{ minWidth: '150px' }}>차대번호(VIN)</th>
                                          <th className="px-4 py-3 text-left font-semibold text-gray-700" style={{ minWidth: '120px' }}>차명</th>
                                          <th className="px-4 py-3 text-center font-semibold text-gray-700" style={{ minWidth: '60px' }}>연식</th>
                                          <th className="px-4 py-3 text-right font-semibold text-gray-700" style={{ minWidth: '80px' }}>배기량</th>
                                          <th className="px-4 py-3 text-right font-semibold text-gray-700" style={{ minWidth: '90px' }}>주행거리</th>
                                          <th className="px-4 py-3 text-right font-semibold text-gray-700" style={{ minWidth: '80px' }}>총중량</th>
                                          <th className="px-4 py-3 text-right font-semibold text-gray-700" style={{ minWidth: '70px' }}>길이</th>
                                          <th className="px-4 py-3 text-right font-semibold text-gray-700" style={{ minWidth: '70px' }}>너비</th>
                                          <th className="px-4 py-3 text-right font-semibold text-gray-700" style={{ minWidth: '70px' }}>높이</th>
                                          <th className="px-4 py-3 text-right font-semibold text-gray-700" style={{ minWidth: '70px' }}>CBM</th>
                                          <th className="px-4 py-3 text-center font-semibold text-gray-700" style={{ minWidth: '100px' }}>말소일</th>
                                          <th className="px-4 py-3 text-right font-semibold text-green-700" style={{ minWidth: '120px' }}>계약금액<br /><span className="text-xs font-normal">(계약서)</span></th>
                                          <th className="px-4 py-3 text-left font-semibold text-purple-700" style={{ minWidth: '120px' }}>세금계산서<br /><span className="text-xs font-normal">(공급자)</span></th>
                                          <th className="px-4 py-3 text-left font-semibold text-gray-700" style={{ minWidth: '100px' }}>문서타입</th>
                                          <th className="px-4 py-3 text-center font-semibold text-gray-700" style={{ minWidth: '100px' }}>상태</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-200">
                                        {mappedVehicles.map((vehicle, idx) => {
                                          // ⭐ vehicle.extractedData는 이미 병합된 데이터 (필드별 우선순위 적용 + CBM 계산 완료)
                                          const data = vehicle.extractedData || {};


                                          return (
                                            <tr key={idx} className="hover:bg-gray-50 cursor-pointer">
                                              {/* 소유자명 */}
                                              <td className="px-4 py-3 text-gray-900" style={{ whiteSpace: 'nowrap' }}>
                                                <button
                                                  onClick={() => {
                                                    setSelectedFileIndex(vehicle.fileIndex);
                                                    setActiveTab('viewer');
                                                  }}
                                                  className="font-semibold text-gray-900 hover:text-blue-600 underline"
                                                >
                                                  {vehicle.ownerName || '-'}
                                                </button>
                                              </td>
                                              {/* 구분 */}
                                              <td className="px-4 py-3 text-center" style={{ whiteSpace: 'nowrap' }}>
                                                {vehicle.isCompany ? (
                                                  <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded font-bold">법인</span>
                                                ) : (
                                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded font-bold">개인</span>
                                                )}
                                              </td>
                                              {/* 주민번호 (신분증에서 매핑) */}
                                              <td className="px-4 py-3 text-gray-700" style={{ whiteSpace: 'nowrap' }}>
                                                {vehicle.identity ? (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setSelectedFileIndex(vehicle.identity.fileIndex);
                                                      setActiveTab('viewer');
                                                    }}
                                                    className="text-blue-700 hover:underline"
                                                  >
                                                    {vehicle.identity.residentNumber || '-'}
                                                  </button>
                                                ) : (
                                                  <span className="text-gray-400">-</span>
                                                )}
                                              </td>
                                              {/* 사업자번호 (사업자등록증에서 매핑) */}
                                              <td className="px-4 py-3 text-gray-700" style={{ whiteSpace: 'nowrap' }}>
                                                {vehicle.businessReg ? (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setSelectedFileIndex(vehicle.businessReg.fileIndex);
                                                      setActiveTab('viewer');
                                                    }}
                                                    className="text-orange-700 hover:underline"
                                                  >
                                                    {vehicle.businessReg.businessNumber || '-'}
                                                  </button>
                                                ) : (
                                                  <span className="text-gray-400">-</span>
                                                )}
                                              </td>
                                              {/* 주소 */}
                                              <td className="px-4 py-3 text-gray-600" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={data.address}>
                                                {renderTableCell(data, 'address', data.address)}
                                              </td>
                                              {/* 차량번호 */}
                                              <td className="px-4 py-3" style={{ whiteSpace: 'nowrap' }}>
                                                {isFieldApplicable(data?.documentType, 'plateNumber') ? (
                                                  data?.plateNumber ? (
                                                    <span className="font-semibold text-blue-900">{data.plateNumber}</span>
                                                  ) : (
                                                    <span className="text-gray-400">-</span>
                                                  )
                                                ) : (
                                                  <span className="text-gray-300 text-xs">-</span>
                                                )}
                                              </td>
                                              {/* 차대번호(VIN) */}
                                              <td className="px-4 py-3 font-mono text-xs text-gray-700" style={{ whiteSpace: 'nowrap' }}>
                                                {renderTableCell(data, 'vin', data.vin)}
                                              </td>
                                              {/* 차명 */}
                                              <td className="px-4 py-3 text-gray-700" style={{ whiteSpace: 'nowrap' }}>
                                                {renderTableCell(data, 'model', data.model)}
                                              </td>
                                              {/* 연식 */}
                                              <td className="px-4 py-3 text-center text-gray-700" style={{ whiteSpace: 'nowrap' }}>
                                                {renderTableCell(data, 'modelYear', data.modelYear)}
                                              </td>
                                              {/* 배기량 */}
                                              <td className="px-4 py-3 text-right text-gray-700" style={{ whiteSpace: 'nowrap' }}>
                                                {(() => {
                                                  const applicable = isFieldApplicable(data?.documentType, 'displacement');
                                                  const value = data?.displacement;
                                                  if (!applicable) return <span className="text-gray-300 text-xs">-</span>;
                                                  if (value === null || value === undefined || value === '') return <span className="text-orange-400 text-xs font-semibold">미인식</span>;
                                                  // 숫자 변환 및 천단위 콤마 적용
                                                  try {
                                                    const strValue = String(value);
                                                    const cleanValue = strValue.replace(/,/g, '').replace(/[^0-9.]/g, '').trim();

                                                    // 빈 문자열 체크
                                                    if (!cleanValue) {
                                                      return <span className="text-orange-400 text-xs font-semibold">미인식</span>;
                                                    }

                                                    const numValue = Number(cleanValue);

                                                    // 숫자 변환 실패 체크
                                                    if (isNaN(numValue) || !isFinite(numValue)) {
                                                      return value;
                                                    }

                                                    // 천단위 콤마 적용
                                                    return numValue.toLocaleString('ko-KR');
                                                  } catch (e) {
                                                    return value;
                                                  }
                                                })()}
                                              </td>
                                              {/* 주행거리 */}
                                              <td className="px-4 py-3 text-right text-gray-700" style={{ whiteSpace: 'nowrap' }}>
                                                {(() => {
                                                  const applicable = isFieldApplicable(data?.documentType, 'mileage');
                                                  const value = data?.mileage;
                                                  if (!applicable) return <span className="text-gray-300 text-xs">-</span>;
                                                  if (value === null || value === undefined || value === '') return <span className="text-orange-400 text-xs font-semibold">미인식</span>;
                                                  // 숫자 변환 및 천단위 콤마 적용
                                                  try {
                                                    const strValue = String(value);
                                                    const cleanValue = strValue.replace(/,/g, '').replace(/[^0-9.]/g, '').trim();

                                                    // 빈 문자열 체크
                                                    if (!cleanValue) {
                                                      return <span className="text-orange-400 text-xs font-semibold">미인식</span>;
                                                    }

                                                    const numValue = Number(cleanValue);

                                                    // 숫자 변환 실패 체크
                                                    if (isNaN(numValue) || !isFinite(numValue)) {
                                                      return value;
                                                    }

                                                    // 천단위 콤마 적용
                                                    return numValue.toLocaleString('ko-KR');
                                                  } catch (e) {
                                                    return value;
                                                  }
                                                })()}
                                              </td>
                                              {/* 총중량 */}
                                              <td className="px-4 py-3 text-right text-gray-700" style={{ whiteSpace: 'nowrap' }}>
                                                {(() => {
                                                  const applicable = isFieldApplicable(data?.documentType, 'totalWeight');
                                                  const value = data?.totalWeight;
                                                  if (!applicable) return <span className="text-gray-300 text-xs">-</span>;
                                                  if (value === null || value === undefined || value === '') return <span className="text-orange-400 text-xs font-semibold">미인식</span>;
                                                  // 숫자 변환 및 천단위 콤마 적용
                                                  try {
                                                    if (value === 0 || value === '0') return '0';
                                                    const strValue = String(value);
                                                    const cleanValue = strValue.replace(/,/g, '').replace(/[^0-9.]/g, '').trim();
                                                    if (!cleanValue || cleanValue === '0') return <span className="text-orange-400 text-xs font-semibold">미인식</span>;
                                                    const numValue = Number(cleanValue);
                                                    if (isNaN(numValue) || !isFinite(numValue)) return value;
                                                    return numValue.toLocaleString('ko-KR');
                                                  } catch (e) {
                                                    return value;
                                                  }
                                                })()}
                                              </td>
                                              {/* 길이 */}
                                              <td className="px-4 py-3 text-right text-gray-700" style={{ whiteSpace: 'nowrap' }}>
                                                {(() => {
                                                  const applicable = isFieldApplicable(data?.documentType, 'length');
                                                  const value = data?.length;
                                                  if (!applicable) return <span className="text-gray-300 text-xs">-</span>;
                                                  if (value === null || value === undefined || value === '') return <span className="text-orange-400 text-xs font-semibold">미인식</span>;
                                                  // 숫자 변환 및 천단위 콤마 적용
                                                  try {
                                                    if (value === 0 || value === '0') return '0';
                                                    const strValue = String(value);
                                                    const cleanValue = strValue.replace(/,/g, '').replace(/[^0-9.]/g, '').trim();
                                                    if (!cleanValue || cleanValue === '0') return <span className="text-orange-400 text-xs font-semibold">미인식</span>;
                                                    const numValue = Number(cleanValue);
                                                    if (isNaN(numValue) || !isFinite(numValue)) return value;
                                                    return numValue.toLocaleString('ko-KR');
                                                  } catch (e) {
                                                    return value;
                                                  }
                                                })()}
                                              </td>
                                              {/* 너비 */}
                                              <td className="px-4 py-3 text-right text-gray-700" style={{ whiteSpace: 'nowrap' }}>
                                                {(() => {
                                                  const applicable = isFieldApplicable(data?.documentType, 'width');
                                                  const value = data?.width;
                                                  if (!applicable) return <span className="text-gray-300 text-xs">-</span>;
                                                  if (value === null || value === undefined || value === '') return <span className="text-orange-400 text-xs font-semibold">미인식</span>;
                                                  // 숫자 변환 및 천단위 콤마 적용
                                                  try {
                                                    if (value === 0 || value === '0') return '0';
                                                    const strValue = String(value);
                                                    const cleanValue = strValue.replace(/,/g, '').replace(/[^0-9.]/g, '').trim();
                                                    if (!cleanValue || cleanValue === '0') return <span className="text-orange-400 text-xs font-semibold">미인식</span>;
                                                    const numValue = Number(cleanValue);
                                                    if (isNaN(numValue) || !isFinite(numValue)) return value;
                                                    return numValue.toLocaleString('ko-KR');
                                                  } catch (e) {
                                                    return value;
                                                  }
                                                })()}
                                              </td>
                                              {/* 높이 */}
                                              <td className="px-4 py-3 text-right text-gray-700" style={{ whiteSpace: 'nowrap' }}>
                                                {(() => {
                                                  const applicable = isFieldApplicable(data?.documentType, 'height');
                                                  const value = data?.height;
                                                  if (!applicable) return <span className="text-gray-300 text-xs">-</span>;
                                                  if (value === null || value === undefined || value === '') return <span className="text-orange-400 text-xs font-semibold">미인식</span>;
                                                  // 숫자 변환 및 천단위 콤마 적용
                                                  try {
                                                    if (value === 0 || value === '0') return '0';
                                                    const strValue = String(value);
                                                    const cleanValue = strValue.replace(/,/g, '').replace(/[^0-9.]/g, '').trim();
                                                    if (!cleanValue || cleanValue === '0') return <span className="text-orange-400 text-xs font-semibold">미인식</span>;
                                                    const numValue = Number(cleanValue);
                                                    if (isNaN(numValue) || !isFinite(numValue)) return value;
                                                    return numValue.toLocaleString('ko-KR');
                                                  } catch (e) {
                                                    return value;
                                                  }
                                                })()}
                                              </td>
                                              {/* CBM */}
                                              <td className="px-4 py-3 text-right font-semibold text-green-700" style={{ whiteSpace: 'nowrap' }}>
                                                {(() => {
                                                  const applicable = isFieldApplicable(data?.documentType, 'cbm');
                                                  const value = data?.cbm;
                                                  if (!applicable) return <span className="text-gray-300 text-xs">-</span>;
                                                  if (value === null || value === undefined || value === '') return <span className="text-orange-400 text-xs font-semibold">미인식</span>;
                                                  // 숫자 변환 및 천단위 콤마 적용
                                                  try {
                                                    if (value === 0 || value === '0') return '0';
                                                    const strValue = String(value);
                                                    const cleanValue = strValue.replace(/,/g, '').replace(/[^0-9.]/g, '').trim();
                                                    if (!cleanValue || cleanValue === '0') return <span className="text-orange-400 text-xs font-semibold">미인식</span>;
                                                    const numValue = Number(cleanValue);
                                                    if (isNaN(numValue) || !isFinite(numValue)) return value;
                                                    return numValue.toLocaleString('ko-KR');
                                                  } catch (e) {
                                                    return value;
                                                  }
                                                })()}
                                              </td>
                                              {/* 말소일 */}
                                              <td className="px-4 py-3 text-center text-gray-700" style={{ whiteSpace: 'nowrap' }}>
                                                {renderTableCell(data, 'deregistrationDate', data.deregistrationDate)}
                                              </td>
                                              {/* 계약금액 (계약서에서 매핑) */}
                                              <td className="px-4 py-3 text-right font-semibold text-green-700" style={{ whiteSpace: 'nowrap' }}>
                                                {vehicle.contract ? (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setSelectedFileIndex(vehicle.contract.fileIndex);
                                                      setActiveTab('viewer');
                                                    }}
                                                    className="text-green-700 hover:underline"
                                                  >
                                                    {vehicle.contract.totalAmount ? `${Number(vehicle.contract.totalAmount).toLocaleString()}원` : '-'}
                                                  </button>
                                                ) : (
                                                  <span className="text-gray-400">-</span>
                                                )}
                                              </td>
                                              {/* 세금계산서 (세금계산서에서 매핑) */}
                                              <td className="px-4 py-3 text-gray-700" style={{ whiteSpace: 'nowrap' }}>
                                                {vehicle.taxInvoice ? (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setSelectedFileIndex(vehicle.taxInvoice.fileIndex);
                                                      setActiveTab('viewer');
                                                    }}
                                                    className="text-purple-700 hover:underline text-xs"
                                                  >
                                                    {vehicle.taxInvoice.supplierBusinessName || vehicle.taxInvoice.supplierBusinessNumber || '세금계산서'}
                                                  </button>
                                                ) : (
                                                  <span className="text-gray-400">-</span>
                                                )}
                                              </td>
                                              {/* 문서타입 */}
                                              <td className="px-4 py-3" style={{ whiteSpace: 'nowrap' }}>
                                                <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded font-semibold">
                                                  {data.documentType}
                                                </span>
                                              </td>
                                              {/* 상태 */}
                                              <td className="px-4 py-3 text-center" style={{ whiteSpace: 'nowrap' }}>
                                                {!vehicle.isCompany && !vehicle.identity && (
                                                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded font-semibold whitespace-nowrap">
                                                    ⚠️ 신분증 필요
                                                  </span>
                                                )}
                                                {vehicle.isCompany && !vehicle.businessReg && (
                                                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded font-semibold whitespace-nowrap">
                                                    ⚠️ 사업자 필요
                                                  </span>
                                                )}
                                                {((vehicle.identity && !vehicle.isCompany) || (vehicle.businessReg && vehicle.isCompany)) && (
                                                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded font-semibold whitespace-nowrap">
                                                    ✓ 매핑 완료
                                                  </span>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  );
                                })()}
                              </div>

                              {/* 통계 */}
                              <div className="p-4 border-t border-gray-200 bg-gray-50">
                                <div className="flex items-center gap-6 text-sm">
                                  <div>
                                    <span className="font-semibold text-gray-700">전체 차량: </span>
                                    <span className="text-gray-900">
                                      {fileList.filter(f => f.extractedData &&
                                        ['말소등록증', '등록증', '폐차증', '등록원부', '경락확인서'].includes(f.extractedData.documentType)).length}건
                                    </span>
                                  </div>
                                  <div>
                                    <span className="font-semibold text-gray-700">매핑 완료: </span>
                                    <span className="text-green-600">
                                      {Object.values(fileList.reduce((acc, f) => {
                                        if (!f.extractedData) return acc;
                                        const key = f.extractedData.ownerName || f.extractedData.name || f.extractedData.businessName;
                                        if (!key) return acc;
                                        if (!acc[key]) acc[key] = { hasVehicle: false, hasId: false };
                                        if (['말소등록증', '등록증', '폐차증', '등록원부', '경락확인서'].includes(f.extractedData.documentType)) {
                                          acc[key].hasVehicle = true;
                                        }
                                        if (f.extractedData.documentType === '신분증' || f.extractedData.documentType === '사업자등록증') {
                                          acc[key].hasId = true;
                                        }
                                        return acc;
                                      }, {})).filter(g => g.hasVehicle && g.hasId).length}건
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      }

                    </div >
                    {/* 탭 내용 영역 종료 */}
                  </>
                )}
              </div >

              {/* 페이지 선택 패널 (PDF일 때만 표시) */}
              {
                selectedFile?.pageResults && selectedFile.pageResults.length > 0 && (() => {
                  const toggleAllPages = () => {
                    if (checkedPages.length === selectedFile.pageResults.length) {
                      // 모두 선택 해제
                      setCheckedPages([]);
                    } else {
                      // 모두 선택
                      const allPages = selectedFile.pageResults.map(p => p.pageNumber);
                      setCheckedPages(allPages);
                    }
                  };

                  const togglePage = (pageNum) => {
                    setCheckedPages(prev => {
                      if (prev.includes(pageNum)) {
                        return prev.filter(p => p !== pageNum);
                      } else {
                        return [...prev, pageNum].sort((a, b) => a - b);
                      }
                    });
                  };

                  return null; // 페이지 선택 UI는 오른쪽 패널 안으로 이동
                })()
              }

              {/* 오른쪽 패널 리사이저 - layoutMode에 따라 방향 변경 */}
              {
                !isRightPanelCollapsed && activeTab !== 'legacy-registration' ? (
                  <div
                    className={`relative flex-shrink-0 group hover:bg-blue-50 transition-colors ${layoutMode === 'horizontal'
                      ? 'h-2 w-full cursor-row-resize'
                      : 'w-2 cursor-col-resize'
                      }`}
                    onMouseDown={(e) => {
                      // ⭐ 버튼 클릭인 경우 리사이징 방지
                      const target = e.target;
                      const isButton = target.closest('button');
                      if (isButton) {
                        return; // 버튼 클릭은 리사이징 처리하지 않음
                      }

                      e.preventDefault();
                      e.stopPropagation();

                      const isHorizontal = layoutMode === 'horizontal';
                      const startPos = isHorizontal ? e.clientY : e.clientX;
                      const startSize = isHorizontal ? rightPanelWidth : rightPanelWidth; // 가로모드에서는 height 사용 필요
                      const panelElement = rightPanelRef.current;

                      if (!panelElement) return;

                      // 드래그 중 선택 방지
                      document.body.style.userSelect = 'none';
                      document.body.style.cursor = isHorizontal ? 'row-resize' : 'col-resize';
                      document.body.style.pointerEvents = 'none';
                      if (panelElement) {
                        panelElement.style.pointerEvents = 'auto';
                      }

                      // ⭐ requestAnimationFrame으로 매끄러운 업데이트
                      const handleMouseMove = (moveEvent) => {
                        moveEvent.preventDefault();

                        // 기존 애니메이션 프레임 취소
                        if (resizeAnimationFrameRef.current) {
                          cancelAnimationFrame(resizeAnimationFrameRef.current);
                        }

                        // 즉시 DOM에 반영 (상태 업데이트 없이)
                        resizeAnimationFrameRef.current = requestAnimationFrame(() => {
                          if (isHorizontal) {
                            // 가로모드: 위로 드래그하면 하단 패널이 커져야 함
                            const currentY = moveEvent.clientY;
                            const delta = startPos - currentY;
                            const containerHeight = panelElement.parentElement?.clientHeight || 600;
                            const newHeight = Math.max(100, Math.min(containerHeight - 100, startSize + delta));
                            if (panelElement) {
                              panelElement.style.height = `${newHeight}px`;
                            }
                          } else {
                            // 세로모드: 좌우 드래그
                            const delta = startPos - moveEvent.clientX;
                            const newWidth = Math.max(300, Math.min(1500, startSize + delta));
                            if (panelElement) {
                              panelElement.style.width = `${newWidth}px`;
                            }
                          }
                        });
                      };

                      const handleMouseUp = () => {
                        // 마지막 상태 업데이트 (상태 동기화)
                        if (panelElement) {
                          const finalSize = isHorizontal
                            ? parseInt(panelElement.style.height) || rightPanelWidth
                            : parseInt(panelElement.style.width) || rightPanelWidth;
                          setRightPanelWidth(finalSize);
                        }

                        // 🔧 AUIGrid 리사이즈 (컨테이너 크기에 맞게)
                        setTimeout(() => {
                          if (window.AUIGrid && window.AUIGrid.isCreated('#horizontal_grid')) {
                            window.AUIGrid.resize('#horizontal_grid');
                            console.log('📐 [Splitter] AUIGrid resize');
                          }
                        }, 100);

                        // 이벤트 리스너 제거
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);

                        // 애니메이션 프레임 취소
                        if (resizeAnimationFrameRef.current) {
                          cancelAnimationFrame(resizeAnimationFrameRef.current);
                          resizeAnimationFrameRef.current = null;
                        }

                        // 드래그 종료 후 스타일 복원
                        document.body.style.userSelect = '';
                        document.body.style.cursor = '';
                        document.body.style.pointerEvents = '';
                      };

                      // ⭐ passive: false로 즉시 반응
                      document.addEventListener('mousemove', handleMouseMove, { passive: false });
                      document.addEventListener('mouseup', handleMouseUp, { passive: false });
                    }}
                    title="드래그하여 크기 조절"
                  >
                    {/* 구분선 - layoutMode에 따라 방향 변경 */}
                    <div className={`absolute ${layoutMode === 'horizontal'
                      ? 'inset-x-0 top-1/2 -translate-y-1/2 h-0.5 bg-gray-300 group-hover:bg-blue-500 group-hover:h-1'
                      : 'inset-y-0 left-1/2 -translate-x-1/2 w-0.5 bg-gray-300 group-hover:bg-blue-500 group-hover:w-1'
                      } transition-all`} />

                    {/* 토글 버튼 (오른쪽 패널) - layoutMode에 따라 화살표 방향 및 동작 변경 */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1.5 z-40">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (layoutMode === 'horizontal') {
                            // 가로모드: PDF 뷰어(가운데 패널)를 토글하여 그리드 전체 화면
                            setIsCenterPanelCollapsed(!isCenterPanelCollapsed);
                          } else {
                            // 세로모드: 오른쪽 패널 토글
                            setIsRightPanelCollapsed(!isRightPanelCollapsed);
                          }
                        }}
                        style={{ pointerEvents: 'auto' }}
                        className={`${layoutMode === 'horizontal' ? 'h-4 w-12' : 'w-4 h-12'} bg-white border border-gray-300 hover:bg-gray-50 rounded-md flex items-center justify-center text-gray-500 hover:text-blue-600 shadow-sm transition-all hover:scale-110 cursor-pointer`}
                        title={layoutMode === 'horizontal' ? 'PDF 뷰어 접기/펴기' : '오른쪽 패널 접기/펴기'}
                      >
                        {layoutMode === 'horizontal' ? (
                          // 가로모드: 위/아래 화살표 (그리드를 전체로 보려면 위로)
                          isCenterPanelCollapsed ? (
                            <ChevronDown size={14} strokeWidth={2} />
                          ) : (
                            <ChevronUp size={14} strokeWidth={2} />
                          )
                        ) : (
                          // 세로모드: 좌/우 화살표
                          isRightPanelCollapsed ? (
                            <ChevronLeft size={14} strokeWidth={2} />
                          ) : (
                            <ChevronRight size={14} strokeWidth={2} />
                          )
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* 패널이 닫혀있을 때 토글 버튼 */
                  <div className="relative w-1 flex-shrink-0 bg-gray-100">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1.5 z-40">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setIsRightPanelCollapsed(!isRightPanelCollapsed);
                        }}
                        style={{ pointerEvents: 'auto' }}
                        className="w-4 h-12 bg-white border border-gray-300 hover:bg-gray-50 rounded-md flex items-center justify-center text-gray-500 hover:text-blue-600 shadow-sm transition-all hover:scale-110 cursor-pointer"
                        title="오른쪽 패널 접기/펴기"
                      >
                        {isRightPanelCollapsed ? (
                          <ChevronLeft size={14} strokeWidth={2} />
                        ) : (
                          <ChevronRight size={14} strokeWidth={2} />
                        )}
                      </button>
                    </div>
                  </div>
                )

              }

              {/* 오른쪽 패널: OCR 결과 및 템플릿 관리 (레거시 거래처 등록 시 숨김) */}
              {
                activeTab !== 'legacy-registration' && (
                  <div
                    ref={rightPanelRef}
                    className={`${isRightPanelCollapsed
                      ? (layoutMode === 'horizontal' ? 'h-0' : 'w-0')
                      : isCenterPanelCollapsed
                        ? 'flex-1'
                        : (layoutMode === 'horizontal' ? 'h-[40%]' : '')
                      } bg-white ${layoutMode === 'horizontal' ? 'border-t' : 'border-l'} border-gray-200 overflow-auto transition-all duration-300 relative`}
                    style={layoutMode === 'horizontal'
                      ? { height: isRightPanelCollapsed ? 0 : isCenterPanelCollapsed ? 'auto' : `${rightPanelWidth}px` }
                      : { width: isRightPanelCollapsed ? 0 : isCenterPanelCollapsed ? 'auto' : `${rightPanelWidth}px` }
                    }
                  >
                    {/* 템플릿 관리 사이드 드로어 (오버레이) */}
                    {showTemplateManagement && (
                      <div className="absolute inset-0 z-50 bg-white">
                        <TemplateManagement
                          onClose={() => setShowTemplateManagement(false)}
                          isModal={false}
                          apiBaseUrl={API_BASE_URL}
                          selectedFile={selectedFile}
                          currentDocumentImage={selectedFileWithBoundingBoxes?.pngImage}  // 🏷️ 라벨링용 현재 문서 이미지
                          onUpdateViewer={handleExternalFileSelect} // 🚀 뷰어 동기화 함수 전달
                          onUpdate={fetchTemplates} // ⭐ 템플릿 업데이트 시 App 상태 동기화
                          onTemplateSelect={(templateName) => {
                            // 🎯 템플릿 관리에서 선택된 템플릿 App.js 상태에 동기화
                            console.log('🎯 템플릿 관리에서 선택됨:', templateName);
                            setSelectedOcrTemplate(templateName);
                            // 🔧 이전 파일 선택 초기화 (템플릿의 sampleImage가 표시되도록)
                            setSelectedFileIndex(null);
                            // 🎯 템플릿 탭으로 명시적 전환 (같은 템플릿 재선택 시에도 동작하도록)
                            setCenterViewTab('template');
                          }}
                          onExtractFromCurrent={async (file) => {
                            // 현재 파일에서 헤더 추출
                            const formData = new FormData();
                            formData.append('file', file.file); // file 객체 안에 실제 File 객체가 있음

                            const res = await fetch(`${API_BASE_URL}/api/ocr/extract-header`, {
                              method: 'POST',
                              body: formData
                            });

                            const data = await res.json();
                            if (data.success) {
                              return { headers: data.headers, values: data.values };
                            } else {
                              throw new Error(data.error);
                            }
                          }}
                          initialSelectedId={templateManagementTargetId}
                          initialMode={templateManagementMode}
                        />
                      </div>
                    )}

                    {!isRightPanelCollapsed && (
                      <div className="h-full flex flex-row">
                        {/* OCR 결과 콘텐츠 영역 - 페이지 사이드바는 오른쪽 패널로 이동됨 */}
                        <div className="flex-1 overflow-y-auto">
                          {(() => {
                            // 디버깅을 위한 상태 로깅
                            const hasFlashData = !!selectedFile?.flashResult?.data;
                            const hasProData = !!selectedFile?.proResult?.data;
                            const hasExtractedData = !!extractedData;

                            console.log('🔍 [오른쪽 패널] 렌더링 조건 확인:', {
                              hasFlashData,
                              hasProData,
                              hasExtractedData,
                              flashResult: selectedFile?.flashResult,
                              proResult: selectedFile?.proResult,
                              extractedData: extractedData,
                              shouldShowCompare: hasFlashData && hasProData && !hasExtractedData
                            });

                            return null;
                          })()}

                          {/* LLM 비교 및 휴먼인더루프 화면 - 파일이 선택되면 즉시 표시 */}
                          {selectedFile ? (
                            <RightPanelTabs
                              selectedFile={selectedFile}
                              templates={templates}
                              selectedTemplateId={Object.values(templates).find(t => t.name === selectedOcrTemplate)?.id || ''}
                              apiBaseUrl={API_BASE_URL}
                              initialPrompt={editablePrompt}
                              availableModels={selectedValidationModels}
                              onRun={handleReExtract}
                              isProcessing={playgroundProcessing}
                              onTemplateUpdate={fetchTemplates}
                              onOpenTemplateManager={(mode) => handleOpenTemplateManager(mode, Object.values(templates).find(t => t.name === selectedOcrTemplate)?.id || '')}
                              onReset={handleReset}
                              onTemplateChange={(templateId) => {
                                const tmpl = Object.values(templates).find(t => t.id === templateId);
                                if (tmpl) setSelectedOcrTemplate(tmpl.name);
                              }}
                              // 🔧 문서추출 툴바 props
                              onExtract={() => {
                                // 기존 왼쪽 패널의 문서 추출 버튼 클릭 트리거
                                const extractBtn = document.querySelector('[title*="딥러닝 추출을 시작합니다"]') || document.querySelector('[title*="문서 추출을 시작합니다"]');
                                if (extractBtn && !extractBtn.disabled) {
                                  extractBtn.click();
                                }
                              }}
                              isExtracting={loading}
                              extractLabel={checkedCount > 0 ? `딥러닝 (${checkedCount})` : '딥러닝'}
                              extractDisabled={checkedCount === 0 && selectedFileIndex === null}
                              onCancelExtract={handleCancelProcessing}
                              onExcelDownload={() => {
                                window.dispatchEvent(new CustomEvent('triggerExcelDownload'));
                              }}
                              // 📄 페이지 리스트 관련 props
                              totalPages={selectedFile.totalPages || selectedFile.pageResults?.length || 0}
                              checkedPages={checkedPages}
                              selectedPageNumber={selectedPageNumber}
                              onPageChange={handlePdfPageChange}
                              onPageToggle={(pageNum) => {
                                setCheckedPages(prev => {
                                  if (prev.includes(pageNum)) {
                                    return prev.filter(p => p !== pageNum);
                                  } else {
                                    return [...prev, pageNum].sort((a, b) => a - b);
                                  }
                                });
                              }}
                            >
                              {/* PDF 파일이면 즉시 PageBasedGridPanel 표시 (처리 대기 중 상태부터) */}
                              {selectedFile.isPDF ? (
                                /* 🔍 Debug: selectedFile.groundedFields 확인 */
                                console.log('📤 [App→PageBasedGridPanel] groundedFields:', {
                                  hasGroundedFields: !!selectedFile.groundedFields,
                                  keys: Object.keys(selectedFile.groundedFields || {}),
                                  hasItems0: !!selectedFile.groundedFields?.['items[0]'],  // 🆕 items[0] 명시적 확인
                                  items0Data: selectedFile.groundedFields?.['items[0]'],   // 🆕 items[0] 데이터
                                  withBbox: Object.values(selectedFile.groundedFields || {}).filter(v => v?.bbox?.coordinates).length
                                }) || <PageBasedGridPanel
                                  documentType={
                                    // ⭐ pageResults에서 우선 가져오기 (OCR 파이프라인이 여기에 저장)
                                    selectedFile.pageResults?.[0]?.flashData?.documentType ||
                                    selectedFile.pageResults?.[0]?.proData?.documentType ||
                                    selectedFile.pageResults?.[0]?.extractedData?.documentType ||
                                    // 기존 폴백
                                    selectedFile.flashResult?.data?.documentType ||
                                    selectedFile.proResult?.data?.documentType ||
                                    selectedFile.classifyResult?.data?.documentType ||
                                    '기타'
                                  }
                                  flashData={selectedFile.flashResult?.data}
                                  proData={selectedFile.proResult?.data}
                                  pageResults={selectedFile.pageResults}
                                  totalPages={selectedFile.totalPages}
                                  isRightPanelCollapsed={isRightPanelCollapsed}
                                  selectedPageNumber={selectedPageNumber}
                                  checkedPages={checkedPages}
                                  fileId={selectedFile.id}
                                  validatedPages={validatedPages[selectedFile.id] || {}}
                                  templates={templates}
                                  selectedTemplateId={Object.values(templates).find(t => t.name === selectedOcrTemplate)?.id || ''} // ⭐ 왼쪽 패널 템플릿 동기화
                                  groundedFields={selectedFile.groundedFields || {}} // ⭐ [Grounding] LangExtract 필드 위치 정보
                                  onFieldHover={handleFieldHover}
                                  onFieldClick={handleFieldClick}
                                  onPageChange={handlePdfPageChange}
                                  onPageToggle={(pageNum) => {
                                    // 📄 페이지 체크/언체크 토글
                                    setCheckedPages(prev => {
                                      if (prev.includes(pageNum)) {
                                        return prev.filter(p => p !== pageNum);
                                      } else {
                                        return [...prev, pageNum].sort((a, b) => a - b);
                                      }
                                    });
                                  }}
                                  onValidatePage={handleValidatePage}
                                  onValidateMultiplePages={handleValidateMultiplePages}
                                  onValidateAllPages={handleValidateAllPages}
                                  isPageValidated={isPageValidated}
                                  onSaveVehicle={handleSaveVehicle}
                                  onProceed={(editedData) => {
                                    // 사용자가 수정한 데이터 사용
                                    console.log('✅ 사용자 수정 완료 (PDF 페이지별):', editedData);

                                    setFileList(fileList.map((f, idx) =>
                                      idx === selectedFileIndex
                                        ? { ...f, extractedData: editedData, status: 'completed' }
                                        : f
                                    ));

                                    // 검증 결과 그리드 탭으로 자동 이동
                                    setActiveTab('grid');
                                  }}
                                />
                              ) : useGridMode ? (
                                <PageBasedGridPanel
                                  documentType={
                                    // ⭐ pageResults에서 우선 가져오기 (OCR 파이프라인이 여기에 저장)
                                    selectedFile.pageResults?.[0]?.flashData?.documentType ||
                                    selectedFile.pageResults?.[0]?.proData?.documentType ||
                                    selectedFile.pageResults?.[0]?.extractedData?.documentType ||
                                    // 🎯 JPG 파일용: file.flashData 등에서 가져오기
                                    selectedFile.flashData?.documentType ||
                                    selectedFile.proData?.documentType ||
                                    selectedFile.extractedData?.documentType ||
                                    selectedFile.flashResult?.data?.documentType ||
                                    selectedFile.proResult?.data?.documentType ||
                                    selectedFile.classifyResult?.data?.documentType ||
                                    '기타'
                                  }
                                  flashData={
                                    // ⭐ pageResults[0]에서 우선 가져오기
                                    selectedFile.pageResults?.[0]?.flashData ||
                                    // 🎯 JPG 파일용: file.flashData 직접 접근
                                    selectedFile.flashData ||
                                    selectedFile.flashResult?.data
                                  }
                                  proData={
                                    selectedFile.pageResults?.[0]?.proData ||
                                    // 🎯 JPG 파일용: file.proData 직접 접근
                                    selectedFile.proData ||
                                    selectedFile.proResult?.data
                                  }
                                  extractedData={
                                    // ⭐ pageResults[0]의 extractedData 또는 file 레벨의 extractedData
                                    selectedFile.pageResults?.[0]?.extractedData ||
                                    selectedFile.extractedData
                                  }
                                  pageResults={selectedFile.pageResults || null} // ⭐ pageResults도 전달
                                  totalPages={selectedFile.pageResults?.length || 1}
                                  selectedPageNumber={1} // ⭐ 단일 이미지는 항상 페이지 1
                                  checkedPages={[1]} // ⭐ 단일 이미지는 항상 페이지 1 체크됨
                                  fileId={selectedFile.id} // ⭐ 파일 ID 추가
                                  validatedPages={validatedPages[selectedFile.id] || {}} // ⭐ 검증 상태 추가
                                  isRightPanelCollapsed={isRightPanelCollapsed}
                                  templates={templates}
                                  selectedTemplateId={Object.values(templates).find(t => t.name === selectedOcrTemplate)?.id || ''} // ⭐ 왼쪽 패널 템플릿 동기화
                                  groundedFields={selectedFile.groundedFields || {}} // ⭐ [Grounding] LangExtract 필드 위치 정보
                                  onFieldHover={handleFieldHover}
                                  onFieldClick={handleFieldClick}
                                  onValidatePage={handleValidatePage}
                                  onValidateMultiplePages={handleValidateMultiplePages}
                                  onValidateAllPages={handleValidateAllPages}
                                  isPageValidated={isPageValidated}
                                  onSaveVehicle={handleSaveVehicle} // ⭐ 차량 저장 핸들러 추가
                                  onProceed={(editedData) => {
                                    // 사용자가 수정한 데이터 사용
                                    console.log('✅ 사용자 수정 완료 (그리드):', editedData);

                                    setFileList(fileList.map((f, idx) =>
                                      idx === selectedFileIndex
                                        ? { ...f, extractedData: editedData, status: 'completed' }
                                        : f
                                    ));

                                    // 검증 결과 그리드 탭으로 자동 이동
                                    setActiveTab('grid');
                                  }}
                                />
                              ) : (
                                <>
                                  {/* Prompt Playground (이미지 모드) */}
                                  <PromptPlayground
                                    isOpen={isPromptPlaygroundOpen}
                                    onClose={() => setIsPromptPlaygroundOpen(false)}
                                    onRun={handleReExtract}
                                    initialPrompt={editablePrompt}
                                    availableModels={selectedValidationModels}
                                    currentDocumentType={selectedFile.classifyResult?.data?.documentType || '기타'}
                                    isProcessing={playgroundProcessing}
                                    apiBaseUrl={API_BASE_URL}
                                    templates={templates} // ⭐ 템플릿 전달
                                  />
                                  <CompareResultPanel
                                    documentType={selectedFile.flashResult?.data?.documentType || selectedFile.proResult?.data?.documentType || selectedFile.classifyResult?.data?.documentType || '기타'}
                                    flashData={selectedFile.flashResult?.data}
                                    proData={selectedFile.proResult?.data}
                                    onProceed={(editedData) => {
                                      // 사용자가 수정한 데이터 사용
                                      console.log('✅ 사용자 수정 완료:', editedData);

                                      setFileList(fileList.map((f, idx) =>
                                        idx === selectedFileIndex
                                          ? { ...f, extractedData: editedData, status: 'completed' }
                                          : f
                                      ));

                                      // 검증 결과 그리드 탭으로 자동 이동
                                      setActiveTab('grid');
                                    }}
                                  />
                                </>
                              )}
                            </RightPanelTabs>
                          ) : (

                            <>
                              {/* 일반 모드: 기존 UI */}
                              {extractedData ? (
                                <div className="h-full flex flex-col">
                                  <div className="p-4 border-b border-gray-200" style={{ backgroundColor: '#F4F4F4' }}>
                                    <div className="flex items-center justify-between">
                                      <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                                        <Shield className="w-5 h-5 text-gray-900" />
                                        {extractedData.metadata?.validated ? '검증 완료' : '1차 추출 결과'}
                                      </h2>

                                      {/* 일치율 표시 */}
                                      {extractedData?.metadata?.matchRate !== undefined && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-gray-600">Flash vs Pro</span>
                                          <span className={`inline-block px-3 py-1 text-sm rounded-full font-bold ${extractedData.metadata.matchRate === 100
                                            ? 'text-[#0A1037]'
                                            : extractedData.metadata.matchRate >= 90
                                              ? 'text-[#0A1037]'
                                              : 'text-white'
                                            }`}
                                            style={{
                                              backgroundColor: extractedData.metadata.matchRate === 100
                                                ? '#1EE9B6'
                                                : extractedData.metadata.matchRate >= 90
                                                  ? '#FFDD33'
                                                  : '#E94948'
                                            }}>
                                            {extractedData.metadata.matchRate}%
                                          </span>
                                        </div>
                                      )}
                                    </div>

                                    {/* Flash vs Pro 비교 보기 버튼 + 상세 정보 */}
                                    <div className="mt-3 flex items-center justify-between">
                                      {extractedData?.metadata && (
                                        <div className="text-xs text-gray-600">
                                          일치 {extractedData.metadata.matchCount || 0}개 ·
                                          불일치 {(extractedData.metadata.totalFields || 0) - (extractedData.metadata.matchCount || 0)}개 ·
                                          총 {extractedData.metadata.totalFields || 0}개 필드
                                        </div>
                                      )}

                                      {/* Flash vs Pro 비교 보기 버튼 */}
                                      {selectedFile?.flashResult && selectedFile?.proResult && (
                                        <button
                                          onClick={() => {
                                            // extractedData를 임시로 제거하여 CompareResultPanel 표시
                                            setFileList(fileList.map((f, idx) =>
                                              idx === selectedFileIndex
                                                ? { ...f, extractedData: null }
                                                : f
                                            ));
                                          }}
                                          className="text-xs px-3 py-1.5 rounded font-semibold hover:opacity-80 transition-opacity flex items-center gap-1.5"
                                          style={{ backgroundColor: '#339AF0', color: 'white' }}
                                        >
                                          <Eye className="w-3.5 h-3.5" />
                                          Flash vs Pro 비교 보기
                                        </button>
                                      )}
                                    </div>
                                  </div>

                                  <div className="flex-1 overflow-y-auto p-4">
                                    {/* 디버깅: 데이터 확인 */}
                                    {process.env.NODE_ENV === 'development' && (
                                      <div className="mb-4 p-2 bg-gray-100 border border-gray-300 rounded text-xs">
                                        <pre className="whitespace-pre-wrap overflow-auto max-h-40">
                                          {JSON.stringify({
                                            plateNumber: extractedData.plateNumber,
                                            vin: extractedData.vin,
                                            model: extractedData.model,
                                            validated: extractedData.metadata?.validated
                                          }, null, 2)}
                                        </pre>
                                      </div>
                                    )}

                                    {!extractedData.metadata?.validated && (
                                      <div className="mb-4 p-3 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                                        <div className="flex items-start gap-2">
                                          <span className="text-xl">⚠️</span>
                                          <div className="flex-1">
                                            <p className="text-yellow-900 text-sm font-bold mb-1">
                                              1차 추출 결과 (검증 전)
                                            </p>
                                            <p className="text-yellow-800 text-xs mb-2">
                                              Flash 모델로 추출한 결과입니다. Pro 모델과 비교 검증이 필요합니다.
                                            </p>
                                            <div className="bg-white p-2 rounded border border-yellow-200 text-xs">
                                              <p className="font-semibold text-gray-900 mb-1">검증이 필요한 이유:</p>
                                              <ul className="list-disc list-inside text-gray-700 space-y-0.5">
                                                <li>Flash는 빠르지만 일부 필드를 놓칠 수 있음</li>
                                                <li>Pro와 비교하여 정확도 확인 필요</li>
                                                <li>일치율 100%일 때만 자동 통과 가능</li>
                                              </ul>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {extractedData.metadata?.validated && extractedData.metadata?.comparisonResult && (
                                      <div className={`mb-4 p-3 border-2 rounded-lg ${extractedData.metadata.matchRate === 100
                                        ? 'bg-green-50 border-green-300'
                                        : 'bg-yellow-50 border-yellow-300'
                                        }`}>
                                        <div className="flex items-start gap-2">
                                          <span className="text-xl">
                                            {extractedData.metadata.matchRate === 100 ? '✅' : '⚠️'}
                                          </span>
                                          <div className="flex-1">
                                            <p className={`text-sm font-bold mb-1 ${extractedData.metadata.matchRate === 100
                                              ? 'text-green-900'
                                              : 'text-yellow-900'
                                              }`}>
                                              2차 검증 완료 (Flash vs Pro 비교)
                                            </p>
                                            {/* OCR 워크플로우 헤더 (레거시 거래처 등록 시 숨김) */}
                                            {activeTab !== 'legacy-registration' && (
                                              <div className="flex items-center gap-2 mb-4">
                                                <span className={`text-sm font-bold ${fileList.length > 0 ? 'text-gray-400' : 'text-blue-600'}`}>1. 파일 업로드</span>
                                                <ChevronRight size={14} className="text-gray-300" />
                                                <span className={`text-sm font-bold ${fileList.length > 0 && !extractedData ? 'text-blue-600' : 'text-gray-400'}`}>2. 확인 선택</span>
                                                <ChevronRight size={14} className="text-gray-300" />
                                                <span className={`text-sm font-bold ${extractedData ? 'text-blue-600' : 'text-gray-400'}`}>3. 추출 시작</span>
                                              </div>
                                            )}
                                            <div className="bg-white p-2 rounded border text-xs space-y-1">
                                              <div className="flex items-center justify-between">
                                                <span className="font-semibold text-gray-700">일치율:</span>
                                                <span className={`font-bold text-lg ${extractedData.metadata.matchRate === 100
                                                  ? 'text-green-700'
                                                  : extractedData.metadata.matchRate >= 90
                                                    ? 'text-yellow-700'
                                                    : 'text-red-700'
                                                  }`}>
                                                  {extractedData.metadata.comparisonResult.agreementRate}
                                                </span>
                                              </div>
                                              <div className="flex items-center justify-between">
                                                <span className="text-gray-600">일치 필드:</span>
                                                <span className="text-gray-900 font-medium">
                                                  {extractedData.metadata.matchDetails.matchCount}개
                                                </span>
                                              </div>
                                              <div className="flex items-center justify-between">
                                                <span className="text-gray-600">차이 필드:</span>
                                                <span className={`font-medium ${extractedData.metadata.comparisonResult.differentFields === 0
                                                  ? 'text-green-700'
                                                  : 'text-yellow-700'
                                                  }`}>
                                                  {extractedData.metadata.comparisonResult.differentFields}개
                                                </span>
                                              </div>
                                            </div>

                                            {extractedData.metadata.matchRate === 100 ? (
                                              <div className="mt-2 p-2 bg-green-100 rounded text-xs text-green-800">
                                                <p className="font-semibold">자동 통과 가능</p>
                                                <p>Flash와 Pro가 모든 필드에서 일치합니다. 안심하고 사용하세요.</p>
                                              </div>
                                            ) : (
                                              <div className="mt-2 p-2 bg-yellow-100 rounded text-xs text-yellow-800">
                                                <p className="font-semibold">⚠️ 사용자 확인 필요 (휴먼인더루프)</p>
                                                <p>일부 필드가 일치하지 않습니다. 아래 노란색 필드를 확인하고 올바른 값을 선택하세요.</p>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {extractedData.metadata?.validated && extractedData.metadata.needsReview && !extractedData.metadata?.comparisonResult && (
                                      <div className="mb-4 p-3 bg-gray-50 border border-gray-300 rounded-lg">
                                        <p className="text-gray-800 text-sm font-medium">
                                          ⚠️ 일부 필드의 신뢰도가 낮습니다. 확인해주세요.
                                        </p>
                                      </div>
                                    )}

                                    {extractedData.metadata?.validated && extractedData.metadata?.claudeValidation?.issues?.length > 0 && (
                                      <div className="mb-4 p-3 bg-gray-50 border border-gray-300 rounded-lg">
                                        <p className="text-gray-900 font-medium text-sm mb-2">
                                          검증 결과:
                                        </p>
                                        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                                          {extractedData.metadata.claudeValidation.issues.map((issue, i) => (
                                            <li key={i}>{issue}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    <div className="space-y-3">
                                      {/* 문서 타입 */}
                                      {extractedData.documentType && (
                                        <div className="mb-4">
                                          <label className="block text-xs font-medium text-gray-700 mb-2">
                                            📋 문서 타입
                                          </label>
                                          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg">
                                            <p className="text-lg font-bold text-blue-900 text-center">
                                              {extractedData.documentType}
                                            </p>
                                          </div>
                                        </div>
                                      )}

                                      {/* 동적 필드 렌더링 (그리드 레이아웃 지원) */}
                                      {(() => {
                                        const docType = extractedData.documentType;
                                        const fieldsMap = DOCUMENT_FIELDS_MAP[docType];

                                        if (!fieldsMap) {
                                          // 알 수 없는 타입: 기본 필드 모두 표시하거나, extractedData 키 기반 표시
                                          return (
                                            <div className="text-gray-500 text-center p-4">
                                              <p>정의되지 않은 문서 타입입니다: {docType}</p>
                                            </div>
                                          );
                                        }

                                        return (
                                          <div className="grid grid-cols-6 gap-3">
                                            {Object.entries(fieldsMap).map(([fieldKey, config]) => {
                                              if (!config || config === false) return null;

                                              // 기본값 (Full width)
                                              let colSpan = 'col-span-6';
                                              if (config.width === 'half') colSpan = 'col-span-3';
                                              if (config.width === 'third') colSpan = 'col-span-2';

                                              const label = config.label || fieldKey;

                                              return (
                                                <div key={fieldKey} className={colSpan}>
                                                  {renderField(label, fieldKey)}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        );
                                      })()}

                                      {extractedData.metadata?.validated && extractedData.metadata?.claudeValidation?.reasoning && (
                                        <div className="p-3 bg-gray-50 border border-gray-300 rounded-lg">
                                          <p className="text-xs font-medium text-gray-900 mb-1">
                                            검증 근거
                                          </p>
                                          <p className="text-xs text-gray-700">
                                            {extractedData.metadata.claudeValidation.reasoning}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="p-4 border-t border-gray-200 bg-gray-50">
                                    <button
                                      onClick={handleConfirm}
                                      className="w-full px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition font-medium shadow-sm"
                                    >
                                      ✓ 확인 완료 - 시스템 입력
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                /* 파일 없을 때: RightPanelTabs 표시 (템플릿 설정 확인용) */
                                <RightPanelTabs
                                  selectedFile={null}
                                  templates={templates}
                                  selectedTemplateId={Object.values(templates).find(t => t.name === selectedOcrTemplate)?.id || ''}
                                  apiBaseUrl={API_BASE_URL}
                                  initialPrompt={editablePrompt}
                                  availableModels={selectedValidationModels}
                                  onRun={handleReExtract}
                                  isProcessing={playgroundProcessing}
                                  onTemplateUpdate={fetchTemplates}
                                  onOpenTemplateManager={(mode) => handleOpenTemplateManager(mode, Object.values(templates).find(t => t.name === selectedOcrTemplate)?.id || '')}
                                  onReset={handleReset}
                                  onTemplateChange={(templateId) => {
                                    const tmpl = Object.values(templates).find(t => t.id === templateId);
                                    if (tmpl) setSelectedOcrTemplate(tmpl.name);
                                  }}
                                >
                                  {/* 추출 결과 탭 콘텐츠: 파일 업로드 안내 */}
                                  <div className="h-full flex items-center justify-center text-gray-500">
                                    <div className="text-center px-8 max-w-md">
                                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                        <span className="text-2xl">📄</span>
                                      </div>
                                      <h3 className="text-lg font-bold text-gray-900 mb-2">문서를 업로드하세요</h3>
                                      <p className="text-sm text-gray-600">
                                        왼쪽에서 파일을 업로드하면<br />추출 결과가 여기에 표시됩니다
                                      </p>
                                    </div>
                                  </div>
                                </RightPanelTabs>
                              )}
                            </>
                          )
                          }
                        </div>
                      </div>
                    )}
                  </div>
                )
              }
              {/* ✏️ PDF 뷰어 + 오른쪽 패널 Wrapper 끝 */}
            </div>

            {/* 🤖 AI 채팅 패널 - PageBasedGridPanel 인라인 사이드바로 대체됨 */}
          </div>
          {/* ✏️ 메인 콘텐츠 + AI 패널 Wrapper 끝 */}
        </div >
      )}

      {/* 템플릿 관리 모달 (제거됨 - 사이드 드로어로 이동) */}
      {/* {showTemplateManagement && (
        <TemplateManagement onClose={() => setShowTemplateManagement(false)} />
      )} */}

      {/* 프롬프트 관리 모달 */}
      < PromptManagementModal
        isOpen={showPromptManager}
        onClose={() => setShowPromptManager(false)
        }
        apiUrl={API_BASE_URL}
      />

      {/* 기초설정 모달 */}
      {
        showMasterSettingsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-lg shadow-xl w-[52vw] h-[80vh] flex flex-col">
              <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <h3 className="text-lg font-semibold" style={{ color: '#0A1037' }}>
                    기초설정
                  </h3>
                  <span className="text-sm text-gray-600">
                    차량 기초자료관리(그룹코드: {localStorage.getItem('groupCode') || '0001'})
                  </span>
                  <button
                    onClick={() => {
                      // VehicleMasterManagement 컴포넌트의 갱신 기능을 호출
                      window.dispatchEvent(new CustomEvent('refreshVehicleMaster'));
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors text-sm"
                    title="새로고침"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>갱신</span>
                  </button>
                </div>
                <button
                  onClick={() => setShowMasterSettingsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden px-0 py-0 min-h-0">
                <VehicleMasterManagement API_BASE_URL={API_BASE_URL} />
              </div>
            </div>
          </div>
        )
      }

      {/* 🧙 문서 처리 마법사 */}
      <DocumentWizard
        isOpen={showDocumentWizard}
        onClose={() => setShowDocumentWizard(false)}
        firstPagePreview={wizardFirstPage}
        templates={templates}
        API_BASE_URL={API_BASE_URL}
        onSelectExisting={(templateName) => {
          setSelectedOcrTemplate(templateName);
          console.log('📋 [Wizard] 기존 템플릿 선택:', templateName);
        }}
        onCreateNew={(fields, templateName) => {
          console.log('🆕 [Wizard] 신규 필드:', fields);
          if (templateName) {
            console.log('💾 [Wizard] 템플릿 저장 요청:', templateName);
            // TODO: 템플릿 저장 API 호출
          }
        }}
        onExtract={() => {
          console.log('🚀 [Wizard] 추출 시작');
          // 문서 추출 버튼 자동 클릭
          const extractBtn = document.querySelector('[title*="문서 추출"]');
          if (extractBtn) extractBtn.click();
        }}
      />

      {/* 📊 마이그레이션 도구 모달 */}
      {
        showMigrationModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-xl shadow-2xl w-[900px] max-h-[85vh] flex flex-col overflow-hidden">
              <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                <h3 className="text-lg font-semibold" style={{ color: '#0A1037' }}>
                  📊 마이그레이션 도구
                </h3>
                <button
                  onClick={() => setShowMigrationModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-auto bg-white">
                <ExcelImageExtractor />
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
}

export default App;
